/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useSessionTimeout from '../hooks/useSessionTimeout';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { generateOwnerReceipt } from '../services/pdfGenerator';
import { generateDisputeStatement } from '../utils/ledger/generateDisputeStatement';
import { generateOwnerPayoutLedgerPDF } from '../utils/ledger/generateOwnerPayoutLedgerPDF';
import { computeBookingTaxQuote } from '../utils/gst/gstRules';
import MobileNav from '../components/MobileNav';
import { useWindowSize } from '../hooks/useWindowSize';
import { useDeviceLayout } from '../hooks/useDeviceLayout';
import { logoutAndRedirect } from '../utils/authLogout';
import {
  getOwnerBookingsPage,
  approveBooking,
  completeBooking,
  getOwnerSettlements,
  getOwnerProfile,
  updateOwnerProfile,
  submitMachineRegistration,
  getOwnerMachineRegistrations,
  getWalletBalance,
  getMyTransactionsPage,
  getMachines,
  updateMachine,
  secureFetch,
  getOwnerBlacklistedClients,
  listOwnerPendingQuotations,
  listOwnerAwaitingMobilizationQuotations,
  listOwnerMobilizationConfirmedQuotations,
  acceptOwnerQuotation,
  rejectOwnerQuotation,
  confirmOwnerMobilization,
  convertOwnerQuotation,
  getTermsStatus,
  acknowledgeTerms,
  getAvailabilityBlocks,
  createAvailabilityBlock,
  deleteAvailabilityBlock,
} from '../supabaseService';
import { submitWithdrawRequest } from '../services/walletApi';
import { appendUniqueById } from '../utils/pagination';
import BookingFeedbackSummary from '../components/BookingFeedbackSummary';
import BookingFlowV2Card from '../components/booking/BookingFlowV2Card';
import ContractBookingStatus from '../components/booking/ContractBookingStatus';
import { bookingNeedsLiveSync, runningElapsedMs } from '../services/bookingFlowApi';
import { formatHoursHm } from '../utils/durationHours';
import PushNotificationToggle from '../components/notifications/PushNotificationToggle';
import NotificationCenter from '../components/NotificationCenter';
import { buildMachineLookup, getBookingMachineLabel } from '../utils/bookingDisplay';
import {
  buildOwnerReceiptPayload,
  isOwnerProfileIncomplete,
  mapOwnerProfileForReceipt,
} from '../utils/ownerReceiptPayload';
import { maskAccountNumber, maskPan } from '../utils/ownerProfileMask';
import {
  isRtoApplicable,
  getDocFieldsForMobility,
  emptyRegDocumentsForMobility,
  getPdiChecksForMobility,
  validateRegistrationStep,
  ATTACHMENT_OPTIONS,
  ATTACHMENT_LABEL_BY_ID,
  ATTACHMENT_RATE_ELIGIBLE,
} from '../utils/machineRegistrationConfig';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Users, PanelLeft, PanelLeftClose } from 'lucide-react';
import PhoneChangeModal from '../components/PhoneChangeModal';
import OwnerKycForm from '../components/OwnerKycForm';
import MachineRegistrationStep2 from '../components/owner/MachineRegistrationStep2';
import { Button } from '@/components/ui/button';
import { buildCsvFromRows, downloadCsvContent, forceCsvText } from '../utils/csvExport';
import { cn } from '@/lib/utils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function getCurrentPayrollMonthStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const PAYROLL_EXPORT_MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

function getPreviousPayrollMonthStr(monthStr) {
  const year = Number(monthStr.slice(0, 4));
  const monthIndex = Number(monthStr.slice(5, 7));
  const d = new Date(year, monthIndex - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function payrollMonthKey(operatorId, month) {
  return `${operatorId}:${month}`;
}

const OWNER_ATTENDANCE_STATUSES = [
  'present',
  'halfday',
  'paid_leave',
  'unpaid_leave',
  'absent',
];

function formatOwnerAttendanceStatus(status, t) {
  const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'present') return t('presentLabel');
  if (normalized === 'halfday' || normalized === 'half_day') return t('halfDayLabel');
  if (normalized === 'absent') return t('absentLabel');
  if (normalized === 'paid_leave') return t('paidLeaveLabel');
  if (normalized === 'unpaid_leave') return t('unpaidLeaveLabel');
  return status || '—';
}

function ownerAttendanceStatusColor(statusKey) {
  if (statusKey === 'present' || statusKey === 'paid_leave') return '#4CAF50';
  if (statusKey === 'halfday') return '#FF9800';
  if (statusKey === 'unpaid_leave') return '#e94560';
  return '#8896a8';
}

function formatOwnerAttendanceHmrDisplay(hmr) {
  const n = Number(hmr);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return formatHoursHm(n);
}

function formatTimeHHMM(hms) {
  if (!hms) return '—';
  return hms.slice(0, 5);
}

function summarizeOwnerAttendanceRows(rows) {
  const summary = {
    present: 0,
    halfday: 0,
    absent: 0,
    leave: 0,
  };
  (rows || []).forEach((row) => {
    const status = String(row.status || '').toLowerCase();
    if (status === 'present') summary.present += 1;
    else if (status === 'halfday') summary.halfday += 1;
    else if (status === 'absent') summary.absent += 1;
    else if (status === 'paid_leave' || status === 'unpaid_leave') summary.leave += 1;
  });
  return summary;
}

function operatorCardActionBtn(active, accentColor) {
  return {
    padding: '8px 14px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
    border: `1px solid ${active ? accentColor : 'rgba(201,168,76,0.35)'}`,
    background: active ? `${accentColor}22` : 'rgba(201,168,76,0.08)',
    color: active ? accentColor : '#e8e0d0',
  };
}

const ANALYTICS_CHART_GOLD = '#C9A84C';
const ANALYTICS_CHART_GOLD_LIGHT = '#f5e6b8';
const ANALYTICS_CHART_GRID = 'rgba(201, 168, 76, 0.15)';
const ANALYTICS_CHART_AXIS = '#8896a8';
const ANALYTICS_CHART_HEIGHT = 280;

function formatAnalyticsRevenue(value) {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
  return `₹${Number(value || 0).toLocaleString('en-IN')}`;
}

const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** 1-based month number → month name. Returns em-dash for out-of-range input. */
function formatMonthName(monthNumber) {
  const n = Number(monthNumber);
  if (!Number.isInteger(n) || n < 1 || n > 12) return '—';
  return MONTH_NAMES_FULL[n - 1];
}

function formatTerminationFormula(formula, flatAmount) {
  if (String(formula || '') === 'flat') {
    const amt = Number(flatAmount || 0);
    return `Flat fee — ₹${amt.toLocaleString('en-IN')}`;
  }
  return '25% of remaining cycles (max 3)';
}

/** Remaining time as HH:MM:SS. Null when no deadline, 'Expired' once past. */
function formatQuotationCountdown(deadlineIso, nowTs) {
  if (!deadlineIso) return null;
  const deadlineMs = new Date(deadlineIso).getTime();
  if (!Number.isFinite(deadlineMs)) return null;
  const remaining = deadlineMs - nowTs;
  if (remaining <= 0) return 'Expired';
  const totalSeconds = Math.floor(remaining / 1000);
  const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function formatContractSpanDays(startDate, endDate) {
  if (!startDate || !endDate) return '—';
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '—';
  const days = Math.round((end - start) / 86400000) + 1;
  return `${days} day${days === 1 ? '' : 's'}`;
}

function formatBookingAttachmentSummary(b) {
  if (!b?.selected_attachment) return '';
  const name = ATTACHMENT_LABEL_BY_ID[b.selected_attachment] || b.selected_attachment;
  const parts = [name];
  if (b.attachment_usage_hours) parts.push(formatHoursHm(b.attachment_usage_hours));
  const rate = Number(b.attachment_hourly_rate);
  if (b.attachment_hourly_rate && Number.isFinite(rate) && rate > 0) {
    parts.push(`₹${rate.toLocaleString('en-IN', { maximumFractionDigits: 0 })}/hr`);
  }
  return parts.join(' · ');
}

function FleetAnalyticsTooltip({ active, payload, label, bookingsLabel }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      style={{
        background: 'linear-gradient(145deg, #0f2040, #0D1B2A)',
        border: '1px solid rgba(201, 168, 76, 0.4)',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}
    >
      <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, color: 'rgba(201,168,76,0.8)', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontSize: '16px', fontWeight: 700, color: '#c9a84c' }}>
        {formatAnalyticsRevenue(row.revenue)}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#8896a8' }}>
        {bookingsLabel}: {row.bookings ?? 0}
      </p>
    </div>
  );
}

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function complianceDateStatus(dateStr) {
  if (!dateStr) return 'not_set';
  const today = new Date().toISOString().slice(0, 10);
  const d7 = new Date(`${today}T00:00:00.000Z`);
  d7.setUTCDate(d7.getUTCDate() + 7);
  const d30 = new Date(`${today}T00:00:00.000Z`);
  d30.setUTCDate(d30.getUTCDate() + 30);
  const plus7 = d7.toISOString().slice(0, 10);
  const plus30 = d30.toISOString().slice(0, 10);
  if (dateStr < today) return 'expired';
  if (dateStr <= plus7) return 'expiring_critical';
  if (dateStr <= plus30) return 'expiring_soon';
  return 'valid';
}

function complianceStatusBadgeStyle(status) {
  if (status === 'expired') return { bg: 'rgba(239,68,68,0.15)', border: '#ef4444', color: '#ef4444' };
  if (status === 'expiring_critical') return { bg: 'rgba(249,115,22,0.15)', border: '#f97316', color: '#f97316' };
  if (status === 'expiring_soon') return { bg: 'rgba(245,158,11,0.15)', border: '#f59e0b', color: '#f59e0b' };
  if (status === 'valid') return { bg: 'rgba(76,175,80,0.15)', border: '#4CAF50', color: '#4CAF50' };
  return { bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.35)', color: '#94a3b8' };
}

function formatComplianceDateLabel(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatComplianceDateRange(startStr, endStr) {
  const startLabel = formatComplianceDateLabel(startStr);
  const endLabel = formatComplianceDateLabel(endStr);
  if (startLabel && endLabel) {
    return `${startLabel} → ${endLabel}`;
  }
  if (endLabel) {
    return endLabel;
  }
  if (startLabel) {
    return startLabel;
  }
  return null;
}

function serviceDueDateStatus(dateStr) {
  if (!dateStr) return 'not_set';
  const today = new Date().toISOString().slice(0, 10);
  const d30 = new Date(`${today}T00:00:00.000Z`);
  d30.setUTCDate(d30.getUTCDate() + 30);
  const plus30 = d30.toISOString().slice(0, 10);
  if (dateStr < today) return 'overdue';
  if (dateStr <= plus30) return 'due_soon';
  return 'ok';
}

function serviceDueStatusBadgeStyle(status) {
  if (status === 'overdue') return { bg: 'rgba(239,68,68,0.15)', border: '#ef4444', color: '#ef4444' };
  if (status === 'due_soon') return { bg: 'rgba(245,158,11,0.15)', border: '#f59e0b', color: '#f59e0b' };
  if (status === 'ok') return { bg: 'rgba(76,175,80,0.15)', border: '#4CAF50', color: '#4CAF50' };
  return { bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.35)', color: '#94a3b8' };
}

function serviceTypeLabel(type, t) {
  const key = String(type || '').toLowerCase();
  const map = {
    routine: t('serviceTypeRoutine'),
    major: t('serviceTypeMajor'),
    breakdown: t('serviceTypeBreakdown'),
    pdi: t('serviceTypePdi'),
    other: t('serviceTypeOther'),
  };
  return map[key] || type || '—';
}

function recoveryTrafficEmoji(trafficLight) {
  if (trafficLight === 'green') return '🟢';
  if (trafficLight === 'yellow') return '🟡';
  return '🔴';
}

function recoveryStatusI18nKey(trafficLight) {
  if (trafficLight === 'green') return 'recoveryOnTrack';
  if (trafficLight === 'yellow') return 'recoveryNeedsAttention';
  return 'recoveryActionRequired';
}

function workingHoursFormValue(raw, fallback) {
  const s = String(raw || '').trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!match) return fallback;
  return `${String(match[1]).padStart(2, '0')}:${match[2]}`;
}

const DEFAULT_WORKING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const parseToAmPm = (hhmm) => {
  const [h, m] = (hhmm || '09:00').split(':').map(Number);
  const hour24 = Number.isFinite(h) ? h : 9;
  const minuteRaw = Number.isFinite(m) ? m : 0;
  const steps = [0, 15, 30, 45];
  const minute = steps.reduce(
    (best, s) => (Math.abs(s - minuteRaw) < Math.abs(best - minuteRaw) ? s : best),
    0,
  );
  return {
    hour: hour24 % 12 || 12,
    minute,
    period: hour24 < 12 ? 'AM' : 'PM',
  };
};

const toHHMM = (hour, minute, period) => {
  let h = Number(hour);
  if (period === 'AM' && h === 12) h = 0;
  if (period === 'PM' && h !== 12) h += 12;
  return `${String(h).padStart(2, '0')}:${String(Number(minute)).padStart(2, '0')}`;
};

function formatWorkingHoursClock(raw) {
  const s = String(raw || '').trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!match) return '';
  const hour24 = Number(match[1]);
  const mins = match[2];
  if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) return '';
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${mins} ${ampm}`;
}

function formatAvailabilityDate(raw) {
  const s = String(raw || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function recoveryProgressBar(percent) {
  const safe = Math.min(100, Math.max(0, Number(percent) || 0));
  const filled = Math.round(safe / 10);
  return `${'█'.repeat(filled)}${'░'.repeat(10 - filled)} ${Math.round(safe)}%`;
}

const FINANCIAL_OWNERSHIP_OPTIONS = [
  { value: 'new', labelKey: 'machineOwnershipNew' },
  { value: 'used_first_owner', labelKey: 'machineOwnershipUsed' },
  { value: 'second_hand', labelKey: 'machineOwnershipSecondHand' },
];

const EMPTY_SERVICE_FORM = {
  service_date: '',
  service_type: 'routine',
  hmr_at_service: '',
  next_service_hmr: '',
  next_service_date: '',
  work_done: '',
  notes: '',
};

const parseBookingDate = (b) => {
  const raw = b?.start_date || b?.created_at;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (b?.date) {
    const d = new Date(b.date);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
};

const groupOwnerBookingsSchedule = (bookings) => {
  if (!Array.isArray(bookings) || bookings.length === 0) return [];
  const monthMap = new Map();
  bookings.forEach((b) => {
    const d = parseBookingDate(b);
    const monthKey = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const dayKey = d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' });
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, { sortTime: d.getTime(), days: new Map() });
    }
    const monthEntry = monthMap.get(monthKey);
    monthEntry.sortTime = Math.max(monthEntry.sortTime, d.getTime());
    if (!monthEntry.days.has(dayKey)) {
      monthEntry.days.set(dayKey, { sortTime: d.getTime(), bookings: [] });
    }
    const dayEntry = monthEntry.days.get(dayKey);
    dayEntry.sortTime = Math.max(dayEntry.sortTime, d.getTime());
    dayEntry.bookings.push(b);
  });
  return Array.from(monthMap.entries())
    .sort((a, b) => b[1].sortTime - a[1].sortTime)
    .map(([month, { days }]) => ({
      month,
      dayGroups: Array.from(days.entries())
        .sort((a, b) => b[1].sortTime - a[1].sortTime)
        .map(([day, { bookings: dayBookings }]) => ({ day, bookings: dayBookings })),
    }));
};

const readSessionUser = () => {
  try {
    return JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
  } catch {
    return {};
  }
};

function AccountStatusPill({ status }) {
  const key = String(status || 'active').toLowerCase();
  const tones = {
    active: { background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', label: '● Active' },
    pending: { background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', label: '● Pending' },
    rejected: { background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', label: '● Rejected' },
    inactive: { background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid rgba(156,163,175,0.3)', label: '● Inactive' },
  };
  const tone = tones[key] || tones.active;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        marginLeft: '8px',
        background: tone.background,
        color: tone.color,
        border: tone.border,
        whiteSpace: 'nowrap',
      }}
    >
      {tone.label}
    </span>
  );
}

function OwnerTierBadge({ tier }) {
  const verified = String(tier || 'lite').toLowerCase() === 'verified';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 700,
        marginLeft: '8px',
        whiteSpace: 'nowrap',
        background: verified ? 'rgba(201,168,76,0.12)' : 'rgba(136,150,168,0.1)',
        color: verified ? '#c9a84c' : '#8896a8',
        border: verified ? '1px solid rgba(201,168,76,0.55)' : '1px solid rgba(136,150,168,0.45)',
      }}
    >
      {verified ? '✅ Verified' : '🔓 Lite'}
    </span>
  );
}

const CONTRACT_VERIFIED_REQUIRED_MSG =
  'Contract bookings require Verified status — complete KYC + bank details to upgrade.';

const OWNER_DATA = {
  name: 'Owner',
  phone: '',
  email: '',
  since: new Date().getFullYear().toString(),
};

/**
 * Legal entity tier for owners. Distinct from owner_tier (KYC lite/verified).
 * 1 = individual/sole proprietor, 2 = partnership/LLP, 3 = private limited/OPC.
 * Unknown or missing entity type falls back to the most restricted tier.
 */
function getOwnerEntityTier(entityType) {
  const type = String(entityType || '').trim().toLowerCase();
  if (['individual', 'sole_proprietor'].includes(type)) return 1;
  if (['partnership', 'llp'].includes(type)) return 2;
  if (['private_limited', 'opc'].includes(type)) return 3;
  return 1;
}

const NAV = [
  { id: 'dashboard', icon: '📊', label: 'Dashboard', i18nKey: 'dashboard' },
  { id: 'bookings', icon: '📋', label: 'My Bookings', i18nKey: 'myBookings' },
  { id: 'machines-hub', icon: '🚜', label: 'Machines', i18nKey: 'navSectionMachineManagement' },
  { id: 'finance-hub', icon: '💰', label: 'Finance', i18nKey: 'navSectionFinance' },
  { id: 'operations-hub', icon: '👥', label: 'Operations', i18nKey: 'navSectionOperations' },
  { id: 'account-hub', icon: '👤', label: 'Account', i18nKey: 'navSectionAccount' },
];

const OWNER_TAB_ORDER = [
  'dashboard', 'bookings',
  'machines-hub', 'finance-hub', 'operations-hub', 'account-hub',
  'machines', 'register', 'edit-registration',
  'wallet', 'reports', 'analytics', 'recovery',
  'operators', 'tracking',
  'kyc', 'settings',
  'alerts', 'notifications', 'terms', 'support',
];

const OWNER_OFF_NAV_TABS = [
  { id: 'machines', icon: '🚜', label: 'My Machines', i18nKey: 'myMachines' },
  { id: 'register', icon: '📝', label: 'Register Machine', i18nKey: 'registerMachine' },
  { id: 'edit-registration', icon: '📝', label: 'Equipment Profile', i18nKey: 'equipmentProfile' },
  { id: 'wallet', icon: '💲', label: 'Payout Wallet', i18nKey: 'ownerPayoutWallet' },
  { id: 'reports', icon: '💰', label: 'Reports', i18nKey: 'reports' },
  { id: 'analytics', icon: '📊', label: 'Fleet Analytics', i18nKey: 'fleetAnalytics' },
  { id: 'recovery', icon: '📉', label: 'Recovery Plan', i18nKey: 'recoveryPlan' },
  { id: 'operators', icon: '👥', label: 'Operators', i18nKey: 'operators' },
  { id: 'tracking', icon: '📍', label: 'GPS Tracking', i18nKey: 'tracking' },
  { id: 'kyc', icon: '🪪', label: 'KYC Verification', i18nKey: 'kycVerification' },
  { id: 'settings', icon: '⚙️', label: 'Settings', i18nKey: 'ownerSettings' },
  { id: 'alerts', icon: '⚠️', label: 'Needs Attention', i18nKey: 'alerts' },
  { id: 'notifications', icon: '📨', label: 'Message Log', i18nKey: 'notificationCenter' },
  { id: 'terms', icon: '📜', label: 'Terms & Conditions', i18nKey: 'tacTitle' },
  { id: 'support', icon: '🆘', label: 'Support', i18nKey: 'support' },
];

const DEFAULT_NAV_SECTION_OPEN = {
  navSectionMachineManagement: false,
  navSectionFinance: false,
  navSectionOperations: false,
  navSectionAccount: false,
};

function ownerNavSectionsStorageKey(ownerId) {
  return `owner_nav_sections_${ownerId || 'anon'}`;
}

function readOwnerNavSectionOpen(ownerId) {
  try {
    const raw = localStorage.getItem(ownerNavSectionsStorageKey(ownerId));
    if (!raw) return { ...DEFAULT_NAV_SECTION_OPEN };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_NAV_SECTION_OPEN };
    return {
      ...DEFAULT_NAV_SECTION_OPEN,
      ...Object.fromEntries(
        Object.keys(DEFAULT_NAV_SECTION_OPEN).map((key) => [
          key,
          parsed[key] === undefined ? DEFAULT_NAV_SECTION_OPEN[key] : Boolean(parsed[key]),
        ]),
      ),
    };
  } catch (_err) {
    return { ...DEFAULT_NAV_SECTION_OPEN };
  }
}

function writeOwnerNavSectionOpen(ownerId, next) {
  try {
    localStorage.setItem(ownerNavSectionsStorageKey(ownerId), JSON.stringify(next));
  } catch (_err) { /* ignore quota / private mode */ }
}

function sumNavBadges(items) {
  let total = 0;
  let capped = false;
  (items || []).forEach((item) => {
    if (item.badge == null || item.badge === '') return;
    if (item.badge === '99+') {
      capped = true;
      return;
    }
    const n = Number(item.badge);
    if (Number.isFinite(n)) total += n;
  });
  if (capped || total > 99) return '99+';
  return total > 0 ? total : null;
}

const REG_STEP_KEYS = ['ownerInfo', 'machineInfo', 'pdiInfo', 'documents', 'agreement'];
const AGREEMENT_TERM_KEYS = [
  'termCommission', 'termTds', 'termGstTcs', 'termPaymentDays', 'termFuel', 'termGps', 'termNoDirectContact',
];

const statusLabel = (status, translate) => {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return translate('active');
  if (s === 'idle') return translate('idle');
  return status;
};

const isBookingCompleted = (b) => {
  const s = String(b?.status || '').toLowerCase();
  return s === 'completed' || s === 'done';
};

function ownerAlertMsg(a, t) {
  switch (a.type) {
    case 'booking':
      return `${t('ownerAlertBookingPending')} ${a.bookedHours ?? '—'} ${t('ownerAlertBookingHours')}`;
    case 'quotation':
      return a.msg
        || `Contract quotation #${a.quotationRef || a.quotationId || '—'} awaiting your response (1 hour)`;
    case 'settlement':
      return `${t('ownerAlertPayoutPending').replace('{{amount}}', (a.amount ?? 0).toLocaleString('en-IN'))}`;
    case 'fuel':
      return `${a.machineId} ${t('ownerAlertFuelLow')} — ${a.fuelLevelPercent ?? 0}% (${a.litresRemaining ?? 0}L ${t('ownerAlertFuelRemaining')})`;
    case 'issue': {
      const issueTypeKeys = {
        mechanical: 'issueMechanical',
        fuel: 'issueFuel',
        accident: 'issueAccident',
        electrical: 'issueElectrical',
        site: 'issueSite',
        other: 'issueOther',
      };
      const typeLabel = a.issueType
        ? t(issueTypeKeys[a.issueType] || 'issueOther')
        : t('ownerAlertIssue');
      return `${typeLabel}: ${a.description || ''}`;
    }
    case 'compliance': {
      const fieldMap = {
        insurance_expiry: t('complianceInsurance'),
        rc_expiry: t('complianceRcBook'),
        puc_expiry: t('compliancePuc'),
      };
      const fieldLabel = a.field ? (fieldMap[a.field] || a.field) : t('complianceDates');
      if (a.alertStatus === 'expired' || a.color === '#ef4444') {
        return `${a.machineId} ${fieldLabel} ${t('complianceExpired')}`;
      }
      const dateStatus = a.expiryDate ? complianceDateStatus(a.expiryDate) : null;
      if (a.color === '#f97316' || dateStatus === 'expiring_critical') {
        return `${a.machineId} ${fieldLabel} ${t('complianceExpiringCritical')}`;
      }
      return `${a.machineId} ${fieldLabel} ${t('complianceExpiringSoon')}`;
    }
    case 'service': {
      const status = a.serviceStatus || '';
      if (status === 'overdue') {
        return `${a.machineId} ${t('serviceOverdue')}`;
      }
      return `${a.machineId} ${t('serviceDueSoon')}`;
    }
    default:
      return a.msg || '';
  }
}

function OwnerSiteWorkSection({ onAccepted }) {
  const [quotations, setQuotations] = useState([]);
  const [activeItems, setActiveItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [successById, setSuccessById] = useState({});
  const [lastSuccess, setLastSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/owner/site-work');
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load site work');
      }
      const drafts = Array.isArray(data?.quotations) ? data.quotations : [];
      setQuotations(drafts.filter((q) => String(q.status || '') === 'draft'));
      const items = Array.isArray(data?.items) ? data.items : [];
      setActiveItems(items.filter((b) => (
        b.site_work_type
        && !['Cancelled', 'Completed', 'Disputed'].includes(String(b.status || ''))
      )));
    } catch (err) {
      setError(err?.message || 'Could not load site work');
      setQuotations([]);
      setActiveItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAccept = async (quotation) => {
    if (!quotation?.id || busyId) return;
    setBusyId(quotation.id);
    setError('');
    try {
      const result = await secureFetch(
        `/api/owner/site-work/${encodeURIComponent(quotation.id)}/accept`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to accept site work');
      }
      setSuccessById((prev) => ({
        ...prev,
        [quotation.id]: '✅ Site work accepted — booking created',
      }));
      setLastSuccess('✅ Site work accepted — booking created');
      setQuotations((prev) => prev.filter((q) => q.id !== quotation.id));
      if (typeof onAccepted === 'function') onAccepted();
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to accept site work');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      marginBottom: '16px',
      background: 'linear-gradient(135deg, #0a1628, #060e1c)',
      border: quotations.length > 0 ? '1px solid rgba(255,152,0,0.4)' : '1px solid rgba(201,168,76,0.2)',
      borderRadius: '12px',
      padding: '16px',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <h3 style={{ color: '#c9a84c', fontSize: '14px', fontWeight: 800, margin: 0 }}>Site Work</h3>
        <button
          type="button"
          onClick={load}
          style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '6px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {error && <p style={{ color: '#e94560', fontSize: '12px', fontWeight: 600, margin: '0 0 10px' }}>{error}</p>}
      {lastSuccess && (
        <p style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 800, margin: '0 0 10px', textAlign: 'center' }}>
          {lastSuccess}
        </p>
      )}
      {quotations.length === 0 && activeItems.length === 0 && !loading ? (
        <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>No pending site work quotations.</p>
      ) : null}
      {quotations.map((q) => {
        const unit = q.billing_basis === 'per_meter' ? 'm' : 'ft';
        const busy = busyId === q.id;
        return (
          <div
            key={q.id}
            style={{
              background: 'linear-gradient(135deg, #0a1628, #060e1c)',
              border: '1px solid rgba(201,168,76,0.28)',
              borderRadius: '10px',
              padding: '14px',
              marginBottom: '8px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <span style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                {String(q.site_work_type || 'site work').replace(/_/g, ' ')}
              </span>
              <span style={{ background: 'rgba(255,152,0,0.15)', border: '1px solid #FF9800', color: '#FF9800', padding: '3px 10px', borderRadius: '20px', fontSize: '11px' }}>
                draft
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginBottom: '12px' }}>
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px' }}>
                <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Billing basis</p>
                <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>{String(q.billing_basis || '—').replace(/_/g, ' ')}</p>
              </div>
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px' }}>
                <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Estimated depth</p>
                <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
                  {q.estimated_depth != null ? `${Number(q.estimated_depth).toLocaleString('en-IN')} ${unit}` : '—'}
                </p>
              </div>
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px' }}>
                <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Soft / murram / rock</p>
                <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
                  ₹{Number(q.strata_rate_soft || 0).toLocaleString('en-IN')}
                  {' / '}
                  ₹{Number(q.strata_rate_murram || 0).toLocaleString('en-IN')}
                  {' / '}
                  ₹{Number(q.strata_rate_rock || 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px' }}>
                <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Advance (25%)</p>
                <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800, margin: 0 }}>
                  ₹{Number(q.advance_amount || 0).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
            {successById[q.id] && (
              <p style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 800, margin: '0 0 8px', textAlign: 'center' }}>
                {successById[q.id]}
              </p>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => handleAccept(q)}
              style={{
                width: '100%',
                padding: '11px',
                background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                color: '#030810',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 800,
                cursor: busy ? 'wait' : 'pointer',
                fontSize: '13px',
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? 'Accepting…' : 'Accept Site Work →'}
            </button>
          </div>
        );
      })}
      {activeItems.length > 0 && (
        <div style={{ marginTop: quotations.length > 0 ? '12px' : 0 }}>
          <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800, margin: '0 0 8px' }}>Active site work</p>
          {activeItems.map((b) => {
            const depthDone = Number(b.total_depth_completed || 0);
            const depthEst = Number(b.estimated_depth || 0);
            const unit = b.billing_basis === 'per_meter' ? 'm' : 'ft';
            const pct = depthEst > 0 ? Math.min(100, Math.round((depthDone / depthEst) * 100)) : 0;
            return (
              <div
                key={b.id}
                style={{
                  background: 'rgba(0,0,0,0.28)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: '10px',
                  padding: '12px',
                  marginBottom: '8px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <span style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800 }}>
                    {b.booking_ref || b.id} · {String(b.site_work_type || '').replace(/_/g, ' ')}
                  </span>
                  <span style={{ color: '#8896a8', fontSize: '11px' }}>{b.status || 'Active'}</span>
                </div>
                <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: '0 0 6px' }}>
                  Depth {depthDone.toLocaleString('en-IN')} / {depthEst.toLocaleString('en-IN')} {unit}
                </p>
                <div style={{ height: '8px', borderRadius: '20px', background: 'rgba(201,168,76,0.15)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #a07830, #e2c97e)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const OwnerMonthAccordion = ({
  month, dayGroups, bookingGridCols, setOwnerBookings, t, machineLookup,
  onBookingRefresh,
  approvingId, completingId, onApprove, onComplete,
  ownerTier = 'lite',
  approveErrorByBookingId = {},
  awaitingMobQuotations = [],
  confirmedMobQuotations = [],
  mobilizationReleasedByBookingId = {},
  convertedBookingIds = {},
  mobilizationActionId = null,
  convertActionId = null,
  onConfirmMobilization,
  onConvertQuotation,
  pendingTerminations = {},
  onRequestTermination,
  onOpenLoadingMilestone,
  loadingMilestoneBusyId = null,
}) => {
  const [open, setOpen] = React.useState(false);
  const total = dayGroups.reduce((sum, group) => sum + group.bookings.length, 0);
  const pending = dayGroups.reduce((sum, group) => sum + group.bookings.filter(
    (b) => !b.owner_approved
      && !['Cancelled', 'Completed', 'Disputed'].includes(b.status),
  ).length, 0);
  return (
    <div style={{ marginBottom: '12px', border: pending > 0 ? '1px solid rgba(255,152,0,0.4)' : '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', overflow: 'hidden' }}>
      <div onClick={() => setOpen(!open)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: 'linear-gradient(135deg, #0f2040, #0a1628)', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '18px' }}>{String.fromCodePoint(0x1F4C5)}</span>
          <span style={{ color: '#c9a84c', fontWeight: '700', fontSize: '15px' }}>{month}</span>
          <span style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '2px 10px', borderRadius: '20px', fontSize: '11px' }}>{t('bookingsCount').replace('{count}', total)}</span>
          {pending > 0 && <span style={{ background: 'rgba(255,152,0,0.15)', border: '1px solid #FF9800', color: '#FF9800', padding: '2px 10px', borderRadius: '20px', fontSize: '11px' }}>{t('pendingCount').replace('{count}', pending)}</span>}
        </div>
        <span style={{ color: '#c9a84c', fontSize: '18px' }}>{open ? String.fromCodePoint(0x25B2) : String.fromCodePoint(0x25BC)}</span>
      </div>
      {open && (
        <div style={{ padding: '10px' }}>
          {dayGroups.map(({ day, bookings }) => {
            const dayPending = bookings.filter(
              (b) => !b.owner_approved
                && !['Cancelled', 'Completed', 'Disputed'].includes(b.status),
            ).length;
            return (
              <div key={day} style={{ marginBottom: '10px', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '10px', overflow: 'hidden', background: 'rgba(0,0,0,0.15)' }}>
                <div style={{ padding: '12px 14px', background: 'rgba(201,168,76,0.08)', borderBottom: '1px solid rgba(201,168,76,0.12)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ color: '#c9a84c', fontSize: '13px', fontWeight: 700 }}>{String.fromCodePoint(0x1F4C6)} {day}</span>
                    <span style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', color: '#4CAF50', padding: '1px 8px', borderRadius: '20px', fontSize: '10px' }}>{t('bookingsCount').replace('{count}', bookings.length)}</span>
                    {dayPending > 0 && <span style={{ background: 'rgba(255,152,0,0.15)', border: '1px solid #FF9800', color: '#FF9800', padding: '1px 8px', borderRadius: '20px', fontSize: '10px' }}>{t('pendingCount').replace('{count}', dayPending)}</span>}
                  </div>
                </div>
                <div style={{ padding: '8px' }}>
                  {bookings.map((b, i) => {
                    const bookingAt = parseBookingDate(b);
                    const machineLabel = getBookingMachineLabel(b, machineLookup);
                    const siteAddress = String(b.site_address || '').trim();
                    const sitePincode = String(b.site_pincode || '').trim();
                    const siteLat = Number(b.site_lat);
                    const siteLng = Number(b.site_lng);
                    const hasSiteCoords = Number.isFinite(siteLat) && Number.isFinite(siteLng);
                    const showSiteLocation = Boolean(siteAddress || sitePincode || hasSiteCoords);
                    return (
                      <div key={b.id || i} style={{ background: 'linear-gradient(135deg, #0a1628, #060e1c)', border: b.owner_approved ? '1px solid rgba(76,175,80,0.3)' : '1px solid rgba(255,152,0,0.3)', borderRadius: '10px', padding: '14px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                          <div>
                            <span style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>{b.booking_ref || b.id}</span>
                            <span style={{ marginLeft: '8px', color: '#8896a8', fontSize: '11px' }}>{bookingAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <span style={{ background: b.owner_approved ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)', border: b.owner_approved ? '1px solid #4CAF50' : '1px solid #FF9800', color: b.owner_approved ? '#4CAF50' : '#FF9800', padding: '3px 10px', borderRadius: '20px', fontSize: '11px' }}>{['Cancelled'].includes(b.status)
                            ? `❌ ${t('statusCancelled')}`
                            : b.status === 'Completed'
                              ? `✅ ${t('statusCompleted')}`
                              : b.status === 'Disputed'
                                ? `⚠️ ${t('statusDisputed')}`
                                : b.owner_approved
                                  ? `✅ ${t('statusApproved')}`
                                  : `⏳ ${t('statusPending')}`}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: bookingGridCols, gap: '8px', marginBottom: '12px' }}>
                          {[
                            { label: t('client'), val: 'Client' },
                            { label: t('machine'), val: machineLabel },
                            { label: t('type'),
                              val: b.site_work_type
                                ? `Site Work · ${String(b.site_work_type).replace(/_/g, ' ')}`
                                : b.booking_type === 'contract'
                                  ? 'Contract'
                                  : (b.booking_unit || b.booking_type || '—'),
                            },
                            { label: t('location'), val: b.location || 'N/A' },
                            { label: t('amount'), val: `₹${Number(b.base_amount || 0).toLocaleString('en-IN')}` },
                            { label: t('advance'), val: `₹${Number(b.advance_paid || 0).toLocaleString('en-IN')}` },
                            ...(b.selected_attachment
                              ? [{
                                label: t('bookingAttachmentLabel'),
                                val: formatBookingAttachmentSummary(b),
                              }]
                              : []),
                          ].map((d, j) => (
                            <div key={j} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px' }}>
                              <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>{d.label}</p>
                              <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: '600', margin: 0 }}>{d.val}</p>
                            </div>
                          ))}
                        </div>
                        {b.site_work_type && (
                          <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.22)', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
                            <p style={{ color: '#c9a84c', fontSize: '11px', fontWeight: 800, margin: '0 0 6px' }}>
                              Site Work — {String(b.site_work_type).replace(/_/g, ' ')}
                            </p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: '0 0 6px' }}>
                              Depth {Number(b.total_depth_completed || 0).toLocaleString('en-IN')}
                              {' / '}
                              {Number(b.estimated_depth || 0).toLocaleString('en-IN')}
                              {' '}
                              {b.billing_basis === 'per_meter' ? 'm' : 'ft'}
                            </p>
                            <div style={{ height: '8px', borderRadius: '20px', background: 'rgba(201,168,76,0.15)', overflow: 'hidden' }}>
                              <div style={{
                                width: `${Number(b.estimated_depth) > 0
                                  ? Math.min(100, Math.round((Number(b.total_depth_completed || 0) / Number(b.estimated_depth)) * 100))
                                  : 0}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #a07830, #e2c97e)',
                              }}
                              />
                            </div>
                          </div>
                        )}
                        {showSiteLocation && (
                          <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
                            {siteAddress && (
                              <div style={{ marginBottom: sitePincode || hasSiteCoords ? '8px' : 0 }}>
                                <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>{t('siteAddressLabel')}</p>
                                <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: '600', margin: 0, wordBreak: 'break-word' }}>{siteAddress}</p>
                              </div>
                            )}
                            {sitePincode && (
                              <div style={{ marginBottom: hasSiteCoords ? '8px' : 0 }}>
                                <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>{t('sitePincodeLabel')}</p>
                                <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: '600', margin: 0 }}>{sitePincode}</p>
                              </div>
                            )}
                            {hasSiteCoords && (
                              <a
                                href={`https://www.google.com/maps?q=${encodeURIComponent(siteLat)},${encodeURIComponent(siteLng)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-block', color: '#c9a84c', fontSize: '12px', fontWeight: '600', textDecoration: 'none' }}
                              >
                                {t('openInMaps')}
                              </a>
                            )}
                          </div>
                        )}
                        {(b.selected_attachment || b.site_location || b.work_description) && (
                          <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
                            {b.selected_attachment && (
                              <div style={{
                                background: 'rgba(255,152,0,0.12)',
                                border: '1px solid #C9A84C',
                                borderRadius: '8px',
                                padding: '10px',
                                marginBottom: (b.site_location || b.work_description) ? '8px' : 0,
                              }}
                              >
                                <p style={{
                                  color: '#FFB74D',
                                  fontSize: '10px',
                                  fontWeight: 800,
                                  margin: '0 0 4px',
                                  letterSpacing: '0.3px',
                                }}
                                >
                                  ⚠️ Attachment Required
                                </p>
                                <p style={{
                                  color: '#e8e0d0',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  margin: 0,
                                  wordBreak: 'break-word',
                                }}
                                >
                                  {formatBookingAttachmentSummary(b)}
                                </p>
                              </div>
                            )}
                            {b.site_location && (
                              <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 6px', wordBreak: 'break-word' }}>
                                {String.fromCodePoint(0x1F4CD)} {t('bookingSiteLabel')}: {b.site_location}
                              </p>
                            )}
                            {b.work_description && (
                              <p style={{ color: '#e8e0d0', fontSize: '12px', margin: 0, wordBreak: 'break-word' }}>
                                {String.fromCodePoint(0x1F4CB)} {t('bookingWorkLabel')}: {b.work_description}
                              </p>
                            )}
                          </div>
                        )}
                        {b.booking_type === 'contract' ? (
                          <>
                            {pendingTerminations[b.id]?.status === 'pending' && (
                              <p style={{
                                margin: '8px 0',
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,152,0,0.4)',
                                background: 'rgba(255,152,0,0.1)',
                                color: '#FFB74D',
                                fontSize: '11px',
                                fontWeight: 700,
                              }}
                              >
                                ⏳ Termination pending admin approval
                              </p>
                            )}
                            <ContractBookingStatus booking={b} role="owner" />
                            {(() => {
                              const penaltyQ = (awaitingMobQuotations || []).find((q) => q.bookingId === b.id)
                                || (confirmedMobQuotations || []).find((q) => q.bookingId === b.id);
                              const noshow = Number(penaltyQ?.ownerNoshowPenaltyPct ?? 25);
                              const abandon = Number(penaltyQ?.ownerAbandonmentPenaltyPct ?? 20);
                              const breakdown = Number(penaltyQ?.ownerBreakdownCreditPct ?? 5);
                              const clientExit = Number(penaltyQ?.clientTerminationPenaltyPct ?? 20);
                              return (
                                <div style={{
                                  marginTop: '8px',
                                  marginBottom: '8px',
                                  background: 'rgba(201,168,76,0.06)',
                                  border: '1px solid rgba(201,168,76,0.22)',
                                  borderRadius: '8px',
                                  padding: '10px',
                                }}
                                >
                                  <p style={{ color: '#c9a84c', fontSize: '11px', fontWeight: 800, margin: '0 0 8px' }}>
                                    Penalty terms
                                  </p>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <div>
                                      <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>No-show</p>
                                      <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>{noshow}%</p>
                                    </div>
                                    <div>
                                      <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Abandonment</p>
                                      <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>{abandon}%</p>
                                    </div>
                                    <div>
                                      <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Breakdown credit</p>
                                      <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>{breakdown}%/week</p>
                                    </div>
                                    <div>
                                      <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Client termination</p>
                                      <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>{clientExit}%</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            {Number(b.mobilization_transport_amount || 0) > 0 && (
                              <div style={{ marginTop: '8px' }}>
                                {!b.mobilization_loading_submitted_at ? (
                                  <button
                                    type="button"
                                    disabled={loadingMilestoneBusyId === b.id}
                                    onClick={() => onOpenLoadingMilestone?.(b)}
                                    style={{
                                      width: '100%',
                                      padding: '11px',
                                      background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                                      color: '#030810',
                                      border: 'none',
                                      borderRadius: '8px',
                                      fontWeight: 800,
                                      cursor: loadingMilestoneBusyId === b.id ? 'wait' : 'pointer',
                                      fontSize: '13px',
                                      opacity: loadingMilestoneBusyId === b.id ? 0.7 : 1,
                                    }}
                                  >
                                    {loadingMilestoneBusyId === b.id ? t('loading') : 'Submit Loading Milestone'}
                                  </button>
                                ) : !b.mobilization_loading_approved_at ? (
                                  <div style={{
                                    padding: '10px 12px',
                                    background: 'rgba(255,152,0,0.1)',
                                    border: '1px solid rgba(255,152,0,0.35)',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                  }}
                                  >
                                    <p style={{ color: '#FFB74D', fontSize: '12px', fontWeight: 800, margin: 0 }}>
                                      Loading submitted — awaiting admin approval
                                    </p>
                                  </div>
                                ) : (
                                  <div style={{
                                    padding: '10px 12px',
                                    background: 'rgba(76,175,80,0.1)',
                                    border: '1px solid rgba(76,175,80,0.35)',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                  }}
                                  >
                                    <p style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 800, margin: 0 }}>
                                      Loading approved
                                      {Number(b.mobilization_loading_released || 0) > 0
                                        ? ` — ₹${Number(b.mobilization_loading_released).toLocaleString('en-IN')} released`
                                        : ''}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {!pendingTerminations[b.id]
                              && !['Cancelled', 'Completed'].includes(String(b.status || '')) && (
                              <button
                                type="button"
                                onClick={() => onRequestTermination?.(b)}
                                style={{
                                  width: '100%',
                                  marginTop: '8px',
                                  padding: '10px',
                                  background: 'rgba(40,40,50,0.55)',
                                  border: '1px solid rgba(136,150,168,0.4)',
                                  color: '#aab4c3',
                                  borderRadius: '8px',
                                  fontWeight: 700,
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Request Termination
                              </button>
                            )}
                          </>
                        ) : null}
                        {b.booking_type === 'contract' && (() => {
                          const awaitingQ = awaitingMobQuotations.find((q) => q.bookingId === b.id);
                          const confirmedQ = confirmedMobQuotations.find((q) => q.bookingId === b.id);
                          const releasedAmt = mobilizationReleasedByBookingId[b.id];
                          const isConverted = Boolean(convertedBookingIds[b.id])
                            || confirmedQ?.status === 'converted';

                          if (isConverted) {
                            return (
                              <div style={{
                                padding: '10px 12px',
                                marginTop: '8px',
                                marginBottom: '8px',
                                background: 'rgba(76,175,80,0.1)',
                                border: '1px solid rgba(76,175,80,0.35)',
                                borderRadius: '8px',
                                textAlign: 'center',
                              }}
                              >
                                {releasedAmt != null && (
                                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>
                                    50% advance released · ₹{Number(releasedAmt || 0).toLocaleString('en-IN')}
                                  </p>
                                )}
                                <p style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 800, margin: 0 }}>
                                  ✅ 50% advance released — Machine dispatched
                                </p>
                              </div>
                            );
                          }

                          if (confirmedQ) {
                            const busy = convertActionId === confirmedQ.id;
                            return (
                              <div>
                                {releasedAmt != null && (
                                  <div style={{
                                    padding: '10px 12px',
                                    marginTop: '8px',
                                    marginBottom: '8px',
                                    background: 'rgba(76,175,80,0.1)',
                                    border: '1px solid rgba(76,175,80,0.35)',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                  }}
                                  >
                                    <p style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 800, margin: 0 }}>
                                      50% advance released
                                    </p>
                                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '4px 0 0' }}>
                                      ₹{Number(releasedAmt || 0).toLocaleString('en-IN')} credited to your wallet
                                    </p>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => onConvertQuotation?.(confirmedQ, b)}
                                  style={{
                                    width: '100%',
                                    padding: '11px',
                                    marginTop: '8px',
                                    marginBottom: '8px',
                                    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                                    color: '#030810',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 800,
                                    cursor: busy ? 'wait' : 'pointer',
                                    fontSize: '13px',
                                    opacity: busy ? 0.7 : 1,
                                  }}
                                >
                                  {busy ? t('loading') : 'Convert to Active Contract →'}
                                </button>
                              </div>
                            );
                          }

                          if (releasedAmt != null) {
                            return (
                              <div style={{
                                padding: '10px 12px',
                                marginTop: '8px',
                                marginBottom: '8px',
                                background: 'rgba(76,175,80,0.1)',
                                border: '1px solid rgba(76,175,80,0.35)',
                                borderRadius: '8px',
                                textAlign: 'center',
                              }}
                              >
                                <p style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 800, margin: 0 }}>
                                  50% advance released
                                </p>
                                <p style={{ color: '#8896a8', fontSize: '10px', margin: '4px 0 0' }}>
                                  ₹{Number(releasedAmt || 0).toLocaleString('en-IN')} credited to your wallet
                                </p>
                              </div>
                            );
                          }
                          if (!awaitingQ) return null;
                          const busy = mobilizationActionId === awaitingQ.id;
                          return (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => onConfirmMobilization?.(awaitingQ, b)}
                              style={{
                                width: '100%',
                                padding: '11px',
                                marginTop: '8px',
                                marginBottom: '8px',
                                background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                                color: '#030810',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 800,
                                cursor: busy ? 'wait' : 'pointer',
                                fontSize: '13px',
                                opacity: busy ? 0.7 : 1,
                              }}
                            >
                              {busy ? t('loading') : 'Confirm Mobilization & Dispatch →'}
                            </button>
                          );
                        })()}
                        {!b.owner_approved && !['Cancelled'].includes(b.status) && (
                          <>
                            {(String(b.booking_type || '').toLowerCase() === 'contract'
                              && (String(ownerTier || 'lite').toLowerCase() === 'lite'
                                || approveErrorByBookingId[b.id])) && (
                              <div
                                style={{
                                  padding: '8px 10px',
                                  marginBottom: '8px',
                                  background: 'rgba(233,69,96,0.1)',
                                  border: '1px solid rgba(233,69,96,0.35)',
                                  borderRadius: '8px',
                                  color: '#e94560',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  lineHeight: 1.45,
                                }}
                              >
                                {approveErrorByBookingId[b.id] || CONTRACT_VERIFIED_REQUIRED_MSG}
                              </div>
                            )}
                            <button
                              style={{ width: '100%', padding: '11px', background: 'linear-gradient(135deg, #2e7d32, #4CAF50)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: approvingId === b.id ? 'wait' : 'pointer', fontSize: '13px', opacity: approvingId === b.id ? 0.7 : 1 }}
                              disabled={approvingId === b.id}
                              onClick={() => onApprove(b, setOwnerBookings)}
                            >
                              {approvingId === b.id ? t('loading') : `${String.fromCodePoint(0x2705)} ${t('approveAndDispatch')}`}
                            </button>
                          </>
                        )}
                        {b.owner_approved && b.owner_approved_at && (
                          <div style={{ padding: '8px 10px', background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: '8px', textAlign: 'center', marginBottom: '8px' }}>
                            <span style={{ color: '#4CAF50', fontSize: '11px' }}>{String.fromCodePoint(0x2705)} {t('machineDispatchedAt')} - {new Date(b.owner_approved_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                        {b.booking_type !== 'contract' && (
                          <BookingFlowV2Card booking={b} viewOnly onRefresh={onBookingRefresh} />
                        )}
                        {b.booking_type !== 'contract'
                          && b.owner_approved
                          && !isBookingCompleted(b)
                          && !['Dispatched', 'Extension_Pending', 'Completing', 'Cancelled', 'Disputed'].includes(b.status) && (
                          <button
                            type="button"
                            style={{ width: '100%', padding: '11px', marginBottom: '8px', background: 'linear-gradient(135deg, #c9a84c, #8b6914)', color: '#0a1628', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: completingId === b.id ? 'wait' : 'pointer', fontSize: '13px', opacity: completingId === b.id ? 0.7 : 1 }}
                            disabled={completingId === b.id}
                            onClick={() => onComplete(b, setOwnerBookings)}
                          >
                            {completingId === b.id ? t('ownerWorkCompleting') : t('ownerMarkWorkComplete')}
                          </button>
                        )}
                        {isBookingCompleted(b) && (
                          <div style={{ padding: '8px 10px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '8px', textAlign: 'center', marginBottom: '8px' }}>
                            <span style={{ color: '#c9a84c', fontSize: '11px', fontWeight: 600 }}>{t('statusCompleted')}</span>
                          </div>
                        )}
                        {b.status === 'Disputed' && (
                          <button
                            type="button"
                            style={{
                              width: '100%',
                              padding: '10px',
                              marginBottom: '8px',
                              background: 'rgba(233,69,96,0.1)',
                              border: '1px solid rgba(233,69,96,0.4)',
                              color: '#e94560',
                              borderRadius: '8px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                            onClick={() => {
                              generateDisputeStatement(b).catch((err) => {
                                console.error('Dispute statement PDF failed:', err);
                                alert(err?.message || t('pdfReportFailed'));
                              });
                            }}
                          >
                            {String.fromCodePoint(0x1F4C4)} {t('downloadDisputeStatement')}
                          </button>
                        )}
                        <BookingFeedbackSummary booking={b} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** SOP Phase 1–4 onboarding stepper — English labels this sprint */
function OnboardingTracker({ isMobile }) {
  const { t } = useLanguage();
  const phaseLabels = {
    1: t('onboardingTrackerPhase1'),
    2: t('onboardingTrackerPhase2'),
    3: t('onboardingTrackerPhase3'),
    4: t('onboardingTrackerPhase4'),
  };
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await secureFetch('/api/owner/onboarding-status');
        if (!cancelled && data?.phases) setPayload(data);
      } catch (err) {
        if ((err?.message || '') === 'auth_required') return;
      }
    };
    load();
    const poll = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      load();
    }, 60000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, []);

  if (!payload || payload.overall_complete === true) return null;

  const phases = Array.isArray(payload.phases) ? payload.phases : [];

  const helperFor = (phase, status) => {
    if (phase === 2 && status === 'pending') return t('onboardingTrackerHelper1');
    if (phase === 2 && status === 'in_progress') return t('onboardingTrackerHelper2');
    if (phase === 2 && status === 'rejected') return t('onboardingTrackerHelper3');
    if (phase === 3 && status === 'pending') return t('onboardingTrackerHelper4');
    if (phase === 3 && status === 'in_progress') return t('onboardingTrackerHelper5');
    if (phase === 4 && status === 'pending') return t('onboardingTrackerHelper6');
    return null;
  };

  const statusBadge = (status) => {
    if (status === 'complete') {
      return (
        <span style={{ color: '#C9A84C', fontWeight: 800, fontSize: 13 }} aria-label={t('onboardingTrackerComplete')}>✓</span>
      );
    }
    if (status === 'in_progress') {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(201,168,76,0.12)',
          border: '1px solid rgba(201,168,76,0.45)',
          borderRadius: 999,
          padding: '3px 8px',
          color: '#C9A84C',
          fontSize: 10,
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              border: '2px solid rgba(201,168,76,0.35)',
              borderTopColor: '#C9A84C',
              animation: 'od-onboard-spin 0.9s linear infinite',
            }}
          />
          {t('onboardingTrackerInProgress')}
        </span>
      );
    }
    if (status === 'rejected') {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          color: '#e94560',
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
        >
          <span aria-hidden="true">✗</span> {t('onboardingTrackerActionRequired')}
        </span>
      );
    }
    return (
      <span style={{ color: '#6b7280', fontSize: 13 }} aria-label={t('onboardingTrackerPending')}>🔒</span>
    );
  };

  return (
    <div style={{
      borderRadius: 12,
      border: '1px solid rgba(201,168,76,0.3)',
      background: 'linear-gradient(135deg, rgba(18,42,74,0.95), rgba(6,14,28,0.98))',
      padding: isMobile ? '14px 12px' : '16px 18px',
      marginBottom: 4,
    }}
    >
      <style>{`
        @keyframes od-onboard-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <p style={{
        margin: '0 0 14px',
        color: '#C9A84C',
        fontSize: isMobile ? 13 : 14,
        fontWeight: 700,
      }}
      >
        {t('onboardingTrackerTitle')}
      </p>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        alignItems: isMobile ? 'stretch' : 'flex-start',
        justifyContent: 'space-between',
      }}
      >
        {phases.map((p, idx) => {
          const helper = helperFor(p.phase, p.status);
          return (
            <React.Fragment key={p.phase}>
              <div style={{
                flex: isMobile ? 'none' : '1 1 0',
                minWidth: 0,
                display: 'flex',
                flexDirection: isMobile ? 'row' : 'column',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? 10 : 8,
                textAlign: isMobile ? 'left' : 'center',
              }}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: p.status === 'complete'
                    ? 'linear-gradient(135deg, #c9a84c, #8b6914)'
                    : p.status === 'rejected'
                      ? 'rgba(233,69,96,0.2)'
                      : 'rgba(201,168,76,0.12)',
                  border: p.status === 'rejected'
                    ? '1px solid rgba(233,69,96,0.5)'
                    : '1px solid rgba(201,168,76,0.5)',
                  color: p.status === 'complete' ? '#0a1628' : '#C9A84C',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
                >
                  {p.phase}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: '#e8e0d0',
                    fontSize: 12,
                    fontWeight: 700,
                    marginBottom: 4,
                  }}
                  >
                    {phaseLabels[p.phase] || p.label || `${t('onboardingTrackerPhaseLabel')} ${p.phase}`}
                  </div>
                  <div style={{ marginBottom: helper ? 4 : 0 }}>
                    {statusBadge(p.status)}
                  </div>
                  {helper ? (
                    <p style={{
                      margin: 0,
                      color: '#8896a8',
                      fontSize: 10,
                      lineHeight: 1.4,
                    }}
                    >
                      {helper}
                    </p>
                  ) : null}
                </div>
              </div>
              {!isMobile && idx < phases.length - 1 ? (
                <div
                  aria-hidden="true"
                  style={{
                    width: 24,
                    height: 2,
                    marginTop: 15,
                    flexShrink: 0,
                    background: 'rgba(201,168,76,0.35)',
                    alignSelf: 'flex-start',
                  }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
      {payload.overall_complete === false ? (
        <p style={{
          margin: '14px 0 0',
          color: '#C9A84C',
          fontSize: 11,
          fontWeight: 600,
          textAlign: isMobile ? 'left' : 'center',
        }}
        >
          {t('onboardingTrackerTarget')}
        </p>
      ) : null}
    </div>
  );
}


const OwnerDashboard = () => {
  const navigate = useNavigate();
  useSessionTimeout();
  const { t, lang } = useLanguage(); // eslint-disable-line
  const navItems = NAV.map((item) => ({
    ...item,
    label: item.i18nKey ? t(item.i18nKey) : item.label,
    sectionLabel: item.sectionKey ? t(item.sectionKey) : null,
  }));
  const ownerTabTitleItems = [
    ...NAV,
    ...OWNER_OFF_NAV_TABS,
  ].map((n) => {
    const translated = n.i18nKey ? t(n.i18nKey) : n.label;
    const label = (n.id === 'edit-registration'
      && (!translated || translated === n.i18nKey))
      ? (n.label || 'Equipment Profile')
      : translated;
    return { ...n, label };
  });
  const { isMobile, isTablet, gridCols } = useWindowSize();
  const layoutMode = useDeviceLayout();
  const useMobileNav = layoutMode === 'phone';
  const sidebarOverlay = layoutMode === 'tablet';
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    setSidebarOpen(!sidebarOverlay);
  }, [sidebarOverlay]);

  const [sessionUser, setSessionUser] = useState(() => readSessionUser());
  const ownerDisplayName = sessionUser.name || sessionUser.full_name || OWNER_DATA.name;
  const ownerPhone = sessionUser.phone || OWNER_DATA.phone;
  const ownerEmail = sessionUser.email || OWNER_DATA.email;
  const ownerSince = sessionUser.created_at
    ? new Date(sessionUser.created_at).getFullYear().toString()
    : OWNER_DATA.since;

  const [showPhoneChangeModal, setShowPhoneChangeModal] = useState(false);

  const handleOwnerPhoneChanged = (newPhone) => {
    setSessionUser((prev) => {
      const next = { ...prev, phone: newPhone };
      localStorage.setItem('developmentexpress_user', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    const loadTermsPrompt = async () => {
      try {
        const payload = await getTermsStatus();
        if (cancelled || !payload?.success) return;
        const currentVersion = payload.currentVersion || '';
        const userVersion = payload?.user?.terms_version || '';
        const needs = Boolean(payload.needsAcknowledgement);
        if (!needs || !currentVersion) return;
        const seenKey = `owner_terms_prompt_seen_${sessionUser?.id || sessionUser?.user_id || 'anon'}_${currentVersion}`;
        const alreadySeen = localStorage.getItem(seenKey) === '1';
        if (alreadySeen) return;
        localStorage.setItem(seenKey, '1');
        setTermsPrompt({
          visible: true,
          currentVersion,
          userVersion,
          acknowledging: false,
          error: '',
        });
      } catch (_err) {
        // non-blocking
      }
    };
    loadTermsPrompt();
    return () => { cancelled = true; };
  }, [sessionUser?.id, sessionUser?.user_id]);

  const handleAcknowledgeTerms = async () => {
    setTermsPrompt((prev) => ({ ...prev, acknowledging: true, error: '' }));
    try {
      const payload = await acknowledgeTerms();
      if (!payload?.success) {
        throw new Error('Could not acknowledge terms right now.');
      }
      setSessionUser((prev) => {
        const next = {
          ...prev,
          terms_accepted: true,
          terms_accepted_at: payload.user?.terms_accepted_at || new Date().toISOString(),
          terms_version: payload.user?.terms_version || termsPrompt.currentVersion,
        };
        localStorage.setItem('developmentexpress_user', JSON.stringify(next));
        return next;
      });
      setTermsPrompt((prev) => ({ ...prev, visible: false, acknowledging: false, error: '' }));
    } catch (err) {
      setTermsPrompt((prev) => ({
        ...prev,
        acknowledging: false,
        error: err?.message || 'Could not acknowledge terms right now.',
      }));
    }
  };

  const [activeTab, setActiveTab] = useState('dashboard');
  const ownerNavUserId = sessionUser?.id || sessionUser?.user_id || 'anon';
  const [ownerTier, setOwnerTier] = useState('lite');
  const [liteUpgradeDismissed, setLiteUpgradeDismissed] = useState(false);
  const [approveErrorByBookingId, setApproveErrorByBookingId] = useState({});
  const liteUpgradeDismissKey = `owner_lite_upgrade_dismissed_${ownerNavUserId}`;

  useEffect(() => {
    try {
      setLiteUpgradeDismissed(sessionStorage.getItem(liteUpgradeDismissKey) === '1');
    } catch (_err) {
      setLiteUpgradeDismissed(false);
    }
  }, [liteUpgradeDismissKey]);

  const dismissLiteUpgradeBanner = () => {
    setLiteUpgradeDismissed(true);
    try {
      sessionStorage.setItem(liteUpgradeDismissKey, '1');
    } catch (_err) {
      // ignore
    }
  };

  const ownerEntityTier = getOwnerEntityTier(sessionUser?.owner_entity_type);
  const [entityTierNoteDismissed, setEntityTierNoteDismissed] = useState(false);
  const entityTierNoteDismissKey = `owner_entity_tier_note_dismissed_${ownerNavUserId}`;

  useEffect(() => {
    try {
      setEntityTierNoteDismissed(sessionStorage.getItem(entityTierNoteDismissKey) === '1');
    } catch (_err) {
      setEntityTierNoteDismissed(false);
    }
  }, [entityTierNoteDismissKey]);

  const dismissEntityTierNote = () => {
    setEntityTierNoteDismissed(true);
    try {
      sessionStorage.setItem(entityTierNoteDismissKey, '1');
    } catch (_err) {
      // ignore
    }
  };

  // Deep link or restored tab must not land on a tab this entity tier cannot see.
  useEffect(() => {
    if (activeTab === 'analytics' && ownerEntityTier < 2) setActiveTab('dashboard');
  }, [activeTab, ownerEntityTier]);

  useEffect(() => {
    const savedTab = sessionStorage.getItem('returnTab');
    if (savedTab) {
      sessionStorage.removeItem('returnTab');
      setActiveTab(savedTab);
    }
  }, []);

  const applyOwnerProfileTier = useCallback((profile) => {
    if (!profile) return;
    setOwnerTier(String(profile.owner_tier || 'lite').toLowerCase() === 'verified'
      ? 'verified'
      : 'lite');
  }, []);

  const [navSectionOpen, setNavSectionOpen] = useState(() => readOwnerNavSectionOpen(ownerNavUserId));

  useEffect(() => {
    setNavSectionOpen(readOwnerNavSectionOpen(ownerNavUserId));
  }, [ownerNavUserId]);

  const isNavSectionOpen = (sectionKey) => {
    if (!sectionKey) return true;
    if (navSectionOpen[sectionKey]) return true;
    const activeItem = NAV.find((item) => item.id === activeTab);
    return activeItem?.sectionKey === sectionKey;
  };

  const toggleNavSection = (sectionKey) => {
    if (!sectionKey || !(sectionKey in DEFAULT_NAV_SECTION_OPEN)) return;
    setNavSectionOpen((prev) => {
      // Toggle stored preference only. Display may still force-open when the
      // active tab lives in this group (see isNavSectionOpen) without writing
      // that forced state to localStorage.
      const next = { ...prev, [sectionKey]: !prev[sectionKey] };
      writeOwnerNavSectionOpen(ownerNavUserId, next);
      return next;
    });
  };

  const [regStep, setRegStep] = useState(1);
  const regStep2Ref = useRef(null);
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [opStep, setOpStep] = useState(1);
  const [opData, setOpData] = useState({
    name: '', phone: '', password: '',
    experienceYears: '',
    experienceMachineTypes: [],
    monthlySalary: '',
  });
  const [opFiles, setOpFiles] = useState({
    photo: null, license: null, aadhaarFront: null, aadhaarBack: null, police: null,
  });
  const [opPaths, setOpPaths] = useState({
    photo: '', license: '', aadhaarFront: '', aadhaarBack: '',
  });
  const [opError, setOpError] = useState('');
  const [opSuccess, setOpSuccess] = useState('');
  const [opSubmitting, setOpSubmitting] = useState(false);
  const [operatorList, setOperatorList] = useState([]);
  const [opListLoading, setOpListLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [assigningOperatorId, setAssigningOperatorId] = useState(null);
  const [assignError, setAssignError] = useState('');
  const [advancePanelOperatorId, setAdvancePanelOperatorId] = useState(null);
  const [advanceForm, setAdvanceForm] = useState({ amount: '', note: '' });
  const [advanceSubmitting, setAdvanceSubmitting] = useState(false);
  const [advanceError, setAdvanceError] = useState('');
  const [advanceLedger, setAdvanceLedger] = useState({});
  const [advanceLedgerLoading, setAdvanceLedgerLoading] = useState({});
  const [payrollPanelOperatorId, setPayrollPanelOperatorId] = useState(null);
  const [payrollMonthByOperator, setPayrollMonthByOperator] = useState({});
  const [payrollPreviewByKey, setPayrollPreviewByKey] = useState({});
  const [payrollPreviewLoading, setPayrollPreviewLoading] = useState({});
  const [payrollPreviewError, setPayrollPreviewError] = useState({});
  const [payrollRunning, setPayrollRunning] = useState({});
  const [payrollRunError, setPayrollRunError] = useState({});
  const [payrollRunSuccess, setPayrollRunSuccess] = useState({});
  const [payrollRunCompleted, setPayrollRunCompleted] = useState({});
  const [payrollHistoryByOperator, setPayrollHistoryByOperator] = useState({});
  const [payrollHistoryLoading, setPayrollHistoryLoading] = useState({});
  const [payrollHistoryError, setPayrollHistoryError] = useState({});
  const [payrollExportMonth, setPayrollExportMonth] = useState(getCurrentPayrollMonthStr);
  const [payrollExportLoading, setPayrollExportLoading] = useState(false);
  const [payrollExportError, setPayrollExportError] = useState('');
  const [attendancePanelOperatorId, setAttendancePanelOperatorId] = useState(null);
  const [attendanceMonthByOperator, setAttendanceMonthByOperator] = useState({});
  const [attendanceByKey, setAttendanceByKey] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState({});
  const [attendanceError, setAttendanceError] = useState({});
  const [attendanceEditDraft, setAttendanceEditDraft] = useState({});
  const [attendanceSavingKey, setAttendanceSavingKey] = useState('');
  const [attendanceSaveSuccess, setAttendanceSaveSuccess] = useState({});
  const [attendanceProposeDraft, setAttendanceProposeDraft] = useState({});
  const [attendanceProposeSavingKey, setAttendanceProposeSavingKey] = useState('');
  const [attendanceProposeError, setAttendanceProposeError] = useState({});
  const [salaryEditOperatorId, setSalaryEditOperatorId] = useState(null);
  const [salaryEditAmount, setSalaryEditAmount] = useState('');
  const [salaryEditSubmitting, setSalaryEditSubmitting] = useState(false);
  const [salaryEditError, setSalaryEditError] = useState('');
  const [salaryUpdateSuccess, setSalaryUpdateSuccess] = useState({});
  const [plQuotaByOperator, setPlQuotaByOperator] = useState({});
  const [plQuotaLoading, setPlQuotaLoading] = useState({});
  const [plQuotaError, setPlQuotaError] = useState({});
  const [plQuotaEditOperatorId, setPlQuotaEditOperatorId] = useState(null);
  const [plQuotaEditAmount, setPlQuotaEditAmount] = useState('');
  const [plQuotaEditSubmitting, setPlQuotaEditSubmitting] = useState(false);
  const [plQuotaEditError, setPlQuotaEditError] = useState('');
  const [plQuotaUpdateSuccess, setPlQuotaUpdateSuccess] = useState({});
  const [regDocuments, setRegDocuments] = useState(() => emptyRegDocumentsForMobility(''));
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [ownerBookings, setOwnerBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState('all');
  const filterBarRef = useRef(null);
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth());
  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear(),
  );
  const [ownerBookingsLoading, setOwnerBookingsLoading] = useState(true);
  const [ownerBookingsError, setOwnerBookingsError] = useState('');
  const [ownerBookingsOffset, setOwnerBookingsOffset] = useState(0);
  const [ownerBookingsHasMore, setOwnerBookingsHasMore] = useState(false);
  const [ownerBookingsLoadingMore, setOwnerBookingsLoadingMore] = useState(false);
  const [terminationModal, setTerminationModal] = useState(null);
  const [terminationType, setTerminationType] = useState('owner');
  const [forceMajeureReason, setForceMajeureReason] = useState('');
  const [terminationSubmitting, setTerminationSubmitting] = useState(false);
  const [terminationError, setTerminationError] = useState('');
  const [pendingTerminations, setPendingTerminations] = useState({});
  const [pendingQuotations, setPendingQuotations] = useState([]);
  const [pendingQuotationsLoading, setPendingQuotationsLoading] = useState(false);
  const [pendingQuotationsError, setPendingQuotationsError] = useState('');
  const [awaitingMobQuotations, setAwaitingMobQuotations] = useState([]);
  const [confirmedMobQuotations, setConfirmedMobQuotations] = useState([]);
  const [mobilizationReleasedByBookingId, setMobilizationReleasedByBookingId] = useState({});
  const [convertedBookingIds, setConvertedBookingIds] = useState({});
  const [mobilizationActionId, setMobilizationActionId] = useState(null);
  const [convertActionId, setConvertActionId] = useState(null);
  const [loadingMilestoneModal, setLoadingMilestoneModal] = useState(null);
  const [loadingPhotoFile, setLoadingPhotoFile] = useState(null);
  const [loadingReceiptFile, setLoadingReceiptFile] = useState(null);
  const [loadingMilestoneBusyId, setLoadingMilestoneBusyId] = useState(null);
  const [loadingMilestoneError, setLoadingMilestoneError] = useState('');
  const [quotationActionId, setQuotationActionId] = useState(null);
  const [termsPrompt, setTermsPrompt] = useState({
    visible: false,
    currentVersion: '',
    userVersion: '',
    acknowledging: false,
    error: '',
  });
  const [approvingId, setApprovingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [settlementFlash, setSettlementFlash] = useState(null);
  const [withdrawAmt, setWithdrawAmt] = useState('');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletTxns, setWalletTxns] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [ledgerYear, setLedgerYear] = useState(() => new Date().getFullYear());
  const [ledgerMonth, setLedgerMonth] = useState(() => new Date().getMonth() + 1);
  const [ledgerOpening, setLedgerOpening] = useState(null);
  const [ledgerClosing, setLedgerClosing] = useState(null);
  const [ledgerCredits, setLedgerCredits] = useState(0);
  const [ledgerDebits, setLedgerDebits] = useState(0);
  const [ledgerPdfBusy, setLedgerPdfBusy] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  const [settlementsError, setSettlementsError] = useState(null);
  const [settlementsReload, setSettlementsReload] = useState(0);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsMonths, setAnalyticsMonths] = useState(6);
  const [receiptGeneratingId, setReceiptGeneratingId] = useState(null);
  const settlementsFetchKeyRef = useRef(-1);
  const [ownerBankProfile, setOwnerBankProfile] = useState(null);
  const [ownerBankProfileLoading, setOwnerBankProfileLoading] = useState(false);
  const [ownerBankProfileError, setOwnerBankProfileError] = useState('');
  const [ownerBankProfileEditing, setOwnerBankProfileEditing] = useState(false);
  const [ownerBankProfileSaving, setOwnerBankProfileSaving] = useState(false);
  const [ownerBankProfileSuccess, setOwnerBankProfileSuccess] = useState('');
  const [ownerBankProfileForm, setOwnerBankProfileForm] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc: '',
    pan_number: '',
    gstin: '',
  });
  const ownerBankProfileFetchRef = useRef(false);
  const isBankIncomplete = ownerBankProfile && (
    !ownerBankProfile.bank_name
    || !ownerBankProfile.account_number
    || !ownerBankProfile.ifsc
  );
  const [regData, setRegData] = useState({
    ownerName: ownerDisplayName,
    ownerPhone,
    ownerEmail,
    mobilityClass: '',
    machineName: '', machineType: '', regNo: '', year: '', capacity: '',
    pincode: '',
    minimum_hours: 3,
    payment_due_days: 15,
    early_payment_days: 3,
    early_payment_discount_pct: 0,
    late_payment_fee_pct: 0,
    pdiChecked: false, insuranceValid: false, pollutionValid: false,
    agreed: false,
  });

  const [machineData, setMachineData] = useState([]);
  const [editingMachineId, setEditingMachineId] = useState(null);
  const [editRegSelectedId, setEditRegSelectedId] = useState(null);
  const [editRegActiveTab, setEditRegActiveTab] = useState({});
  const [editMachineForm, setEditMachineForm] = useState({});
  const [complianceData, setComplianceData] = useState({});
  const [recoveryData, setRecoveryData] = useState({});
  const [recoveryLoading, setRecoveryLoading] = useState({});
  const [editingFinancialMachineId, setEditingFinancialMachineId] = useState(null);
  const [financialForm, setFinancialForm] = useState({});
  const [financialSaving, setFinancialSaving] = useState(false);
  const [financialLoanOpen, setFinancialLoanOpen] = useState(false);
  const [dieselLatest, setDieselLatest] = useState({});
  const [dieselLoading, setDieselLoading] = useState({});
  const [dieselFormOpen, setDieselFormOpen] = useState(null);
  const [dieselForm, setDieselForm] = useState({});
  const [dieselSaving, setDieselSaving] = useState(false);
  const [dieselRateSuggestion, setDieselRateSuggestion] = useState({});
  const [dieselRateSuggestionLoading, setDieselRateSuggestionLoading] = useState({});
  const [dieselRateApplying, setDieselRateApplying] = useState({});
  const [dieselHistoryOpen, setDieselHistoryOpen] = useState({});
  const [dieselPriceHistory, setDieselPriceHistory] = useState({});
  const [dieselHistoryLoading, setDieselHistoryLoading] = useState({});
  const [dieselReceiptUploading, setDieselReceiptUploading] = useState(false);
  const [dieselExtractMessage, setDieselExtractMessage] = useState(null);
  const dieselReceiptInputRef = useRef(null);
  const [marketPosition, setMarketPosition] = useState({});
  const [marketPositionLoading, setMarketPositionLoading] = useState({});
  const [marketFormOpen, setMarketFormOpen] = useState(null);
  const [marketForm, setMarketForm] = useState({});
  const [marketSaving, setMarketSaving] = useState(false);
  const [marketRatePendingReview, setMarketRatePendingReview] = useState({});
  const [complianceOpenMachineId, setComplianceOpenMachineId] = useState(null);
  const [editingComplianceId, setEditingComplianceId] = useState(null);
  const [complianceForm, setComplianceForm] = useState({});
  const [complianceSaving, setComplianceSaving] = useState(false);
  const [complianceExtracting, setComplianceExtracting] = useState({});
  const [complianceExtractMessage, setComplianceExtractMessage] = useState({});
  const [serviceLogs, setServiceLogs] = useState({});
  const [serviceFormOpen, setServiceFormOpen] = useState(null);
  const [serviceFormData, setServiceFormData] = useState({ ...EMPTY_SERVICE_FORM });
  const [serviceLoading, setServiceLoading] = useState({});
  const [serviceSaving, setServiceSaving] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState({});
  const [availabilityBlocks, setAvailabilityBlocks] = useState({});
  const [availabilityLoading, setAvailabilityLoading] = useState({});
  const [availabilityError, setAvailabilityError] = useState({});
  const [availabilityForm, setAvailabilityForm] = useState({});
  const [availabilitySaving, setAvailabilitySaving] = useState({});
  const [availabilityDeleting, setAvailabilityDeleting] = useState({});
  const [availabilityToggling, setAvailabilityToggling] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [ownerKycStatus, setOwnerKycStatus] = useState(null);
  const [alertCounts, setAlertCounts] = useState({
    bookingsPending: 0,
    settlementsPending: 0,
    lowFuelMachines: 0,
    openIssues: 0,
    complianceAlerts: 0,
    serviceAlerts: 0,
  });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);
  const [blacklistedClients, setBlacklistedClients] = useState([]);
  const [blacklistedClientsLoading, setBlacklistedClientsLoading] = useState(false);
  const [blacklistedClientsError, setBlacklistedClientsError] = useState('');

  const ownerHardwareBackFromPopRef = useRef(false);
  const ownerActiveTabRef = useRef(activeTab);
  const ownerHardwareBackModalsRef = useRef({
    settlementFlash: null,
  });

  ownerActiveTabRef.current = activeTab;
  ownerHardwareBackModalsRef.current = {
    settlementFlash,
    sidebarOpen,
    sidebarOverlay,
  };

  useEffect(() => {
    const loadData = async () => {
      const user = readSessionUser();
      if (user?.role !== 'owner') {
        navigate(`/${user?.role || 'login'}`, { replace: true });
        return;
      }
      const machines = await getMachines();
      const ownerId = sessionUser?.id || sessionUser?.user_id;
      const ownerMachines = Array.isArray(machines)
        ? machines.filter((m) => !ownerId || m.owner_id === ownerId)
        : [];
      setMachineData(ownerMachines);
      const regs = await getOwnerMachineRegistrations();
      setPendingRegistrations(regs.items || []);
    };
    loadData();
    const seenKey = `owner_settlements_seen_${sessionUser.id || sessionUser.user_id || 'anon'}`;
    const seenAt = localStorage.getItem(seenKey) || '';
    getOwnerSettlements(seenAt).then((items) => {
      const latest = items[0];
      if (latest && latest.created_at && latest.created_at > seenAt) {
        setSettlementFlash({
          gross: Number(latest.gross_amount || 0),
          net: Number(latest.net_owner_amount || 0),
          bookingRef: latest.booking_ref,
          pending: latest.status === 'pending_transfer',
        });
        localStorage.setItem(seenKey, latest.created_at);
      }
    }).catch(() => {});
    setOwnerBookingsLoading(true);
    getOwnerBookingsPage({ limit: 100, offset: 0 }).then((result) => {
      setOwnerBookings(Array.isArray(result.items) ? result.items : []);
      setOwnerBookingsHasMore(Boolean(result.hasMore));
      setOwnerBookingsOffset(result.nextOffset ?? (result.items?.length || 0));
      setOwnerBookingsError(result.error || '');
      setOwnerBookingsLoading(false);
    });
  }, [navigate]);

  useEffect(() => {
    const y = new Date().getFullYear();
    const list = [y + 1, y, y - 1, y - 2];
    if (!list.includes(yearFilter)) {
      setYearFilter(y);
    }
  }, [yearFilter]);

  useEffect(() => {
    const loadOwnerKycStatus = async () => {
      try {
        const data = await secureFetch('/api/owner/kyc');
        setOwnerKycStatus(data.kyc?.status || 'not_submitted');
      } catch (err) {
        if ((err?.message || '') === 'auth_required') return;
        // soft gate — ignore load errors
      }
    };
    loadOwnerKycStatus();
  }, []);

  useEffect(() => {
    const poll = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      (async () => {
        try {
          const data = await secureFetch('/api/owner/kyc');
          setOwnerKycStatus(data.kyc?.status || 'not_submitted');
        } catch (err) {
          if ((err?.message || '') === 'auth_required') return;
        }
      })();
    }, 30000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (ownerHardwareBackFromPopRef.current) {
      ownerHardwareBackFromPopRef.current = false;
      return;
    }
    window.history.pushState(
      { tab: activeTab },
      '',
      `?tab=${activeTab}`,
    );
  }, [activeTab]);

  useEffect(() => {
    const restoreCurrentTabInHistory = () => {
      const tab = ownerActiveTabRef.current;
      window.history.pushState({ tab }, '', `?tab=${tab}`);
    };

    const onPopState = (event) => {
      const modals = ownerHardwareBackModalsRef.current;
      if (modals.sidebarOpen && modals.sidebarOverlay) {
        setSidebarOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.settlementFlash) {
        setSettlementFlash(null);
        restoreCurrentTabInHistory();
        return;
      }
      if (event.state && event.state.tab) {
        ownerHardwareBackFromPopRef.current = true;
        setActiveTab(event.state.tab);
      } else {
        ownerHardwareBackFromPopRef.current = true;
        setActiveTab(OWNER_TAB_ORDER[0]);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  async function loadAnalytics() {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const data = await secureFetch(
        `/api/owner/analytics/fleet?months=${analyticsMonths}`,
      );
      setAnalyticsData(data);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setAnalyticsData(null);
      setAnalyticsError(t('failedToLoad'));
    } finally {
      setAnalyticsLoading(false);
    }
  }

  async function loadAlerts() {
    setAlertsLoading(true);
    setAlertsError(null);
    try {
      const data = await secureFetch('/api/owner/alerts');
      setAlerts(data.alerts || []);
      setAlertCounts(data.counts || {
        bookingsPending: 0,
        settlementsPending: 0,
        lowFuelMachines: 0,
        openIssues: 0,
        complianceAlerts: 0,
        serviceAlerts: 0,
      });
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setAlertsError(t('failedToLoadAlerts'));
    } finally {
      setAlertsLoading(false);
    }
  }

  async function loadBlacklistedClients() {
    setBlacklistedClientsLoading(true);
    setBlacklistedClientsError('');
    try {
      const clients = await getOwnerBlacklistedClients();
      setBlacklistedClients(clients || []);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setBlacklistedClientsError(err?.message || t('failedToLoad'));
      setBlacklistedClients([]);
    } finally {
      setBlacklistedClientsLoading(false);
    }
  }

  useEffect(() => {
    loadAlerts();
    loadBlacklistedClients();
  }, []);

  useEffect(() => {
    if (activeTab === 'alerts') {
      loadAlerts();
      loadBlacklistedClients();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    loadAnalytics();
  }, [activeTab, analyticsMonths]);

  useEffect(() => {
    if (activeTab !== 'register') {
      setRegError('');
      setRegSuccess('');
      setRegStep(1);
    }
  }, [activeTab]);

  useEffect(() => {
    const ownerId = sessionUser.id || sessionUser.user_id;
    if (activeTab !== 'wallet' || !ownerId) return;
    let cancelled = false;
    const loadWallet = async () => {
      setWalletLoading(true);
      try {
        setWalletError(null);
        const [balance, txResult] = await Promise.all([
          getWalletBalance(ownerId),
          getMyTransactionsPage({
            limit: 2000,
            offset: 0,
            year: ledgerYear,
            month: ledgerMonth,
            includeOpening: true,
          }),
        ]);
        if (!cancelled) {
          setWalletBalance(Number(balance) || 0);
          setWalletTxns(txResult?.items || []);
          setLedgerOpening(txResult?.openingBalance != null ? Number(txResult.openingBalance) : null);
          setLedgerClosing(txResult?.closingBalance != null ? Number(txResult.closingBalance) : null);
          setLedgerCredits(Number(txResult?.creditsTotal) || 0);
          setLedgerDebits(Number(txResult?.debitsTotal) || 0);
        }
      } catch {
        if (!cancelled) {
          setWalletError('Could not load wallet balance. Please try again.');
          setWalletTxns([]);
          setLedgerOpening(null);
          setLedgerClosing(null);
          setLedgerCredits(0);
          setLedgerDebits(0);
        }
      } finally {
        if (!cancelled) setWalletLoading(false);
      }
    };
    loadWallet();
    return () => { cancelled = true; };
  }, [activeTab, sessionUser.id, sessionUser.user_id, ledgerYear, ledgerMonth]);

  useEffect(() => {
    if (activeTab !== 'reports' && activeTab !== 'dashboard') return;
    if (settlementsFetchKeyRef.current === settlementsReload && settlements.length > 0) return;
    settlementsFetchKeyRef.current = settlementsReload;
    let cancelled = false;
    const loadSettlements = async () => {
      setSettlementsLoading(true);
      setSettlementsError(null);
      try {
        const items = await getOwnerSettlements(undefined, { throwOnError: true });
        if (!cancelled) {
          setSettlements(Array.isArray(items) ? items : []);
        }
      } catch (err) {
        if (!cancelled) {
          setSettlements([]);
          setSettlementsError(err?.message || t('ownerSettlementsLoadError'));
        }
      } finally {
        if (!cancelled) setSettlementsLoading(false);
      }
    };
    loadSettlements();
    return () => { cancelled = true; };
  }, [activeTab, t, settlementsReload]);

  useEffect(() => {
    if (activeTab !== 'settings' && activeTab !== 'reports' && activeTab !== 'dashboard') return;
    if (ownerBankProfileFetchRef.current) return;
    let cancelled = false;
    const loadProfile = async () => {
      setOwnerBankProfileLoading(true);
      setOwnerBankProfileError('');
      const result = await getOwnerProfile();
      if (!cancelled) {
        if (result.error) {
          setOwnerBankProfileError(result.error);
          setOwnerBankProfile(null);
        } else {
          setOwnerBankProfile(result.profile);
          applyOwnerProfileTier(result.profile);
        }
        ownerBankProfileFetchRef.current = true;
        setOwnerBankProfileLoading(false);
      }
    };
    loadProfile();
    return () => { cancelled = true; };
  }, [activeTab, applyOwnerProfileTier]);

  const machineLookup = useMemo(() => buildMachineLookup(machineData), [machineData]);
  const groupedOwnerBookings = useMemo(() => groupOwnerBookingsSchedule(ownerBookings), [ownerBookings]);

  const reportSettlementStats = useMemo(() => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    let totalEarned = 0;
    let thisMonth = 0;
    let pending = 0;
    for (const row of settlements) {
      const net = Number(row.net_owner_amount || 0);
      const status = String(row.status || '').toLowerCase();
      if (status === 'settled') {
        totalEarned += net;
        const created = row.created_at ? new Date(row.created_at) : null;
        if (created && !Number.isNaN(created.getTime())
          && created.getMonth() === month && created.getFullYear() === year) {
          thisMonth += net;
        }
      }
      if (status === 'pending_transfer') pending += net;
    }
    return { totalEarned, thisMonth, pending, totalBookings: settlements.length };
  }, [settlements]);

  const dashboardSettlementStats = useMemo(() => {
    let gross = 0;
    let commission = 0;
    let tdsTcs = 0;
    let net = 0;
    for (const row of settlements) {
      gross += Number(row.gross_amount || 0);
      commission += Number(row.commission_amount || 0);
      tdsTcs += Number(row.tds_amount || 0) + Number(row.gst_tcs_amount || 0);
      net += Number(row.net_owner_amount || 0);
    }
    return { gross, commission, tdsTcs, net };
  }, [settlements]);

  const recentSettlements = useMemo(
    () => (settlements || []).slice(0, 3),
    [settlements],
  );

  const sortedFleetMachines = useMemo(() => {
    const rows = analyticsData?.machines;
    if (!Array.isArray(rows)) return [];
    return [...rows].sort((a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0));
  }, [analyticsData]);

  const analyticsTrendSeries = useMemo(
    () => (Array.isArray(analyticsData?.monthly_trend) ? analyticsData.monthly_trend : []),
    [analyticsData],
  );

  const analyticsMaxRevenue = useMemo(
    () => Math.max(...analyticsTrendSeries.map((s) => Number(s.revenue || 0)), 1),
    [analyticsTrendSeries],
  );

  const displayFleetMachines = useMemo(
    () => sortedFleetMachines.filter(
      (row) => Number(row.total_bookings || 0) > 0 || Number(row.total_revenue || 0) > 0,
    ),
    [sortedFleetMachines],
  );

  const settlementStatusLabel = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'settled') return t('ownerSettlementSettled');
    if (s === 'pending_transfer') return t('ownerSettlementPending');
    if (s === 'failed') return t('ownerSettlementFailed');
    return status || '—';
  };

  const settlementStatusColor = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'settled') return '#4CAF50';
    if (s === 'pending_transfer') return '#FF9800';
    if (s === 'failed') return '#e94560';
    return '#8896a8';
  };

  const fmtInr = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;
  const cleanDesc = (raw = '', reference = '') => {
    const r = String(raw || '').replace(/^\[.*?\]\[.*?\]\s*/g, '').trim();
    const ref = String(reference || '').toLowerCase();
    const low = r.toLowerCase();
    if (low.includes('contract_termination') || ref.includes('contract_termination')) {
      return 'Contract Termination Fee';
    }
    if (low.includes('mob_loading_release') || ref.includes('mob_loading_release')) {
      return 'Mobilization Release';
    }
    if (low.includes('ra_bill_release') || ref.includes('ra_bill_release')) {
      return 'RA-Bill Release';
    }
    if (low.includes('settlement_credit') || ref.includes('settlement_credit')) {
      const bk = `${raw} ${reference}`.match(/BK\d+/);
      return bk ? `Booking Settlement · ${bk[0]}` : 'Booking Settlement';
    }
    if (ref.includes('admin_correction') || low.includes('admin_correction')) {
      return 'Admin Correction';
    }
    if (low.includes('manual_topup') || low.includes('wallet_recharge')
      || ref.includes('manual_topup') || ref.includes('wallet_recharge')) {
      return 'Wallet Top-up';
    }
    if (low.includes('reset') || ref.includes('reset')) {
      return 'Wallet Reset';
    }
    return r.length > 60 ? `${r.slice(0, 57)}...` : r;
  };

  const fmtKpiAmount = (amount) => {
    const val = Number(amount || 0);
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
    return fmtInr(val);
  };

  const startOwnerBankProfileEdit = () => {
    const p = ownerBankProfile || {};
    setOwnerBankProfileForm({
      account_holder_name: p.account_holder_name || ownerDisplayName || '',
      bank_name: p.bank_name || '',
      account_number: p.account_number || '',
      ifsc: p.ifsc || '',
      pan_number: p.pan_number || '',
      gstin: p.gstin || '',
    });
    setOwnerBankProfileEditing(true);
    setOwnerBankProfileSuccess('');
  };

  const handleOwnerBankProfileSave = async () => {
    setOwnerBankProfileSaving(true);
    setOwnerBankProfileError('');
    setOwnerBankProfileSuccess('');
    const result = await updateOwnerProfile(ownerBankProfileForm);
    setOwnerBankProfileSaving(false);
    if (!result.ok) {
      setOwnerBankProfileError(result.message || t('actionFailed'));
      return;
    }
    setOwnerBankProfile(result.profile);
    applyOwnerProfileTier(result.profile);
    setOwnerBankProfileEditing(false);
    setOwnerBankProfileSuccess(result.message || t('ownerProfileSaved'));
    alert(t('ownerProfileSaved'));
  };

  const handleSettlementReceipt = async (row) => {
    if (!row?.id && !row?.booking_ref) return;
    const rowId = row.id || row.booking_ref;
    setReceiptGeneratingId(rowId);
    try {
      let apiProfile = ownerBankProfile;
      if (!apiProfile) {
        const result = await getOwnerProfile();
        apiProfile = result.profile;
        if (result.profile) setOwnerBankProfile(result.profile);
      }
      const mapped = mapOwnerProfileForReceipt(apiProfile || {}, sessionUser);
      if (isOwnerProfileIncomplete(mapped)) {
        alert(t('ownerProfileIncomplete'));
        return;
      }
      await generateOwnerReceipt(buildOwnerReceiptPayload(row, sessionUser, apiProfile || {}));
    } catch (err) {
      alert(err?.message || t('actionFailed'));
    } finally {
      setReceiptGeneratingId(null);
    }
  };

  const handleCompleteBooking = async (booking, setBookings) => {
    if (!booking?.id || completingId) return;
    setCompletingId(booking.id);
    const result = await completeBooking(booking.id);
    setCompletingId(null);
    if (!result.ok) {
      alert(result.message || t('actionFailed'));
      return;
    }
    const st = result.settlement || {};
    setBookings((prev) => prev.map((x) => (x.id === booking.id
      ? { ...x, status: 'Completed', completed_at: new Date().toISOString() }
      : x)));
    setSettlementFlash({
      gross: Number(st.gross_amount || booking.total_amount || booking.base_amount || 0),
      net: Number(st.net_owner_amount || 0),
      bookingRef: st.booking_ref || booking.booking_ref,
      pending: result.settlementPending || st.status === 'pending_transfer',
    });
    if (activeTab === 'wallet') {
      const ownerId = sessionUser.id || sessionUser.user_id;
      if (ownerId) {
        const bal = await getWalletBalance(ownerId);
        setWalletBalance(Number(bal) || 0);
      }
    }
  };

  const handleWithdrawRequest = async () => {
    const amount = Number(withdrawAmt);
    if (!Number.isFinite(amount) || amount < 1000) {
      alert(t('ownerWithdrawMin'));
      return;
    }
    setWithdrawSubmitting(true);
    try {
      await submitWithdrawRequest({ amount });
      alert(t('ownerWithdrawSubmitted'));
      setWithdrawAmt('');
      const ownerId = sessionUser.id || sessionUser.user_id;
      if (ownerId) {
        const bal = await getWalletBalance(ownerId);
        setWalletBalance(Number(bal) || 0);
        const txResult = await getMyTransactionsPage({
          limit: 2000,
          offset: 0,
          year: ledgerYear,
          month: ledgerMonth,
          includeOpening: true,
        });
        setWalletTxns(txResult?.items || []);
        setLedgerOpening(txResult?.openingBalance != null ? Number(txResult.openingBalance) : null);
        setLedgerClosing(txResult?.closingBalance != null ? Number(txResult.closingBalance) : null);
        setLedgerCredits(Number(txResult?.creditsTotal) || 0);
        setLedgerDebits(Number(txResult?.debitsTotal) || 0);
      }
    } catch (e) {
      alert(e?.message || t('actionFailed'));
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const handleApproveBooking = async (booking, setBookings) => {
    if (!booking?.id || approvingId) return;
    setApprovingId(booking.id);
    setApproveErrorByBookingId((prev) => {
      const next = { ...prev };
      delete next[booking.id];
      return next;
    });
    const result = await approveBooking(booking.id);
    setApprovingId(null);
    if (!result?.ok) {
      const msg = result?.message || t('actionFailed');
      const isVerifiedGate = /verified/i.test(msg)
        || String(booking.booking_type || '').toLowerCase() === 'contract';
      if (isVerifiedGate) {
        setApproveErrorByBookingId((prev) => ({
          ...prev,
          [booking.id]: /verified/i.test(msg) ? msg : CONTRACT_VERIFIED_REQUIRED_MSG,
        }));
      } else {
        alert(msg);
      }
      return;
    }
    setBookings((prev) => prev.map((x) => (x.id === booking.id
      ? { ...x, owner_approved: true, owner_approved_at: new Date().toISOString() }
      : x)));
    alert(`${t('approved')}! ${t('dispatched')}.`);
  };

  const loadPendingQuotations = useCallback(async () => {
    setPendingQuotationsLoading(true);
    setPendingQuotationsError('');
    try {
      const [pendingData, awaitingData, confirmedData] = await Promise.all([
        listOwnerPendingQuotations(),
        listOwnerAwaitingMobilizationQuotations(),
        listOwnerMobilizationConfirmedQuotations(),
      ]);
      setPendingQuotations(Array.isArray(pendingData?.items) ? pendingData.items : []);
      setAwaitingMobQuotations(Array.isArray(awaitingData?.items) ? awaitingData.items : []);
      setConfirmedMobQuotations(Array.isArray(confirmedData?.items) ? confirmedData.items : []);
    } catch (err) {
      setPendingQuotations([]);
      setAwaitingMobQuotations([]);
      setConfirmedMobQuotations([]);
      setPendingQuotationsError(err?.message || 'Failed to load pending quotations');
    } finally {
      setPendingQuotationsLoading(false);
    }
  }, []);

  const handleConfirmMobilization = async (quotation, booking) => {
    if (!quotation?.id || mobilizationActionId) return;
    setMobilizationActionId(quotation.id);
    try {
      const result = await confirmOwnerMobilization(quotation.id);
      if (!result?.ok && !result?.success) {
        alert(result?.message || t('actionFailed'));
        return;
      }
      const releaseAmount = Number(
        result.released != null
          ? result.released
          : (result.releaseAmount != null
            ? result.releaseAmount
            : Number(quotation.advanceRequired || 0) * 0.5),
      );
      const bookingId = booking?.id || quotation.bookingId;
      if (bookingId) {
        setMobilizationReleasedByBookingId((prev) => ({
          ...prev,
          [bookingId]: releaseAmount,
        }));
      }
      setAwaitingMobQuotations((prev) => prev.filter((q) => q.id !== quotation.id));

      const autoConverted = result.quotation?.status === 'converted'
        || result.converted
        || result.alreadyConverted;
      if (autoConverted && bookingId) {
        setConvertedBookingIds((prev) => ({ ...prev, [bookingId]: true }));
        setConfirmedMobQuotations((prev) => prev.filter((q) => q.id !== quotation.id));
      } else if (result.quotation?.status === 'mobilization_confirmed') {
        setConfirmedMobQuotations((prev) => {
          const without = prev.filter((q) => q.id !== quotation.id);
          return [...without, result.quotation];
        });
      }

      const ownerId = sessionUser.id || sessionUser.user_id;
      if (ownerId) {
        const bal = await getWalletBalance(ownerId);
        setWalletBalance(Number(bal) || 0);
      }
      await loadPendingQuotations();
      alert('✅ 50% advance released — Machine dispatched');
    } catch (err) {
      alert(err?.message || t('actionFailed'));
    } finally {
      setMobilizationActionId(null);
    }
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

  const handleSubmitLoadingMilestone = async () => {
    const booking = loadingMilestoneModal;
    if (!booking?.id || loadingMilestoneBusyId) return;
    if (!loadingPhotoFile) {
      setLoadingMilestoneError('Loading photo is required.');
      return;
    }
    setLoadingMilestoneBusyId(booking.id);
    setLoadingMilestoneError('');
    try {
      const photoUrl = await readFileAsDataUrl(loadingPhotoFile);
      const receiptUrl = loadingReceiptFile
        ? await readFileAsDataUrl(loadingReceiptFile)
        : null;
      const result = await secureFetch(
        `/api/owner/bookings/${encodeURIComponent(booking.id)}/mobilization/submit-loading`,
        {
          method: 'POST',
          body: JSON.stringify({ photoUrl, receiptUrl }),
        },
      );
      if (!result?.success) {
        setLoadingMilestoneError(result?.message || 'Failed to submit loading milestone');
        return;
      }
      const submittedAt = result.submittedAt || new Date().toISOString();
      setOwnerBookings((prev) => prev.map((b) => (
        b.id === booking.id
          ? {
            ...b,
            mobilization_loading_photo_url: photoUrl,
            mobilization_loading_receipt_url: receiptUrl,
            mobilization_loading_submitted_at: submittedAt,
          }
          : b
      )));
      setLoadingMilestoneModal(null);
      setLoadingPhotoFile(null);
      setLoadingReceiptFile(null);
      alert('Loading submitted — awaiting admin approval');
    } catch (err) {
      setLoadingMilestoneError(err?.message || 'Failed to submit loading milestone');
    } finally {
      setLoadingMilestoneBusyId(null);
    }
  };

  const handleConvertQuotation = async (quotation, booking) => {
    if (!quotation?.id || convertActionId) return;
    setConvertActionId(quotation.id);
    try {
      const result = await convertOwnerQuotation(quotation.id);
      if (!result?.success) {
        alert(result?.message || t('actionFailed'));
        return;
      }
      const bookingId = booking?.id || quotation.bookingId;
      if (bookingId) {
        setConvertedBookingIds((prev) => ({ ...prev, [bookingId]: true }));
      }
      setConfirmedMobQuotations((prev) => prev.filter((q) => q.id !== quotation.id));
      alert('Contract active ✅');
    } catch (err) {
      alert(err?.message || t('actionFailed'));
    } finally {
      setConvertActionId(null);
    }
  };

  const handleAcceptQuotation = async (quotation) => {
    if (!quotation?.id || quotationActionId) return;
    setQuotationActionId(quotation.id);
    try {
      const result = await acceptOwnerQuotation(quotation.id);
      if (!result?.success) {
        alert(result?.message || t('actionFailed'));
        return;
      }
      setPendingQuotations((prev) => prev.filter((q) => q.id !== quotation.id));
      loadAlerts();
      alert('Quotation accepted. It is now in admin review.');
    } catch (err) {
      alert(err?.message || t('actionFailed'));
    } finally {
      setQuotationActionId(null);
    }
  };

  // reasonOverride bypasses the native prompt/confirm when the reject modal
  // already collected a reason. Called with one arg, behaviour is unchanged.
  const handleRejectQuotation = async (quotation, reasonOverride = null) => {
    if (!quotation?.id || quotationActionId) return;
    let reason = reasonOverride;
    if (reason == null) {
      reason = window.prompt('Optional reject reason (shown for audit):', '') || '';
      if (!window.confirm('Reject this quotation? The client will be told the machine is not available.')) {
        return;
      }
    }
    setQuotationActionId(quotation.id);
    try {
      const result = await rejectOwnerQuotation(quotation.id, { rejectReason: reason || null });
      if (!result?.success) {
        alert(result?.message || t('actionFailed'));
        return;
      }
      setPendingQuotations((prev) => prev.filter((q) => q.id !== quotation.id));
      loadAlerts();
      alert('Quotation rejected.');
    } catch (err) {
      alert(err?.message || t('actionFailed'));
    } finally {
      setQuotationActionId(null);
    }
  };

  const [quotationClockTs, setQuotationClockTs] = useState(() => Date.now());
  const [rejectQuotationModal, setRejectQuotationModal] = useState(null);
  const [rejectQuotationReason, setRejectQuotationReason] = useState('');
  const [standbyOpenByQuotationId, setStandbyOpenByQuotationId] = useState({});

  // Ticks only while a quotation is awaiting response — no idle interval.
  useEffect(() => {
    if (!pendingQuotations.length) return undefined;
    const timer = setInterval(() => setQuotationClockTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [pendingQuotations.length]);

  const isStandbyOpen = (quotationId) => standbyOpenByQuotationId[quotationId] !== false;

  const toggleStandbySection = (quotationId) => {
    setStandbyOpenByQuotationId((prev) => ({
      ...prev,
      [quotationId]: prev[quotationId] === false,
    }));
  };

  // Quotation alerts land on the review panel itself, not just the tab.
  const openOwnerAlertTarget = (alertItem) => {
    if (!alertItem?.link) return;
    setActiveTab(alertItem.link);
    if (alertItem.type !== 'quotation') return;
    setTimeout(() => {
      try {
        document.getElementById('owner-quotation-review')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_err) {
        // ignore — navigation already happened
      }
    }, 150);
  };

  const confirmRejectQuotation = async () => {
    const quotation = rejectQuotationModal;
    if (!quotation?.id) return;
    const reason = String(rejectQuotationReason || '').trim();
    setRejectQuotationModal(null);
    setRejectQuotationReason('');
    await handleRejectQuotation(quotation, reason);
  };

  useEffect(() => {
    loadPendingQuotations();
  }, [loadPendingQuotations]);

  useEffect(() => {
    if (activeTab === 'bookings') {
      loadPendingQuotations();
    }
  }, [activeTab, loadPendingQuotations]);

  const handleRegNext = () => {
    if (regStep === 2) {
      if (regStep2Ref.current?.currentSection !== 'purchase') {
        regStep2Ref.current?.goSectionNext();
        return;
      }
    }
    const err = validateRegistrationStep(regStep, regData, regDocuments, t);
    if (err) {
      setRegError(err);
      return;
    }
    if (regStep === 2) {
      if (!regData.pincode || !/^\d{6}$/.test(String(regData.pincode))) {
        setRegError('Valid 6-digit pincode is required');
        return;
      }
      if (!regData.minimum_hours || Number(regData.minimum_hours) < 1) {
        setRegError('Minimum hours is required (minimum 1)');
        return;
      }
    }
    setRegError('');
    setRegStep((s) => s + 1);
  };

  const handleDocumentSelect = (key, file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setRegError(t('regFileTypeError'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setRegError(t('regFileSizeError'));
      return;
    }
    setRegError('');
    setRegDocuments((prev) => ({ ...prev, [key]: file }));
  };

  const resetRegistrationForm = () => {
    setRegStep(1);
    setRegData({
      ownerName: ownerDisplayName,
      ownerPhone,
      ownerEmail,
      mobilityClass: '',
      machineName: '', machineType: '', regNo: '', year: '', capacity: '',
      pincode: '',
      payment_due_days: 15,
      early_payment_days: 3,
      early_payment_discount_pct: 0,
      late_payment_fee_pct: 0,
      pdiChecked: false, insuranceValid: false, pollutionValid: false,
      agreed: false,
    });
    setRegDocuments(emptyRegDocumentsForMobility(''));
  };

  const resetOpForm = () => {
    setOpStep(1);
    setOpData({ name: '', phone: '', password: '', experienceYears: '', experienceMachineTypes: [], monthlySalary: '' });
    setOpFiles({ photo: null, license: null, aadhaarFront: null, aadhaarBack: null, police: null });
    setOpPaths({ photo: '', license: '', aadhaarFront: '', aadhaarBack: '' });
    setOpError('');
    setOpSuccess('');
    setOpSubmitting(false);
  };

  const handleOpFileUpload = (type, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      setOpError('Invalid file type — JPG/PNG/PDF only');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setOpError('File too large — max 5MB');
      return;
    }
    setOpError('');
    setOpFiles((prev) => ({ ...prev, [type]: file }));
  };

  // Star operator calculation
  function getOperatorStar(op) {
    const days = op.created_at
      ? Math.floor(
        (Date.now() - new Date(op.created_at))
        / (1000 * 60 * 60 * 24),
      )
      : 0;
    const exp = Number(op.experience_years || 0);

    if (days >= 180 && exp >= 5) {
      return { stars: '⭐⭐⭐', label: 'Star Operator', color: '#c9a84c' };
    } if (days >= 90 && exp >= 2) {
      return { stars: '⭐⭐', label: 'Experienced', color: '#e2c97e' };
    } if (days >= 30 && exp >= 1) {
      return { stars: '⭐', label: 'Rising', color: '#8896a8' };
    }
    return { stars: '', label: 'New', color: '#8896a8' };
  }

  // Days with us
  function getDaysWithUs(createdAt) {
    if (!createdAt) return 0;
    return Math.floor(
      (Date.now() - new Date(createdAt))
      / (1000 * 60 * 60 * 24),
    );
  }

  const fetchOperators = async () => {
    setOpListLoading(true);
    try {
      const data = await secureFetch('/api/owner/operators');
      if (data.success) setOperatorList(data.operators);
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      console.error(e);
    } finally {
      setOpListLoading(false);
    }
  };

  const fetchAdvanceLedger = async (operatorId) => {
    setAdvanceLedgerLoading((prev) => ({ ...prev, [operatorId]: true }));
    try {
      const data = await secureFetch(`/api/owner/operators/${operatorId}/advances`);
      if (data.success) {
        setAdvanceLedger((prev) => ({ ...prev, [operatorId]: data }));
        setOperatorList((prev) => prev.map((op) => (
          op.id === operatorId
            ? {
              ...op,
              advance_total_given: data.advance_total_given,
              advance_total_deducted: data.advance_total_deducted,
              advance_balance: data.advance_balance,
            }
            : op
        )));
      }
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      console.error(e);
    } finally {
      setAdvanceLedgerLoading((prev) => ({ ...prev, [operatorId]: false }));
    }
  };

  const handleOpenAdvancePanel = async (operatorId) => {
    if (advancePanelOperatorId === operatorId) {
      setAdvancePanelOperatorId(null);
      setAdvanceError('');
      return;
    }
    setAdvancePanelOperatorId(operatorId);
    setAdvanceForm({ amount: '', note: '' });
    setAdvanceError('');
    await fetchAdvanceLedger(operatorId);
  };

  const handleGiveAdvance = async (operatorId) => {
    const parsedAmount = Number(advanceForm.amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setAdvanceError(t('validAdvanceRequired'));
      return;
    }
    setAdvanceSubmitting(true);
    setAdvanceError('');
    try {
      await secureFetch(`/api/owner/operators/${operatorId}/advances`, {
        method: 'POST',
        body: JSON.stringify({
          amount: parsedAmount,
          note: advanceForm.note.trim() || undefined,
        }),
      });
      setAdvanceForm({ amount: '', note: '' });
      await fetchAdvanceLedger(operatorId);
      await fetchOperators();
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setAdvanceError(err?.message || t('failedToRecordAdvance'));
    } finally {
      setAdvanceSubmitting(false);
    }
  };

  const getPayrollMonthForOperator = (operatorId) => (
    payrollMonthByOperator[operatorId] || getCurrentPayrollMonthStr()
  );

  const fetchPayrollPreview = async (operatorId, month) => {
    const key = payrollMonthKey(operatorId, month);
    setPayrollPreviewLoading((prev) => ({ ...prev, [key]: true }));
    setPayrollPreviewError((prev) => ({ ...prev, [key]: '' }));
    try {
      const data = await secureFetch(
        `/api/owner/operators/${operatorId}/payroll/preview?month=${encodeURIComponent(month)}`,
      );
      setPayrollPreviewByKey((prev) => ({ ...prev, [key]: data }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPayrollPreviewError((prev) => ({
        ...prev,
        [key]: err?.message || t('failedToLoad'),
      }));
    } finally {
      setPayrollPreviewLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const fetchPayrollHistory = async (operatorId) => {
    setPayrollHistoryLoading((prev) => ({ ...prev, [operatorId]: true }));
    setPayrollHistoryError((prev) => ({ ...prev, [operatorId]: '' }));
    try {
      const data = await secureFetch(
        `/api/owner/operators/${operatorId}/payroll/history?limit=12`,
      );
      setPayrollHistoryByOperator((prev) => ({
        ...prev,
        [operatorId]: data.history || [],
      }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPayrollHistoryError((prev) => ({
        ...prev,
        [operatorId]: err?.message || t('failedToLoad'),
      }));
    } finally {
      setPayrollHistoryLoading((prev) => ({ ...prev, [operatorId]: false }));
    }
  };

  const handleOpenPayrollPanel = async (operatorId) => {
    if (payrollPanelOperatorId === operatorId) {
      setPayrollPanelOperatorId(null);
      setPayrollRunError((prev) => ({ ...prev, [operatorId]: '' }));
      setPayrollRunSuccess((prev) => ({ ...prev, [operatorId]: '' }));
      return;
    }
    const month = getCurrentPayrollMonthStr();
    setPayrollPanelOperatorId(operatorId);
    setPayrollMonthByOperator((prev) => ({ ...prev, [operatorId]: month }));
    setPayrollRunError((prev) => ({ ...prev, [operatorId]: '' }));
    setPayrollRunSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    await Promise.all([
      fetchPayrollPreview(operatorId, month),
      fetchPayrollHistory(operatorId),
    ]);
  };

  const handlePayrollMonthChange = async (operatorId, month) => {
    setPayrollMonthByOperator((prev) => ({ ...prev, [operatorId]: month }));
    setPayrollRunError((prev) => ({ ...prev, [operatorId]: '' }));
    setPayrollRunSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    await fetchPayrollPreview(operatorId, month);
  };

  const handleRunPayroll = async (operatorId) => {
    const month = getPayrollMonthForOperator(operatorId);
    const key = payrollMonthKey(operatorId, month);
    setPayrollRunning((prev) => ({ ...prev, [key]: true }));
    setPayrollRunError((prev) => ({ ...prev, [operatorId]: '' }));
    setPayrollRunSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    try {
      const data = await secureFetch(
        `/api/owner/operators/${operatorId}/payroll/run`,
        {
          method: 'POST',
          body: JSON.stringify({ month }),
        },
      );
      setPayrollRunSuccess((prev) => ({
        ...prev,
        [operatorId]: `${t('payrollRunSuccess')} — ${t('payrollNet')}: ₹${Number(data.net_pay || 0).toLocaleString('en-IN')}, ${t('payrollAdvanceDeducted')}: ₹${Number(data.advance_deducted || 0).toLocaleString('en-IN')}`,
      }));
      setPayrollRunCompleted((prev) => ({ ...prev, [key]: true }));
      await fetchPayrollPreview(operatorId, month);
      await fetchPayrollHistory(operatorId);
      await fetchOperators();
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      if (err?.message === 'PAYROLL_ALREADY_RUN') {
        setPayrollRunError((prev) => ({ ...prev, [operatorId]: t('payrollAlreadyRun') }));
        setPayrollRunCompleted((prev) => ({ ...prev, [key]: true }));
        return;
      }
      if (err?.message === 'NO_PAYABLE_AMOUNT') {
        setPayrollRunError((prev) => ({ ...prev, [operatorId]: t('payrollNoAmount') }));
        return;
      }
      setPayrollRunError((prev) => ({
        ...prev,
        [operatorId]: err?.message || t('failedToLoad'),
      }));
    } finally {
      setPayrollRunning((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleExportPayrollCsv = async () => {
    const month = payrollExportMonth || getCurrentPayrollMonthStr();
    setPayrollExportLoading(true);
    setPayrollExportError('');
    try {
      const data = await secureFetch(
        `/api/owner/payroll/export?month=${encodeURIComponent(month)}`,
      );
      if (!data.success) {
        setPayrollExportError(data.error || t('failedToLoad'));
        return;
      }
      const exportRows = data.rows || [];
      const headers = [
        'Operator Name',
        'Phone',
        'Monthly Salary',
        'Daily Rate',
        'Present',
        'Half Day',
        'Paid Leave',
        'Gross Payable',
        'Advance Balance',
        'Net Payable',
      ];
      const csvRows = exportRows.map((row) => [
        row.operator_name,
        forceCsvText(row.phone),
        row.monthly_salary,
        row.daily_rate,
        row.present_days,
        row.half_days,
        row.paid_leave,
        row.gross_payable,
        row.advance_balance,
        row.net_payable,
      ]);
      const csv = buildCsvFromRows(headers, csvRows);
      await downloadCsvContent(csv, `DE-Payroll-${month}.csv`);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPayrollExportError(err?.message || t('failedToLoad'));
    } finally {
      setPayrollExportLoading(false);
    }
  };

  const payrollExportParts = String(payrollExportMonth || getCurrentPayrollMonthStr()).split('-');
  const payrollExportYear = Number(payrollExportParts[0]) || new Date().getFullYear();
  const payrollExportMonthNum = Number(payrollExportParts[1]) || (new Date().getMonth() + 1);
  const payrollExportYearOptions = (() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2];
  })();
  const setPayrollExportParts = (year, monthNum) => {
    setPayrollExportMonth(`${year}-${String(monthNum).padStart(2, '0')}`);
    setPayrollExportError('');
  };

  const getAttendanceMonthForOperator = (operatorId) => (
    attendanceMonthByOperator[operatorId] || getCurrentPayrollMonthStr()
  );

  const fetchOperatorAttendance = async (operatorId, month) => {
    const key = payrollMonthKey(operatorId, month);
    setAttendanceLoading((prev) => ({ ...prev, [key]: true }));
    setAttendanceError((prev) => ({ ...prev, [key]: '' }));
    try {
      const data = await secureFetch(
        `/api/owner/operators/${operatorId}/attendance?month=${encodeURIComponent(month)}`,
      );
      setAttendanceByKey((prev) => ({ ...prev, [key]: data }));
      setAttendanceEditDraft((prev) => {
        const next = { ...prev };
        delete next[operatorId];
        return next;
      });
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setAttendanceError((prev) => ({
        ...prev,
        [key]: err?.message || t('failedToLoad'),
      }));
    } finally {
      setAttendanceLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const fetchOperatorPlQuota = async (operatorId) => {
    const currentYear = new Date().getFullYear();
    setPlQuotaLoading((prev) => ({ ...prev, [operatorId]: true }));
    setPlQuotaError((prev) => ({ ...prev, [operatorId]: '' }));
    try {
      const data = await secureFetch(
        `/api/owner/operators/${operatorId}/pl-quota?year=${currentYear}`,
      );
      const annualPlQuota = Number(data.quota?.annual_pl_quota ?? data.annual_pl_quota ?? 12);
      const plUsed = Number(data.quota?.pl_used ?? data.pl_used ?? 0);
      const plRemaining = Number.isFinite(Number(data.quota?.pl_remaining))
        ? Number(data.quota.pl_remaining)
        : annualPlQuota - plUsed;
      setPlQuotaByOperator((prev) => ({
        ...prev,
        [operatorId]: {
          annual_pl_quota: annualPlQuota,
          pl_used: plUsed,
          pl_remaining: plRemaining,
        },
      }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPlQuotaError((prev) => ({
        ...prev,
        [operatorId]: err?.message || t('failedToLoad'),
      }));
    } finally {
      setPlQuotaLoading((prev) => ({ ...prev, [operatorId]: false }));
    }
  };

  const handleOpenAttendancePanel = async (operatorId) => {
    if (attendancePanelOperatorId === operatorId) {
      setAttendancePanelOperatorId(null);
      setAttendanceSaveSuccess((prev) => ({ ...prev, [operatorId]: '' }));
      if (plQuotaEditOperatorId === operatorId) {
        setPlQuotaEditOperatorId(null);
        setPlQuotaEditAmount('');
        setPlQuotaEditError('');
      }
      return;
    }
    const month = getCurrentPayrollMonthStr();
    setAttendancePanelOperatorId(operatorId);
    setAttendanceMonthByOperator((prev) => ({ ...prev, [operatorId]: month }));
    setAttendanceSaveSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    setPlQuotaUpdateSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    await Promise.all([
      fetchOperatorAttendance(operatorId, month),
      fetchOperatorPlQuota(operatorId),
    ]);
  };

  const handleAttendanceMonthChange = async (operatorId, month) => {
    setAttendanceMonthByOperator((prev) => ({ ...prev, [operatorId]: month }));
    setAttendanceSaveSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    await fetchOperatorAttendance(operatorId, month);
  };

  const handleSaveAttendanceStatus = async (operatorId, dateKey) => {
    const draftStatus = attendanceEditDraft[operatorId]?.[dateKey];
    if (!draftStatus) return;

    const saveKey = `${operatorId}:${dateKey}`;
    setAttendanceSavingKey(saveKey);
    setAttendanceSaveSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    try {
      await secureFetch(
        `/api/owner/operators/${operatorId}/attendance/${dateKey}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: draftStatus }),
        },
      );
      setAttendanceSaveSuccess((prev) => ({ ...prev, [operatorId]: t('ownerAttendanceOverrideSuccess') }));
      const month = getAttendanceMonthForOperator(operatorId);
      await fetchOperatorAttendance(operatorId, month);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setAttendanceError((prev) => ({
        ...prev,
        [payrollMonthKey(operatorId, getAttendanceMonthForOperator(operatorId))]:
          err?.message || t('failedToLoad'),
      }));
    } finally {
      setAttendanceSavingKey('');
    }
  };

  const handleProposeAttendanceCorrection = async (operatorId, dateKey, fallbackTime = '') => {
    const draftTime = String(
      attendanceProposeDraft[operatorId]?.[dateKey] || fallbackTime || '',
    ).trim();
    if (!draftTime) return;

    const saveKey = `${operatorId}:${dateKey}`;
    setAttendanceProposeSavingKey(saveKey);
    setAttendanceProposeError((prev) => ({ ...prev, [saveKey]: '' }));
    setAttendanceSaveSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    try {
      await secureFetch(
        `/api/owner/operators/${operatorId}/attendance/${dateKey}/propose-correction`,
        {
          method: 'PATCH',
          body: JSON.stringify({ proposed_check_in: draftTime }),
        },
      );
      setAttendanceSaveSuccess((prev) => ({
        ...prev,
        [operatorId]: `Correction proposed for ${dateKey}`,
      }));
      const month = getAttendanceMonthForOperator(operatorId);
      await fetchOperatorAttendance(operatorId, month);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setAttendanceProposeError((prev) => ({
        ...prev,
        [saveKey]: err?.message || t('actionFailed'),
      }));
    } finally {
      setAttendanceProposeSavingKey('');
    }
  };

  const handleOpenSalaryEdit = (operatorId, currentSalary) => {
    if (salaryEditOperatorId === operatorId) {
      setSalaryEditOperatorId(null);
      setSalaryEditAmount('');
      setSalaryEditError('');
      return;
    }
    setSalaryEditOperatorId(operatorId);
    const parsed = Number(currentSalary);
    setSalaryEditAmount(Number.isFinite(parsed) && parsed > 0 ? String(Math.round(parsed)) : '');
    setSalaryEditError('');
    setSalaryUpdateSuccess((prev) => ({ ...prev, [operatorId]: '' }));
  };

  const handleCancelSalaryEdit = () => {
    setSalaryEditOperatorId(null);
    setSalaryEditAmount('');
    setSalaryEditError('');
  };

  const handleSaveOperatorSalary = async (operatorId) => {
    const parsedAmount = Number(salaryEditAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setSalaryEditError(t('monthlySalaryRequired'));
      return;
    }
    setSalaryEditSubmitting(true);
    setSalaryEditError('');
    try {
      const data = await secureFetch(
        `/api/owner/operators/${operatorId}/salary`,
        {
          method: 'PATCH',
          body: JSON.stringify({ monthly_salary: parsedAmount }),
        },
      );
      const updatedSalary = data.monthly_salary ?? parsedAmount;
      setOperatorList((prev) => prev.map((op) => (
        op.id === operatorId ? { ...op, monthly_salary: updatedSalary } : op
      )));
      setSalaryUpdateSuccess((prev) => ({ ...prev, [operatorId]: t('ownerSalaryUpdated') }));
      setSalaryEditOperatorId(null);
      setSalaryEditAmount('');
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setSalaryEditError(err?.message || t('failedToLoad'));
    } finally {
      setSalaryEditSubmitting(false);
    }
  };

  const handleOpenPlQuotaEdit = (operatorId, currentQuota) => {
    if (plQuotaEditOperatorId === operatorId) {
      setPlQuotaEditOperatorId(null);
      setPlQuotaEditAmount('');
      setPlQuotaEditError('');
      return;
    }
    setPlQuotaEditOperatorId(operatorId);
    const parsed = Number(currentQuota);
    setPlQuotaEditAmount(Number.isFinite(parsed) && parsed >= 0 ? String(Math.floor(parsed)) : '12');
    setPlQuotaEditError('');
    setPlQuotaUpdateSuccess((prev) => ({ ...prev, [operatorId]: '' }));
  };

  const handleCancelPlQuotaEdit = () => {
    setPlQuotaEditOperatorId(null);
    setPlQuotaEditAmount('');
    setPlQuotaEditError('');
  };

  const handleSavePlQuota = async (operatorId) => {
    const parsedQuota = Number(plQuotaEditAmount);
    if (!Number.isFinite(parsedQuota) || parsedQuota < 0) {
      setPlQuotaEditError(t('failedToLoad'));
      return;
    }

    setPlQuotaEditSubmitting(true);
    setPlQuotaEditError('');
    setPlQuotaUpdateSuccess((prev) => ({ ...prev, [operatorId]: '' }));
    try {
      await secureFetch(
        `/api/owner/operators/${operatorId}/pl-quota`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            annual_pl_quota: Math.floor(parsedQuota),
            year: new Date().getFullYear(),
          }),
        },
      );
      setPlQuotaUpdateSuccess((prev) => ({ ...prev, [operatorId]: t('plQuotaUpdated') }));
      setPlQuotaEditOperatorId(null);
      setPlQuotaEditAmount('');
      await fetchOperatorPlQuota(operatorId);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPlQuotaEditError(err?.message || t('failedToLoad'));
    } finally {
      setPlQuotaEditSubmitting(false);
    }
  };

  const handleAssignMachine = async (operatorId, machineId) => {
    setAssignError('');
    try {
      const data = await secureFetch(
        `/api/owner/machines/${machineId}/assign-operator`,
        {
          method: 'PATCH',
          body: JSON.stringify({ operatorId }),
        },
      );
      if (data.success) {
        await fetchOperators();
        const machines = await getMachines();
        const ownerId = sessionUser?.id || sessionUser?.user_id;
        setMachineData(
          Array.isArray(machines)
            ? machines.filter((m) => !ownerId || m.owner_id === ownerId)
            : [],
        );
        setAssigningOperatorId(null);
      } else {
        if (data.error === 'KYC_REJECTED') {
          setAssignError(t('operatorKycRejectedAssign'));
        } else {
          setAssignError(data.error || t('assignOperatorFailed'));
        }
      }
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      if (e?.message === 'KYC_REJECTED') {
        setAssignError(t('operatorKycRejectedAssign'));
      } else {
        setAssignError(e?.message || t('assignOperatorFailed'));
      }
    }
  };

  const handleSaveMachine = async (machineId) => {
    try {
      await secureFetch(
        `/api/owner/machines/${machineId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            ...editMachineForm,
            working_hours_start: workingHoursFormValue(
              editMachineForm.working_hours_start,
              '09:00',
            ),
            working_hours_end: workingHoursFormValue(
              editMachineForm.working_hours_end,
              '18:00',
            ),
            working_days: Array.isArray(editMachineForm.working_days)
              ? editMachineForm.working_days
              : DEFAULT_WORKING_DAYS,
          }),
        },
      );
      setEditingMachineId(null);
      setEditMachineForm({});
      const machines = await getMachines();
      const ownerId = sessionUser?.id ||
        sessionUser?.user_id;
      setMachineData(
        Array.isArray(machines)
          ? machines.filter((m) =>
            !ownerId || m.owner_id === ownerId)
          : [],
      );
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      alert(t('machineUpdateFailed'));
    }
  };

  const handleSaveSchedule = async (machineId) => {
    if (!machineId) return;
    try {
      await updateMachine(machineId, {
        working_hours_start: workingHoursFormValue(
          editMachineForm.working_hours_start,
          '09:00',
        ),
        working_hours_end: workingHoursFormValue(
          editMachineForm.working_hours_end,
          '18:00',
        ),
        working_days: Array.isArray(editMachineForm.working_days)
          ? editMachineForm.working_days
          : [...DEFAULT_WORKING_DAYS],
      });
      const machines = await getMachines();
      const ownerId = sessionUser?.id || sessionUser?.user_id;
      setMachineData(
        Array.isArray(machines)
          ? machines.filter((m) => !ownerId || m.owner_id === ownerId)
          : [],
      );
      alert(t('machineUpdateSuccess') || 'Schedule updated successfully.');
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      alert(t('machineUpdateFailed') || 'Update failed. Please try again.');
    }
  };

  const handleToggleAvailability = async (machineUuid, currentValue) => {
    if (!machineUuid || availabilityToggling[machineUuid]) return;
    const newValue = !currentValue;
    setAvailabilityToggling((prev) => ({ ...prev, [machineUuid]: true }));
    setMachineData((prev) => prev.map((row) => (
      row.id === machineUuid
        ? {
          ...row,
          is_available: newValue,
          unavailable_reason: newValue ? null : 'Owner marked unavailable',
        }
        : row
    )));
    try {
      await updateMachine(machineUuid, {
        is_available: newValue,
        unavailable_reason: newValue ? null : 'Owner marked unavailable',
      });
    } catch (err) {
      if ((err?.message || '') !== 'auth_required') {
        setMachineData((prev) => prev.map((row) => (
          row.id === machineUuid
            ? { ...row, is_available: currentValue }
            : row
        )));
      }
    } finally {
      setAvailabilityToggling((prev) => ({ ...prev, [machineUuid]: false }));
    }
  };

  const openEquipmentProfile = (machineUuid, raw) => {
    if (!machineUuid) return;
    setEditRegSelectedId(machineUuid);
    setEditingMachineId(machineUuid);
    setEditMachineForm({
      rate_per_hour: raw?.rate_per_hour != null ? String(raw.rate_per_hour) : '',
      minimum_hours: raw?.minimum_hours != null ? String(raw.minimum_hours) : '',
      rate_per_day: raw?.rate_per_day != null ? String(raw.rate_per_day) : '',
      rate_per_week: raw?.rate_per_week != null ? String(raw.rate_per_week) : '',
      rate_per_month: raw?.rate_per_month != null ? String(raw.rate_per_month) : '',
      fuel_capacity_litres: raw?.fuel_capacity_litres != null
        ? String(raw.fuel_capacity_litres) : '',
      is_negotiable: Boolean(raw?.is_negotiable),
      minimum_rate_per_cycle: raw?.minimum_rate_per_cycle != null
        ? String(raw.minimum_rate_per_cycle) : '',
      attachments: Array.isArray(raw?.attachments) ? [...raw.attachments] : [],
      attachment_rates: (
        raw?.attachment_rates
        && typeof raw.attachment_rates === 'object'
        && !Array.isArray(raw.attachment_rates)
      ) ? { ...raw.attachment_rates } : {},
      attachment_min_hours: (
        raw?.attachment_min_hours
        && typeof raw.attachment_min_hours === 'object'
        && !Array.isArray(raw.attachment_min_hours)
      ) ? { ...raw.attachment_min_hours } : {},
      primary_attachment: raw?.primary_attachment || '',
      attachment_notes: raw?.attachment_notes || '',
      pincode: raw?.pincode ? String(raw.pincode) : '',
      working_hours_start: workingHoursFormValue(raw?.working_hours_start, '09:00'),
      working_hours_end: workingHoursFormValue(raw?.working_hours_end, '18:00'),
      working_days: Array.isArray(raw?.working_days) && raw.working_days.length
        ? [...raw.working_days]
        : [...DEFAULT_WORKING_DAYS],
      payment_due_days: raw?.payment_due_days != null
        ? String(raw.payment_due_days) : '',
      early_payment_days: raw?.early_payment_days != null
        ? String(raw.early_payment_days) : '',
      early_payment_discount_pct: raw?.early_payment_discount_pct != null
        ? String(raw.early_payment_discount_pct) : '',
      late_payment_fee_pct: raw?.late_payment_fee_pct != null
        ? String(raw.late_payment_fee_pct) : '',
    });
    setEditingFinancialMachineId(null);
    setEditingComplianceId(null);
    setActiveTab('edit-registration');
  };

  const openFinancialEdit = (machineUuid, raw) => {
    setEditingFinancialMachineId(machineUuid);
    setFinancialForm({
      ownership_type: raw?.ownership_type || 'new',
      purchase_price: raw?.purchase_price ?? '',
      purchase_date: raw?.purchase_date ? String(raw.purchase_date).slice(0, 10) : '',
      purchase_year: raw?.purchase_year ?? raw?.year ?? '',
      loan_amount: raw?.loan_amount ?? '',
      monthly_emi: raw?.monthly_emi ?? '',
      loan_tenure_months: raw?.loan_tenure_months ?? '',
      loan_start_date: raw?.loan_start_date ? String(raw.loan_start_date).slice(0, 10) : '',
      fuel_consumption_liters_per_hour: raw?.fuel_consumption_liters_per_hour ?? '',
      operator_monthly_salary: raw?.operator_monthly_salary || '',
      service_monthly: raw?.service_monthly || '',
      breakdown_monthly: raw?.breakdown_monthly || '',
      materials_monthly: raw?.materials_monthly || '',
    });
    setFinancialLoanOpen(Boolean(
      raw?.loan_amount || raw?.monthly_emi || raw?.loan_tenure_months || raw?.loan_start_date,
    ));
  };

  const handleSaveFinancialDetails = async (machineId) => {
    setFinancialSaving(true);
    try {
      const toNumOrNull = (val) => {
        if (val === '' || val == null) return null;
        const n = Number(val);
        return Number.isFinite(n) ? n : null;
      };
      const body = {
        ownership_type: financialForm.ownership_type || null,
        purchase_price: toNumOrNull(financialForm.purchase_price),
        purchase_date: financialForm.purchase_date || null,
        purchase_year: toNumOrNull(financialForm.purchase_year),
        fuel_consumption_liters_per_hour: toNumOrNull(financialForm.fuel_consumption_liters_per_hour),
        operator_monthly_salary: toNumOrNull(financialForm.operator_monthly_salary),
        service_monthly: toNumOrNull(financialForm.service_monthly),
        breakdown_monthly: toNumOrNull(financialForm.breakdown_monthly),
        materials_monthly: toNumOrNull(financialForm.materials_monthly),
      };
      if (financialLoanOpen) {
        body.loan_amount = toNumOrNull(financialForm.loan_amount);
        body.monthly_emi = toNumOrNull(financialForm.monthly_emi);
        body.loan_tenure_months = toNumOrNull(financialForm.loan_tenure_months);
        body.loan_start_date = financialForm.loan_start_date || null;
      } else {
        body.loan_amount = null;
        body.monthly_emi = null;
        body.loan_tenure_months = null;
        body.loan_start_date = null;
      }
      await secureFetch(
        `/api/owner/machines/${machineId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
        },
      );
      setEditingFinancialMachineId(null);
      setFinancialForm({});
      setFinancialLoanOpen(false);
      const machines = await getMachines();
      const ownerId = sessionUser?.id || sessionUser?.user_id;
      setMachineData(
        Array.isArray(machines)
          ? machines.filter((m) => !ownerId || m.owner_id === ownerId)
          : [],
      );
      await loadRecoveryForMachine(machineId);
      alert(t('financialDetailsSaved'));
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      alert(e?.message || t('machineUpdateFailed'));
    } finally {
      setFinancialSaving(false);
    }
  };

  const renderFinancialEditForm = (machineUuid) => (
    <div style={{ marginTop: 8 }}>
      <p style={{
        color: '#8896a8',
        fontSize: 11,
        margin: '0 0 6px',
        fontWeight: 600,
      }}>
        {t('machineOwnershipType')}
      </p>
      <div style={{ marginBottom: 8 }}>
        {FINANCIAL_OWNERSHIP_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#e8e0d0',
              fontSize: 11,
              marginBottom: 4,
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name={`ownership-${machineUuid}`}
              value={opt.value}
              checked={financialForm.ownership_type === opt.value}
              onChange={() => setFinancialForm((prev) => ({
                ...prev,
                ownership_type: opt.value,
              }))}
            />
            {t(opt.labelKey)}
          </label>
        ))}
      </div>
      <div style={{ marginBottom: 8 }}>
        <label style={{
          color: '#8896a8',
          fontSize: 11,
          display: 'block',
          marginBottom: 3,
        }}>
          {t('machinePurchasePrice')}
        </label>
        <input
          type="number"
          min="0"
          className="de-owner-input"
          value={financialForm.purchase_price ?? ''}
          onChange={(e) => setFinancialForm((prev) => ({
            ...prev,
            purchase_price: e.target.value,
          }))}
        />
      </div>
      {(financialForm.ownership_type === 'new'
        || financialForm.ownership_type === 'used_first_owner') && (
        <div style={{ marginBottom: 8 }}>
          <label style={{
            color: '#8896a8',
            fontSize: 11,
            display: 'block',
            marginBottom: 3,
          }}>
            {t('machinePurchaseDate')}
          </label>
          <input
            type="date"
            className="de-owner-input"
            value={financialForm.purchase_date || ''}
            onChange={(e) => setFinancialForm((prev) => ({
              ...prev,
              purchase_date: e.target.value,
            }))}
          />
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <label style={{
          color: '#8896a8',
          fontSize: 11,
          display: 'block',
          marginBottom: 3,
        }}>
          {t('machineYearOfManufacture')}
        </label>
        <input
          type="number"
          min="1950"
          max="2100"
          className="de-owner-input"
          value={financialForm.purchase_year ?? ''}
          onChange={(e) => setFinancialForm((prev) => ({
            ...prev,
            purchase_year: e.target.value,
          }))}
        />
      </div>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: '#e8e0d0',
        fontSize: 11,
        marginBottom: 8,
        cursor: 'pointer',
      }}>
        <input
          type="checkbox"
          checked={financialLoanOpen}
          onChange={(e) => {
            setFinancialLoanOpen(e.target.checked);
            if (!e.target.checked) {
              setFinancialForm((prev) => ({
                ...prev,
                loan_amount: '',
                monthly_emi: '',
                loan_tenure_months: '',
                loan_start_date: '',
              }));
            }
          }}
        />
        {t('machineLoanSection')}
      </label>
      {financialLoanOpen && (
        <div style={{ marginBottom: 8 }}>
          {[
            { key: 'loan_amount', labelKey: 'machineLoanAmount' },
            { key: 'monthly_emi', labelKey: 'machineLoanEmi' },
            { key: 'loan_tenure_months', labelKey: 'machineLoanTenure' },
          ].map((field) => (
            <div key={field.key} style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t(field.labelKey)}
              </label>
              <input
                type="number"
                min="0"
                className="de-owner-input"
                value={financialForm[field.key] ?? ''}
                onChange={(e) => setFinancialForm((prev) => ({
                  ...prev,
                  [field.key]: e.target.value,
                }))}
              />
            </div>
          ))}
          <div style={{ marginBottom: 8 }}>
            <label style={{
              color: '#8896a8',
              fontSize: 11,
              display: 'block',
              marginBottom: 3,
            }}>
              {t('machineLoanStartDate')}
            </label>
            <input
              type="date"
              className="de-owner-input"
              value={financialForm.loan_start_date || ''}
              onChange={(e) => setFinancialForm((prev) => ({
                ...prev,
                loan_start_date: e.target.value,
              }))}
            />
          </div>
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <label style={{
          color: '#8896a8',
          fontSize: 11,
          display: 'block',
          marginBottom: 3,
        }}>
          {t('machineFuelConsumption')}
        </label>
        <input
          type="number"
          min="0"
          step="0.1"
          className="de-owner-input"
          value={financialForm.fuel_consumption_liters_per_hour ?? ''}
          onChange={(e) => setFinancialForm((prev) => ({
            ...prev,
            fuel_consumption_liters_per_hour: e.target.value,
          }))}
        />
      </div>
      <div style={{
        marginTop: 16,
        marginBottom: 8,
        fontSize: 12,
        fontWeight: 700,
        color: '#C9A84C',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}>
        {t('machineMonthlyOperatingExpenses')}
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{
          fontSize: 12,
          color: '#8896a8',
          marginBottom: 4,
          display: 'block',
        }}>
          {t('machineOperatorMonthlySalary')}
        </label>
        <input
          type="number"
          min="0"
          step="100"
          className="de-owner-input"
          placeholder="e.g. 15000"
          value={financialForm.operator_monthly_salary ?? ''}
          onChange={(e) => setFinancialForm((f) => ({
            ...f,
            operator_monthly_salary: e.target.value,
          }))}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{
          fontSize: 12,
          color: '#8896a8',
          marginBottom: 4,
          display: 'block',
        }}>
          {t('machineServiceMonthly')}
        </label>
        <input
          type="number"
          min="0"
          step="100"
          className="de-owner-input"
          placeholder="e.g. 5000"
          value={financialForm.service_monthly ?? ''}
          onChange={(e) => setFinancialForm((f) => ({
            ...f,
            service_monthly: e.target.value,
          }))}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{
          fontSize: 12,
          color: '#8896a8',
          marginBottom: 4,
          display: 'block',
        }}>
          {t('machineBreakdownMonthly')}
        </label>
        <input
          type="number"
          min="0"
          step="100"
          className="de-owner-input"
          placeholder="e.g. 2000"
          value={financialForm.breakdown_monthly ?? ''}
          onChange={(e) => setFinancialForm((f) => ({
            ...f,
            breakdown_monthly: e.target.value,
          }))}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{
          fontSize: 12,
          color: '#8896a8',
          marginBottom: 4,
          display: 'block',
        }}>
          {t('machineMaterialsMonthly')}
        </label>
        <input
          type="number"
          min="0"
          step="100"
          className="de-owner-input"
          placeholder="e.g. 1000"
          value={financialForm.materials_monthly ?? ''}
          onChange={(e) => setFinancialForm((f) => ({
            ...f,
            materials_monthly: e.target.value,
          }))}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          disabled={financialSaving}
          onClick={() => handleSaveFinancialDetails(machineUuid)}
          style={{
            background: 'rgba(201,168,76,0.2)',
            border: '1px solid rgba(201,168,76,0.4)',
            color: '#C9A84C',
            borderRadius: 6,
            padding: '6px 16px',
            fontSize: 12,
            cursor: financialSaving ? 'wait' : 'pointer',
            fontWeight: 600,
          }}
        >
          {financialSaving ? t('loading') : t('save')}
        </button>
        <button
          type="button"
          disabled={financialSaving}
          onClick={() => {
            setEditingFinancialMachineId(null);
            setFinancialForm({});
            setFinancialLoanOpen(false);
          }}
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#8896a8',
            borderRadius: 6,
            padding: '6px 16px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {t('cancel')}
        </button>
      </div>
    </div>
  );

  const renderDieselSection = (machineUuid, raw) => {
    const latest = dieselLatest[machineUuid];
    const loading = dieselLoading[machineUuid];
    const pricePerLitre = latest?.price_per_litre != null
      ? Number(latest.price_per_litre)
      : null;
    const consumption = Number(raw?.fuel_consumption_liters_per_hour);
    const hourlyRate = Number(raw?.rate_per_hour);
    let fuelCostPerHour = null;
    let fuelPercentOfRate = null;
    if (pricePerLitre != null && Number.isFinite(pricePerLitre) && pricePerLitre > 0
      && Number.isFinite(consumption) && consumption > 0) {
      fuelCostPerHour = Math.round(consumption * pricePerLitre * 100) / 100;
      if (Number.isFinite(hourlyRate) && hourlyRate > 0) {
        fuelPercentOfRate = Math.round((fuelCostPerHour / hourlyRate) * 10000) / 100;
      }
    }

    return (
      <div style={{
        marginTop: 12,
        padding: 12,
        background: 'rgba(255,255,255,0.03)',
        borderRadius: 8,
        border: '1px solid rgba(201,168,76,0.2)',
      }}>
        {loading ? (
          <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 8px' }}>{t('loading')}</p>
        ) : pricePerLitre != null && Number.isFinite(pricePerLitre) ? (
          <div style={{ marginBottom: 8 }}>
            <p style={{ color: '#e8e0d0', fontSize: 12, fontWeight: 600, margin: '0 0 2px' }}>
              {`${t('dieselPriceLatest')}: ₹${pricePerLitre}/litre`}
            </p>
            {latest?.date && (
              <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                {latest.date}
              </p>
            )}
          </div>
        ) : (
          <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 8px' }}>
            {t('dieselPriceNoRecord')}
          </p>
        )}
        <div style={{ marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => toggleDieselHistory(machineUuid)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#C9A84C',
              fontSize: 11,
              cursor: 'pointer',
              padding: 0,
              fontWeight: 600,
              textDecoration: 'underline',
            }}
          >
            {dieselHistoryOpen[machineUuid] ? '▼' : '▶'} {t('dieselHistory')}
          </button>
          {dieselHistoryOpen[machineUuid] && (
            <div style={{ marginTop: 8 }}>
              {dieselHistoryLoading[machineUuid] ? (
                <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>{t('loading')}</p>
              ) : (dieselPriceHistory[machineUuid] || []).length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>{t('dieselNoHistory')}</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 10,
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
                        <th style={{ color: '#8896a8', textAlign: 'left', padding: '4px 6px 4px 0', fontWeight: 600 }}>
                          {t('dieselColDate')}
                        </th>
                        <th style={{ color: '#8896a8', textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>
                          {t('dieselColPrice')}
                        </th>
                        <th style={{ color: '#8896a8', textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>
                          {t('dieselColQty')}
                        </th>
                        <th style={{ color: '#8896a8', textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>
                          {t('dieselTaluka')}
                        </th>
                        <th style={{ color: '#8896a8', textAlign: 'left', padding: '4px 0 4px 6px', fontWeight: 600 }}>
                          {t('dieselColSource')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dieselPriceHistory[machineUuid] || []).map((row) => (
                        <tr key={row.id || `${row.date}-${row.price_per_litre}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ color: '#e8e0d0', padding: '4px 6px 4px 0' }}>{row.date || '—'}</td>
                          <td style={{ color: '#e8e0d0', padding: '4px 6px' }}>
                            {row.price_per_litre != null ? `₹${row.price_per_litre}` : '—'}
                          </td>
                          <td style={{ color: '#e8e0d0', padding: '4px 6px' }}>
                            {row.quantity_litres != null ? row.quantity_litres : '—'}
                          </td>
                          <td style={{ color: '#e8e0d0', padding: '4px 6px' }}>{row.city || '—'}</td>
                          <td style={{ color: '#e8e0d0', padding: '4px 0 4px 6px' }}>{row.source || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        {fuelCostPerHour != null && (
          <div style={{ marginBottom: 8 }}>
            <p style={{ color: '#e8e0d0', fontSize: 12, fontWeight: 600, margin: '0 0 2px' }}>
              {`${t('dieselFuelCostPerHour')}: ₹${fuelCostPerHour}/hr`}
            </p>
            {fuelPercentOfRate != null && (
              <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                {`${fuelPercentOfRate}% ${t('dieselPercentOfRate')}`}
              </p>
            )}
          </div>
        )}
        {(() => {
          const suggestion = dieselRateSuggestion[machineUuid];
          const suggestionLoading = dieselRateSuggestionLoading[machineUuid];
          if (suggestionLoading || !suggestion?.suggestion_available
            || suggestion.direction === 'no_change') {
            return null;
          }
          const absChange = Math.abs(Number(suggestion.diesel_change) || 0);
          const absSuggested = Math.abs(Number(suggestion.suggested_change) || 0);
          const isIncrease = suggestion.direction === 'increase';
          return (
            <div style={{
              marginBottom: 8,
              padding: 10,
              borderRadius: 6,
              background: isIncrease ? 'rgba(233,69,96,0.08)' : 'rgba(76,175,80,0.08)',
              border: `1px solid ${isIncrease ? 'rgba(233,69,96,0.25)' : 'rgba(76,175,80,0.25)'}`,
            }}>
              <p style={{
                color: '#8896a8',
                fontSize: 10,
                fontWeight: 600,
                margin: '0 0 4px',
                textTransform: 'uppercase',
              }}>
                {t('dieselRateSuggestion')}
              </p>
              <p style={{
                color: isIncrease ? '#e94560' : '#4CAF50',
                fontSize: 11,
                fontWeight: 600,
                margin: '0 0 4px',
              }}>
                {isIncrease
                  ? `🔴 ${t('dieselRateIncrease')} ₹${absChange} — ₹${absSuggested}/hr`
                  : `🟢 ${t('dieselRateDecrease')} ₹${absChange} — ₹${absSuggested}/hr`}
              </p>
              <p style={{ color: '#e8e0d0', fontSize: 11, margin: '0 0 8px' }}>
                {`${t('dieselSuggestedRate')}: ₹${suggestion.suggested_rate}/hr`}
              </p>
              <button
                type="button"
                disabled={dieselRateApplying[machineUuid]}
                onClick={() => handleApplyRateSuggestion(machineUuid, suggestion.suggested_rate)}
                style={{
                  background: isIncrease ? 'rgba(233,69,96,0.2)' : 'rgba(76,175,80,0.2)',
                  border: `1px solid ${isIncrease ? 'rgba(233,69,96,0.4)' : 'rgba(76,175,80,0.4)'}`,
                  color: isIncrease ? '#e94560' : '#4CAF50',
                  borderRadius: 6,
                  padding: '5px 12px',
                  fontSize: 11,
                  cursor: dieselRateApplying[machineUuid] ? 'wait' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {dieselRateApplying[machineUuid]
                  ? t('loading')
                  : t('applyRateSuggestion').replace('{{rate}}', suggestion.suggested_rate)}
              </button>
            </div>
          );
        })()}
        {dieselFormOpen === machineUuid ? (
          <div style={{ marginTop: 8 }}>
            <div style={{
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
              <p style={{
                color: '#8896a8',
                fontSize: 11,
                fontWeight: 600,
                margin: '0 0 6px',
              }}>
                {t('dieselUploadReceipt')}
              </p>
              <input
                ref={dieselReceiptInputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleDieselReceiptUpload(machineUuid, file);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                disabled={dieselReceiptUploading || dieselSaving}
                onClick={() => dieselReceiptInputRef.current?.click()}
                style={{
                  background: 'rgba(201,168,76,0.1)',
                  border: '1px solid rgba(201,168,76,0.3)',
                  color: '#C9A84C',
                  borderRadius: 6,
                  padding: '5px 12px',
                  fontSize: 11,
                  cursor: dieselReceiptUploading ? 'wait' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {dieselReceiptUploading ? t('loading') : t('dieselUploadReceipt')}
              </button>
              {dieselExtractMessage?.machineUuid === machineUuid
                && dieselExtractMessage.type === 'success'
                && dieselExtractMessage.extracted && (
                <p style={{ color: '#4CAF50', fontSize: 11, margin: '6px 0 0', fontWeight: 600 }}>
                  {`${t('dieselExtractSuccess')}: ₹${dieselExtractMessage.extracted.price_per_litre}/litre`}
                  {dieselExtractMessage.extracted.quantity_litres != null
                    ? `, ${dieselExtractMessage.extracted.quantity_litres}L`
                    : ''}
                  {dieselExtractMessage.extracted.date
                    ? ` ${t('date')} ${dieselExtractMessage.extracted.date}`
                    : ''}
                </p>
              )}
              {dieselExtractMessage?.machineUuid === machineUuid
                && dieselExtractMessage.type === 'error' && (
                <p style={{ color: '#e94560', fontSize: 11, margin: '6px 0 0' }}>
                  {t('dieselExtractFailed')}
                </p>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('date')}
              </label>
              <input
                type="date"
                className="de-owner-input"
                value={dieselForm.date || ''}
                onChange={(e) => setDieselForm((prev) => ({
                  ...prev,
                  date: e.target.value,
                }))}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('dieselPricePerLitre')}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="de-owner-input"
                value={dieselForm.price_per_litre ?? ''}
                onChange={(e) => setDieselForm((prev) => ({
                  ...prev,
                  price_per_litre: e.target.value,
                }))}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('dieselQuantity')}
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                className="de-owner-input"
                value={dieselForm.quantity_litres ?? ''}
                onChange={(e) => setDieselForm((prev) => ({
                  ...prev,
                  quantity_litres: e.target.value,
                }))}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('dieselTaluka')}
              </label>
              <input
                type="text"
                className="de-owner-input"
                value={dieselForm.city ?? ''}
                onChange={(e) => setDieselForm((prev) => ({
                  ...prev,
                  city: e.target.value,
                }))}
              />
            </div>
            <p style={{ color: '#8896a8', fontSize: 10, margin: '0 0 8px' }}>
              Manual entry
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                disabled={dieselSaving || dieselReceiptUploading
                  || (dieselExtractMessage?.machineUuid === machineUuid
                    && dieselExtractMessage.type === 'success')}
                onClick={() => handleSaveDieselPrice(machineUuid)}
                style={{
                  background: 'rgba(201,168,76,0.2)',
                  border: '1px solid rgba(201,168,76,0.4)',
                  color: '#C9A84C',
                  borderRadius: 6,
                  padding: '6px 16px',
                  fontSize: 12,
                  cursor: dieselSaving ? 'wait' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {dieselSaving ? t('loading') : t('save')}
              </button>
              <button
                type="button"
                disabled={dieselSaving}
                onClick={() => {
                  setDieselFormOpen(null);
                  setDieselForm({});
                  setDieselExtractMessage(null);
                }}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#8896a8',
                  borderRadius: 6,
                  padding: '6px 16px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openDieselForm(machineUuid)}
            style={{
              background: 'rgba(201,168,76,0.1)',
              border: '1px solid rgba(201,168,76,0.3)',
              color: '#C9A84C',
              borderRadius: 6,
              padding: '5px 12px',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            {t('dieselPriceUpdate')}
          </button>
        )}
      </div>
    );
  };

  const renderMarketSection = (machineUuid, raw) => {
    const loading = marketPositionLoading[machineUuid];
    const data = marketPosition[machineUuid];
    const marketAvailable = data?.market_available === true;
    const position = data?.position;
    const formOpen = marketFormOpen === machineUuid;
    const machineType = raw?.type ? String(raw.type).trim() : '';

    let positionLabel = '';
    let positionColor = '#8896a8';
    if (position === 'below') {
      positionLabel = `🔴 ${t('marketBelowMarket')}`;
      positionColor = '#e94560';
    } else if (position === 'above') {
      positionLabel = `🟢 ${t('marketAboveMarket')}`;
      positionColor = '#4CAF50';
    } else if (position === 'competitive') {
      positionLabel = `🟡 ${t('marketCompetitive')}`;
      positionColor = '#C9A84C';
    }

    return (
      <div style={{
        marginTop: 12,
        padding: 12,
        background: 'rgba(255,255,255,0.03)',
        borderRadius: 8,
        border: '1px solid rgba(201,168,76,0.2)',
      }}>
        {loading ? (
          <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 8px' }}>{t('loading')}</p>
        ) : marketAvailable ? (
          <div style={{ marginBottom: 8 }}>
            <p style={{
              color: positionColor,
              fontSize: 12,
              fontWeight: 700,
              margin: '0 0 6px',
            }}>
              {positionLabel}
            </p>
            <p style={{ color: '#e8e0d0', fontSize: 11, margin: '0 0 4px' }}>
              {`${t('marketRange').replace('{{city}}', data.city || '—')}: ₹${data.market_min} – ₹${data.market_max}/hr`}
            </p>
            {data.city_source === 'local' ? (
              <p style={{ color: '#8896a8', fontSize: 10, margin: '0 0 4px' }}>
                {`📍 ${t('marketSourceLocal').replace('{{city}}', data.city || '—')}`}
              </p>
            ) : data.city_source === 'platform_wide' ? (
              <p style={{ color: '#8896a8', fontSize: 10, margin: '0 0 4px' }}>
                {`🌐 ${t('marketSourcePlatform')}`}
              </p>
            ) : null}
            <p style={{ color: '#e8e0d0', fontSize: 11, margin: '0 0 4px' }}>
              {`${t('ratePerHourLabel')}: ₹${data.current_rate}/hr`}
            </p>
            <p style={{ color: '#8896a8', fontSize: 10, margin: 0 }}>
              {t('marketDataPoints').replace('{{count}}', String(data.data_points || 0))}
            </p>
          </div>
        ) : (
          <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 8px' }}>
            {t('marketNoData')}
          </p>
        )}
        {marketRatePendingReview[machineUuid] ? (
          <p style={{
            color: '#C9A84C',
            fontSize: 11,
            fontWeight: 600,
            margin: '0 0 8px',
            padding: '8px 10px',
            background: 'rgba(201,168,76,0.12)',
            border: '1px solid rgba(201,168,76,0.35)',
            borderRadius: 6,
          }}>
            {t('marketRatePendingReview')}
          </p>
        ) : null}
        {formOpen ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('marketCity')}
              </label>
              <input
                type="text"
                className="de-owner-input"
                value={marketForm.city ?? ''}
                onChange={(e) => setMarketForm((prev) => ({
                  ...prev,
                  city: e.target.value,
                }))}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('type')}
              </label>
              <input
                type="text"
                className="de-owner-input"
                value={machineType}
                readOnly
                style={{ opacity: 0.85 }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('marketMinRate')}
              </label>
              <input
                type="number"
                min="0"
                step="50"
                className="de-owner-input"
                value={marketForm.min_rate_per_hour ?? ''}
                onChange={(e) => setMarketForm((prev) => ({
                  ...prev,
                  min_rate_per_hour: e.target.value,
                }))}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{
                color: '#8896a8',
                fontSize: 11,
                display: 'block',
                marginBottom: 3,
              }}>
                {t('marketMaxRate')}
              </label>
              <input
                type="number"
                min="0"
                step="50"
                className="de-owner-input"
                value={marketForm.max_rate_per_hour ?? ''}
                onChange={(e) => setMarketForm((prev) => ({
                  ...prev,
                  max_rate_per_hour: e.target.value,
                }))}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                disabled={marketSaving || !machineType}
                onClick={() => handleSubmitMarketRate(machineUuid, raw)}
                style={{
                  background: 'rgba(201,168,76,0.2)',
                  border: '1px solid rgba(201,168,76,0.4)',
                  color: '#C9A84C',
                  borderRadius: 6,
                  padding: '6px 16px',
                  fontSize: 12,
                  cursor: marketSaving ? 'wait' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {marketSaving ? t('loading') : t('save')}
              </button>
              <button
                type="button"
                disabled={marketSaving}
                onClick={() => {
                  setMarketFormOpen(null);
                  setMarketForm({});
                }}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#8896a8',
                  borderRadius: 6,
                  padding: '6px 16px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openMarketForm(machineUuid, raw)}
            style={{
              background: 'rgba(201,168,76,0.1)',
              border: '1px solid rgba(201,168,76,0.3)',
              color: '#C9A84C',
              borderRadius: 6,
              padding: '5px 12px',
              fontSize: 11,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t('marketSubmitRate')}
          </button>
        )}
      </div>
    );
  };

  const loadComplianceForMachine = useCallback(async (machineId) => {
    if (!machineId) return;
    try {
      const data = await secureFetch(`/api/owner/machines/${machineId}/compliance`);
      const row = data.compliance
        ? {
          insurance_start: data.compliance.insurance_start,
          insurance_expiry: data.compliance.insurance_expiry,
          rc_start: data.compliance.rc_start,
          rc_expiry: data.compliance.rc_expiry,
          puc_start: data.compliance.puc_start,
          puc_expiry: data.compliance.puc_expiry,
        }
        : {
          insurance_start: null,
          insurance_expiry: null,
          rc_start: null,
          rc_expiry: null,
          puc_start: null,
          puc_expiry: null,
        };
      setComplianceData((prev) => ({ ...prev, [machineId]: row }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      /* ignore */
    }
  }, []);

  const loadRecoveryForMachine = useCallback(async (machineId) => {
    if (!machineId) return;
    setRecoveryLoading((prev) => ({ ...prev, [machineId]: true }));
    try {
      const data = await secureFetch(`/api/owner/machines/${machineId}/recovery`);
      setRecoveryData((prev) => ({ ...prev, [machineId]: data }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      /* ignore */
    } finally {
      setRecoveryLoading((prev) => ({ ...prev, [machineId]: false }));
    }
  }, []);

  const loadDieselLatestForMachine = useCallback(async (machineId) => {
    if (!machineId) return;
    setDieselLoading((prev) => ({ ...prev, [machineId]: true }));
    try {
      const data = await secureFetch(
        `/api/owner/diesel-prices/latest?machine_id=${encodeURIComponent(machineId)}`,
      );
      setDieselLatest((prev) => ({ ...prev, [machineId]: data.latest ?? null }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      /* ignore */
    } finally {
      setDieselLoading((prev) => ({ ...prev, [machineId]: false }));
    }
  }, []);

  const loadDieselHistoryForMachine = useCallback(async (machineId) => {
    if (!machineId) return;
    setDieselHistoryLoading((prev) => ({ ...prev, [machineId]: true }));
    try {
      const data = await secureFetch(
        `/api/owner/diesel-prices?machine_id=${encodeURIComponent(machineId)}&limit=10`,
      );
      setDieselPriceHistory((prev) => ({ ...prev, [machineId]: data.prices ?? [] }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setDieselPriceHistory((prev) => ({ ...prev, [machineId]: [] }));
    } finally {
      setDieselHistoryLoading((prev) => ({ ...prev, [machineId]: false }));
    }
  }, []);

  const toggleDieselHistory = async (machineUuid) => {
    if (dieselHistoryOpen[machineUuid]) {
      setDieselHistoryOpen((prev) => ({ ...prev, [machineUuid]: false }));
      return;
    }
    setDieselHistoryOpen((prev) => ({ ...prev, [machineUuid]: true }));
    await loadDieselHistoryForMachine(machineUuid);
  };

  const loadRateSuggestionForMachine = useCallback(async (machineId) => {
    if (!machineId) return;
    setDieselRateSuggestionLoading((prev) => ({ ...prev, [machineId]: true }));
    try {
      const data = await secureFetch(
        `/api/owner/machines/${machineId}/rate-suggestion`,
      );
      setDieselRateSuggestion((prev) => ({ ...prev, [machineId]: data }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setDieselRateSuggestion((prev) => ({ ...prev, [machineId]: { suggestion_available: false } }));
    } finally {
      setDieselRateSuggestionLoading((prev) => ({ ...prev, [machineId]: false }));
    }
  }, []);

  const handleApplyRateSuggestion = async (machineUuid, suggestedRate) => {
    const rate = Number(suggestedRate);
    if (!machineUuid || !Number.isFinite(rate) || rate <= 0) return;
    setDieselRateApplying((prev) => ({ ...prev, [machineUuid]: true }));
    try {
      await secureFetch(
        `/api/owner/machines/${machineUuid}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ rate_per_hour: rate }),
        },
      );
      alert(t('rateSuggestionApplied'));
      const machines = await getMachines();
      const ownerId = sessionUser?.id || sessionUser?.user_id;
      setMachineData(
        Array.isArray(machines)
          ? machines.filter((m) => !ownerId || m.owner_id === ownerId)
          : [],
      );
      await loadRateSuggestionForMachine(machineUuid);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      alert(err?.message || t('genericError'));
    } finally {
      setDieselRateApplying((prev) => ({ ...prev, [machineUuid]: false }));
    }
  };

  const openDieselForm = (machineUuid) => {
    setDieselFormOpen(machineUuid);
    setDieselExtractMessage(null);
    setDieselForm({
      date: new Date().toISOString().slice(0, 10),
      price_per_litre: '',
      quantity_litres: '',
      city: '',
    });
  };

  const handleDieselReceiptUpload = async (machineUuid, file) => {
    if (!file || !machineUuid) return;
    setDieselReceiptUploading(true);
    setDieselExtractMessage(null);
    try {
      const formData = new FormData();
      formData.append('receipt', file);
      formData.append('machine_id', machineUuid);
      if (dieselForm.date) {
        formData.append('date', dieselForm.date);
      }
      const data = await secureFetch('/api/owner/diesel-prices/upload-receipt', {
        method: 'POST',
        body: formData,
      });
      const extracted = data.extracted || {};
      setDieselForm((prev) => ({
        ...prev,
        date: extracted.date || prev.date,
        price_per_litre: extracted.price_per_litre ?? prev.price_per_litre ?? '',
        quantity_litres: extracted.quantity_litres ?? prev.quantity_litres ?? '',
        city: extracted.location || prev.city || '',
      }));
      setDieselExtractMessage({
        machineUuid,
        type: 'success',
        extracted,
      });
      await loadDieselLatestForMachine(machineUuid);
      await loadRateSuggestionForMachine(machineUuid);
      if (dieselHistoryOpen[machineUuid]) {
        await loadDieselHistoryForMachine(machineUuid);
      }
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setDieselExtractMessage({ machineUuid, type: 'error' });
    } finally {
      setDieselReceiptUploading(false);
    }
  };

  const handleSaveDieselPrice = async (machineUuid) => {
    setDieselSaving(true);
    try {
      const pricePerLitre = Number(dieselForm.price_per_litre);
      if (!Number.isFinite(pricePerLitre) || pricePerLitre <= 0) {
        alert(t('dieselPricePerLitre'));
        return;
      }
      if (!dieselForm.date) {
        alert(t('date'));
        return;
      }
      const body = {
        price_per_litre: pricePerLitre,
        date: dieselForm.date,
        machine_id: machineUuid,
        source: 'manual',
      };
      if (dieselForm.quantity_litres !== '' && dieselForm.quantity_litres != null) {
        body.quantity_litres = Number(dieselForm.quantity_litres);
      }
      if (dieselForm.city != null && String(dieselForm.city).trim()) {
        body.city = String(dieselForm.city).trim();
      }
      await secureFetch('/api/owner/diesel-prices', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      await loadDieselLatestForMachine(machineUuid);
      await loadRateSuggestionForMachine(machineUuid);
      await loadMarketPositionForMachine(machineUuid);
      if (dieselHistoryOpen[machineUuid]) {
        await loadDieselHistoryForMachine(machineUuid);
      }
      setDieselFormOpen(null);
      setDieselForm({});
      alert(t('dieselPriceSaved'));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      alert(err?.message || t('genericError'));
    } finally {
      setDieselSaving(false);
    }
  };

  const loadMarketPositionForMachine = useCallback(async (machineId) => {
    if (!machineId) return;
    setMarketPositionLoading((prev) => ({ ...prev, [machineId]: true }));
    try {
      const data = await secureFetch(
        `/api/owner/machines/${machineId}/market-position`,
      );
      setMarketPosition((prev) => ({ ...prev, [machineId]: data }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setMarketPosition((prev) => ({ ...prev, [machineId]: { market_available: false } }));
    } finally {
      setMarketPositionLoading((prev) => ({ ...prev, [machineId]: false }));
    }
  }, []);

  const openMarketForm = (machineUuid, raw) => {
    const city = marketPosition[machineUuid]?.city
      || dieselLatest[machineUuid]?.city
      || '';
    setMarketFormOpen(machineUuid);
    setMarketForm({
      city: city ? String(city).trim() : '',
      min_rate_per_hour: '',
      max_rate_per_hour: '',
    });
  };

  const handleSubmitMarketRate = async (machineUuid, raw) => {
    const machineType = raw?.type ? String(raw.type).trim() : '';
    if (!machineType) {
      alert(t('genericError'));
      return;
    }
    const city = marketForm.city != null ? String(marketForm.city).trim() : '';
    if (!city) {
      alert(t('marketCity'));
      return;
    }
    const minRate = Number(marketForm.min_rate_per_hour);
    const maxRate = Number(marketForm.max_rate_per_hour);
    if (!Number.isFinite(minRate) || minRate <= 0) {
      alert(t('marketMinRate'));
      return;
    }
    if (!Number.isFinite(maxRate) || maxRate <= 0) {
      alert(t('marketMaxRate'));
      return;
    }
    if (maxRate <= minRate) {
      alert(t('marketMaxRate'));
      return;
    }
    setMarketSaving(true);
    try {
      await secureFetch('/api/owner/market-rates', {
        method: 'POST',
        body: JSON.stringify({
          machine_type: machineType,
          city,
          min_rate_per_hour: minRate,
          max_rate_per_hour: maxRate,
        }),
      });
      setMarketFormOpen(null);
      setMarketForm({});
      setMarketRatePendingReview((prev) => ({ ...prev, [machineUuid]: true }));
      await loadMarketPositionForMachine(machineUuid);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      alert(err?.message || t('genericError'));
    } finally {
      setMarketSaving(false);
    }
  };

  const handleSaveCompliance = async (machineUuid, rtoApplicable) => {
    setComplianceSaving(true);
    try {
      const body = {
        insurance_start: complianceForm.insurance_start || null,
        insurance_expiry: complianceForm.insurance_expiry || null,
      };
      if (rtoApplicable) {
        body.rc_start = complianceForm.rc_start || null;
        body.rc_expiry = complianceForm.rc_expiry || null;
        body.puc_start = complianceForm.puc_start || null;
        body.puc_expiry = complianceForm.puc_expiry || null;
      }
      await secureFetch(
        `/api/owner/machines/${machineUuid}/compliance`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
        },
      );
      await loadComplianceForMachine(machineUuid);
      setEditingComplianceId(null);
      setComplianceForm({});
      setComplianceExtracting({});
      setComplianceExtractMessage({});
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      alert(err?.message || t('machineUpdateFailed'));
    } finally {
      setComplianceSaving(false);
    }
  };

  const handleComplianceExtract = async (machineUuid, field, file) => {
    if (!file) return;
    const fieldKey = field.endKey;
    setComplianceExtracting((prev) => ({ ...prev, [fieldKey]: true }));
    setComplianceExtractMessage((prev) => ({ ...prev, [fieldKey]: '' }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('docType', field.docType);
      const data = await secureFetch(
        `/api/owner/machines/${machineUuid}/compliance/extract`,
        {
          method: 'POST',
          body: formData,
        },
      );
      setComplianceForm((prev) => ({
        ...prev,
        [field.startKey]: data.start_date || '',
        [field.endKey]: data.end_date || '',
      }));
      setComplianceExtractMessage((prev) => ({ ...prev, [fieldKey]: 'success' }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setComplianceExtractMessage((prev) => ({ ...prev, [fieldKey]: 'error' }));
    } finally {
      setComplianceExtracting((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  useEffect(() => {
    if (!machineData.length) {
      setComplianceData({});
      return;
    }
    const ids = machineData.map((m) => m.id).filter(Boolean);
    if (ids.length === 0) return;
    Promise.all(ids.map((id) => loadComplianceForMachine(id)));
  }, [machineData, loadComplianceForMachine]);

  useEffect(() => {
    if (activeTab !== 'edit-registration') {
      setEditRegSelectedId(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'machines' && activeTab !== 'edit-registration'
      && activeTab !== 'recovery') return;
    if (!machineData.length) {
      setRecoveryData({});
      return;
    }
    const ids = machineData.map((m) => m.id).filter(Boolean);
    if (ids.length === 0) return;
    Promise.all(ids.map((id) => loadRecoveryForMachine(id)));
  }, [activeTab, machineData, loadRecoveryForMachine]);

  useEffect(() => {
    if (activeTab !== 'machines' && activeTab !== 'edit-registration') return;
    if (!machineData.length) {
      setDieselLatest({});
      setDieselRateSuggestion({});
      return;
    }
    const ids = machineData.map((m) => m.id).filter(Boolean);
    if (ids.length === 0) return;
    Promise.all(ids.flatMap((id) => [
      loadDieselLatestForMachine(id),
      loadRateSuggestionForMachine(id),
    ]));
  }, [activeTab, machineData, loadDieselLatestForMachine, loadRateSuggestionForMachine]);

  useEffect(() => {
    if (activeTab !== 'machines' && activeTab !== 'edit-registration') return;
    if (!machineData.length) {
      setMarketPosition({});
      return;
    }
    const ids = machineData.map((m) => m.id).filter(Boolean);
    if (ids.length === 0) return;
    Promise.all(ids.map((id) => loadMarketPositionForMachine(id)));
  }, [activeTab, machineData, loadMarketPositionForMachine]);

  const loadServiceLogs = useCallback(async (machineId) => {
    if (!machineId) return;
    setServiceLoading((prev) => ({ ...prev, [machineId]: true }));
    try {
      const data = await secureFetch(`/api/owner/machines/${machineId}/service-logs`);
      setServiceLogs((prev) => ({ ...prev, [machineId]: data.logs || [] }));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      /* ignore */
    } finally {
      setServiceLoading((prev) => ({ ...prev, [machineId]: false }));
    }
  }, []);

  useEffect(() => {
    if (!machineData.length) {
      setServiceLogs({});
      return;
    }
    const ids = machineData.map((m) => m.id).filter(Boolean);
    if (ids.length === 0) return;
    Promise.all(ids.map((id) => loadServiceLogs(id)));
  }, [machineData, loadServiceLogs]);

  const emptyAvailabilityForm = { start_date: '', end_date: '', reason: '' };

  const loadAvailabilityBlocksForMachine = useCallback(async (machineUuid) => {
    if (!machineUuid) return;
    setAvailabilityLoading((prev) => ({ ...prev, [machineUuid]: true }));
    const result = await getAvailabilityBlocks(machineUuid);
    if (result.error === 'auth_required') {
      setAvailabilityLoading((prev) => ({ ...prev, [machineUuid]: false }));
      return;
    }
    setAvailabilityBlocks((prev) => ({ ...prev, [machineUuid]: result.blocks || [] }));
    setAvailabilityError((prev) => ({ ...prev, [machineUuid]: result.error || '' }));
    setAvailabilityLoading((prev) => ({ ...prev, [machineUuid]: false }));
  }, []);

  const toggleAvailabilitySection = (machineUuid) => {
    if (!machineUuid) return;
    const nextOpen = !availabilityOpen[machineUuid];
    setAvailabilityOpen((prev) => ({ ...prev, [machineUuid]: nextOpen }));
    if (nextOpen) {
      setAvailabilityForm((formPrev) => (
        formPrev[machineUuid] ? formPrev : { ...formPrev, [machineUuid]: { ...emptyAvailabilityForm } }
      ));
      loadAvailabilityBlocksForMachine(machineUuid);
    }
  };

  const handleAvailabilityFormChange = (machineUuid, field, value) => {
    setAvailabilityForm((prev) => ({
      ...prev,
      [machineUuid]: { ...(prev[machineUuid] || emptyAvailabilityForm), [field]: value },
    }));
  };

  const handleCreateAvailabilityBlock = async (machineUuid) => {
    if (!machineUuid || availabilitySaving[machineUuid]) return;
    const form = availabilityForm[machineUuid] || emptyAvailabilityForm;
    const startDate = String(form.start_date || '').trim();
    const endDate = String(form.end_date || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      setAvailabilityError((prev) => ({
        ...prev,
        [machineUuid]: 'Start date and end date are required',
      }));
      return;
    }
    if (endDate < startDate) {
      setAvailabilityError((prev) => ({
        ...prev,
        [machineUuid]: 'End date must be on or after start date',
      }));
      return;
    }
    setAvailabilitySaving((prev) => ({ ...prev, [machineUuid]: true }));
    setAvailabilityError((prev) => ({ ...prev, [machineUuid]: '' }));
    const result = await createAvailabilityBlock(machineUuid, {
      start_date: startDate,
      end_date: endDate,
      reason: form.reason,
    });
    setAvailabilitySaving((prev) => ({ ...prev, [machineUuid]: false }));
    if (!result.ok) {
      setAvailabilityError((prev) => ({
        ...prev,
        [machineUuid]: result.error || 'Failed to create availability block',
      }));
      return;
    }
    setAvailabilityForm((prev) => ({ ...prev, [machineUuid]: { ...emptyAvailabilityForm } }));
    await loadAvailabilityBlocksForMachine(machineUuid);
  };

  const handleDeleteAvailabilityBlock = async (machineUuid, blockId) => {
    if (!machineUuid || !blockId || availabilityDeleting[machineUuid]) return;
    if (!window.confirm('Remove this blocked date range?')) return;
    setAvailabilityDeleting((prev) => ({ ...prev, [machineUuid]: blockId }));
    setAvailabilityError((prev) => ({ ...prev, [machineUuid]: '' }));
    const result = await deleteAvailabilityBlock(machineUuid, blockId);
    setAvailabilityDeleting((prev) => ({ ...prev, [machineUuid]: null }));
    if (!result.ok) {
      setAvailabilityError((prev) => ({
        ...prev,
        [machineUuid]: result.error || 'Failed to delete availability block',
      }));
      return;
    }
    await loadAvailabilityBlocksForMachine(machineUuid);
  };

  const renderAvailabilitySection = (machineUuid) => {
    if (!machineUuid) return null;
    const open = Boolean(availabilityOpen[machineUuid]);
    const loading = Boolean(availabilityLoading[machineUuid]);
    const saving = Boolean(availabilitySaving[machineUuid]);
    const deletingId = availabilityDeleting[machineUuid] || null;
    const err = availabilityError[machineUuid] || '';
    const blocks = availabilityBlocks[machineUuid] || [];
    const form = availabilityForm[machineUuid] || emptyAvailabilityForm;
    return (
      <div style={{
        marginTop: 12,
        padding: 12,
        background: 'rgba(255,255,255,0.03)',
        borderRadius: 8,
        border: '1px solid rgba(201,168,76,0.2)',
      }}>
        <button
          type="button"
          onClick={() => toggleAvailabilitySection(machineUuid)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            color: '#C9A84C',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {open ? '▼' : '▶'} Availability
        </button>
        {open ? (
          <div style={{ marginTop: 10 }}>
            {loading ? (
              <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 8px' }}>{t('loading')}</p>
            ) : blocks.length === 0 ? (
              <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 8px' }}>No blocked dates</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                {blocks.map((block) => {
                  const blockId = block.id;
                  const deleting = deletingId === blockId;
                  const reason = String(block.reason || '').trim();
                  return (
                    <div
                      key={blockId || `${block.start_date}-${block.end_date}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        background: 'rgba(0,0,0,0.25)',
                        border: '1px solid rgba(201,168,76,0.15)',
                        borderRadius: 8,
                        padding: '8px 10px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <p style={{ color: '#e8e0d0', fontSize: 12, fontWeight: 600, margin: 0 }}>
                          {formatAvailabilityDate(block.start_date)}
                          {' → '}
                          {formatAvailabilityDate(block.end_date)}
                        </p>
                        <p style={{ color: '#8896a8', fontSize: 11, margin: '2px 0 0' }}>
                          {reason || 'No reason'}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={deleting || saving}
                        onClick={() => handleDeleteAvailabilityBlock(machineUuid, blockId)}
                        style={{
                          background: 'rgba(233,69,96,0.12)',
                          border: '1px solid rgba(233,69,96,0.4)',
                          color: '#e94560',
                          borderRadius: 6,
                          padding: '5px 10px',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: deleting ? 'wait' : 'pointer',
                          opacity: deleting ? 0.6 : 1,
                        }}
                      >
                        {deleting ? t('loading') : 'Delete'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {err ? (
              <p style={{ color: '#e94560', fontSize: 11, margin: '0 0 8px' }}>{err}</p>
            ) : null}
            <div style={{
              display: 'grid',
              gridTemplateColumns: gridCols(1, 2, 2),
              gap: 8,
              marginBottom: 8,
            }}>
              <div>
                <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                  Start date
                </label>
                <input
                  type="date"
                  className="de-owner-input"
                  value={form.start_date || ''}
                  onChange={(e) => handleAvailabilityFormChange(machineUuid, 'start_date', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                  End date
                </label>
                <input
                  type="date"
                  className="de-owner-input"
                  value={form.end_date || ''}
                  onChange={(e) => handleAvailabilityFormChange(machineUuid, 'end_date', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
              Reason (optional)
            </label>
            <input
              type="text"
              className="de-owner-input"
              maxLength={500}
              placeholder="Maintenance, personal use…"
              value={form.reason || ''}
              onChange={(e) => handleAvailabilityFormChange(machineUuid, 'reason', e.target.value)}
              style={{ width: '100%', marginBottom: 8 }}
            />
            <button
              type="button"
              disabled={saving || loading}
              onClick={() => handleCreateAvailabilityBlock(machineUuid)}
              style={{
                background: saving ? 'rgba(201,168,76,0.25)' : '#C9A84C',
                border: '1px solid #C9A84C',
                color: saving ? '#8896a8' : '#030810',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 12,
                fontWeight: 700,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              {saving ? t('loading') : 'Block Dates'}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const handleAddServiceLog = async (machineUuid) => {
    setServiceSaving(true);
    try {
      const body = {};
      Object.entries(serviceFormData).forEach(([key, value]) => {
        if (value === '' || value == null) return;
        if (key === 'hmr_at_service' || key === 'next_service_hmr') {
          const n = Number(value);
          if (Number.isFinite(n)) body[key] = n;
          return;
        }
        body[key] = value;
      });
      await secureFetch(
        `/api/owner/machines/${machineUuid}/service-logs`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      );
      await loadServiceLogs(machineUuid);
      setServiceFormOpen(null);
      setServiceFormData({ ...EMPTY_SERVICE_FORM });
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      alert(err?.message || t('machineUpdateFailed'));
    } finally {
      setServiceSaving(false);
    }
  };

  const handleUnassignMachine = async (machineId) => {
    setAssignError('');
    try {
      const data = await secureFetch(
        `/api/owner/machines/${machineId}/assign-operator`,
        {
          method: 'PATCH',
          body: JSON.stringify({ operatorId: null }),
        },
      );
      if (data.success) {
        await fetchOperators();
        const machines = await getMachines();
        const ownerId = sessionUser?.id || sessionUser?.user_id;
        setMachineData(
          Array.isArray(machines)
            ? machines.filter((m) => !ownerId || m.owner_id === ownerId)
            : [],
        );
      } else {
        setAssignError(data.error || t('unassignOperatorFailed'));
      }
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      setAssignError(e?.message || t('genericError'));
    }
  };

  useEffect(() => {
    if (activeTab === 'operators') {
      fetchOperators();
    }
  }, [activeTab]);

  const handleOpSubmit = async () => {
    if (!opData.name.trim() || opData.name.trim().length < 2) {
      setOpError('Name required (min 2 characters)');
      return;
    }
    const phoneClean = opData.phone.replace(/\D/g, '');
    if (phoneClean.length !== 10) {
      setOpError('Valid 10-digit mobile number required');
      return;
    }
    if (opData.password.length < 8) {
      setOpError('Password min 8 characters');
      return;
    }
    if (!opFiles.aadhaarFront || !opFiles.aadhaarBack) {
      setOpError('Aadhaar front and back documents required');
      return;
    }

    setOpSubmitting(true);
    setOpError('');

    try {
      let photoPath = '';
      if (opFiles.photo) {
        const fd = new FormData();
        fd.append('photo', opFiles.photo);
        fd.append('phone', phoneClean);
        const d = await secureFetch('/api/owner/operator/upload-photo', { method: 'POST', body: fd });
        photoPath = d.path || '';
      }

      let licensePath = '';
      if (opFiles.license) {
        const fdL = new FormData();
        fdL.append('license', opFiles.license);
        fdL.append('phone', phoneClean);
        const dL = await secureFetch('/api/owner/operator/upload-license', { method: 'POST', body: fdL });
        licensePath = dL.path || '';
      }

      const fdAF = new FormData();
      fdAF.append('aadhaarFront', opFiles.aadhaarFront);
      fdAF.append('phone', phoneClean);
      const dAF = await secureFetch('/api/owner/operator/upload-aadhaar-front', { method: 'POST', body: fdAF });

      const fdAB = new FormData();
      fdAB.append('aadhaarBack', opFiles.aadhaarBack);
      fdAB.append('phone', phoneClean);
      const dAB = await secureFetch('/api/owner/operator/upload-aadhaar-back', { method: 'POST', body: fdAB });

      let policePath = '';
      if (opFiles.police) {
        const fdP = new FormData();
        fdP.append('policeVerification', opFiles.police);
        fdP.append('phone', phoneClean);
        const dP = await secureFetch('/api/owner/operator/upload-police-verification', { method: 'POST', body: fdP });
        policePath = dP.path || '';
      }

      await secureFetch(
        '/api/owner/operators/create',
        {
          method: 'POST',
          body: JSON.stringify({
            name: opData.name.trim(),
            phone: phoneClean,
            password: opData.password,
            profilePhotoPath: photoPath,
            licenseDocumentPath: licensePath,
            aadhaarFrontPath: dAF.path,
            aadhaarBackPath: dAB.path,
            policeVerificationPath: policePath,
            experienceYears: Number(opData.experienceYears),
            experienceMachineTypes: opData.experienceMachineTypes,
            monthlySalary: Number(opData.monthlySalary),
          }),
        },
      );

      const createdName = opData.name.trim();
      resetOpForm();
      setOpSuccess(`Operator "${createdName}" created — pending admin approval`);
      setShowAddForm(false);
      fetchOperators();
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setOpError(err.message || 'Something went wrong');
    } finally {
      setOpSubmitting(false);
    }
  };

  const handleRegSubmit = async () => {
    const err = validateRegistrationStep(5, regData, regDocuments, t);
    if (err) {
      setRegError(err);
      return;
    }
    if (!/^\d{6}$/.test(String(regData.pincode || '').trim())) {
      setRegError('Machine base location pincode must be exactly 6 digits.');
      return;
    }
    setRegSubmitting(true);
    setRegError('');
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(regData));
      Object.entries(regDocuments).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });
      const result = await submitMachineRegistration(formData);
      setRegSuccess(result.message || t('machineRegistrationSubmitted'));
      resetRegistrationForm();
      const regs = await getOwnerMachineRegistrations();
      setPendingRegistrations(regs.items || []);
    } catch (submitErr) {
      setRegError(submitErr?.message || t('regFailed'));
    } finally {
      setRegSubmitting(false);
    }
  };

  const loadMoreOwnerBookings = async () => {
    if (!ownerBookingsHasMore || ownerBookingsLoadingMore) return;
    setOwnerBookingsLoadingMore(true);
    const result = await getOwnerBookingsPage({ limit: 100, offset: ownerBookingsOffset });
    if ((result.items || []).length > 0) {
      setOwnerBookings((prev) => appendUniqueById(prev, result.items));
      setOwnerBookingsOffset(result.nextOffset ?? ownerBookingsOffset + result.items.length);
      setOwnerBookingsHasMore(Boolean(result.hasMore));
    } else {
      setOwnerBookingsHasMore(false);
    }
    setOwnerBookingsLoadingMore(false);
  };

  const refreshOwnerBookings = async () => {
    const result = await getOwnerBookingsPage({ limit: 100, offset: 0 });
    setOwnerBookings(Array.isArray(result.items) ? result.items : []);
    setOwnerBookingsHasMore(Boolean(result.hasMore));
    setOwnerBookingsOffset(result.nextOffset ?? (result.items?.length || 0));
  };

  const refreshPendingTerminations = useCallback(async (bookings) => {
    const contracts = (bookings || []).filter((b) => b.booking_type === 'contract' && b.id);
    if (contracts.length === 0) {
      setPendingTerminations({});
      return;
    }
    const entries = await Promise.all(contracts.map(async (b) => {
      try {
        const data = await secureFetch(`/api/owner/terminations?bookingId=${encodeURIComponent(b.id)}`);
        return [b.id, data?.termination || null];
      } catch (_err) {
        return [b.id, null];
      }
    }));
    const map = {};
    entries.forEach(([id, term]) => {
      if (term) map[id] = term;
    });
    setPendingTerminations(map);
  }, []);

  useEffect(() => {
    refreshPendingTerminations(ownerBookings);
  }, [ownerBookings, refreshPendingTerminations]);

  const estimateOwnerTerminationFee = (booking, type) => {
    if (type === 'mutual' || type === 'force_majeure') {
      return { remaining: 0, feeCycles: 0, gross: 0, gst: 0, net: 0 };
    }
    const start = new Date(`${String(booking.start_date || '').slice(0, 10)}T00:00:00`);
    const end = new Date(`${String(booking.end_date || '').slice(0, 10)}T00:00:00`);
    let remaining = 0;
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end >= start) {
      const months = String(booking.billing_cycle || '').toLowerCase() === 'quarterly' ? 3 : 1;
      remaining = 1;
      const cursor = new Date(start);
      while (remaining < 120) {
        cursor.setMonth(cursor.getMonth() + months);
        if (cursor >= end) break;
        remaining += 1;
      }
    }
    const feeCycles = Math.min(remaining, 3);
    const rate = Number(booking.fixed_rate_per_cycle || 0);
    const gross = Math.round(feeCycles * rate * 0.25 * 100) / 100;
    const taxQuote = computeBookingTaxQuote(gross, {
      recipientGstin: booking.client_gstin,
      siteStateCode: booking.site_state_code,
    });
    const gst = taxQuote.gst;
    const net = Math.round((gross + gst) * 100) / 100;
    return { remaining, feeCycles, gross, gst, net, cgst: taxQuote.cgst, sgst: taxQuote.sgst, igst: taxQuote.igst, interState: taxQuote.interState };
  };

  const handleOwnerInitiateTermination = async () => {
    if (!terminationModal || terminationSubmitting) return;
    if (terminationType === 'force_majeure' && !String(forceMajeureReason || '').trim()) {
      setTerminationError('Enter force majeure reason');
      return;
    }
    setTerminationSubmitting(true);
    setTerminationError('');
    try {
      const result = await secureFetch('/api/owner/terminations/initiate', {
        method: 'POST',
        body: JSON.stringify({
          bookingId: terminationModal.id,
          terminationType,
          forceMajeureReason: terminationType === 'force_majeure' ? forceMajeureReason : null,
        }),
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Termination request failed');
      }
      setPendingTerminations((prev) => ({
        ...prev,
        [terminationModal.id]: result.termination || { status: 'pending' },
      }));
      setTerminationModal(null);
      setForceMajeureReason('');
    } catch (err) {
      setTerminationError(err?.message || 'Termination request failed');
    } finally {
      setTerminationSubmitting(false);
    }
  };

  const hasLiveOwnerBooking = useMemo(
    () => ownerBookings.some(bookingNeedsLiveSync),
    [ownerBookings],
  );

  useEffect(() => {
    if (!hasLiveOwnerBooking) return undefined;
    const id = setInterval(() => {
      refreshOwnerBookings();
    }, 5000);
    return () => clearInterval(id);
  }, [hasLiveOwnerBooking]);

  function fuelDisplay(fuelPct, capacityLitres) {
    if (capacityLitres > 0) {
      const litres = Math.round(
        (fuelPct / 100) * capacityLitres,
      );
      return `${litres}L / ${capacityLitres}L`;
    }
    return `${fuelPct}%`;
  }

  const displayMachines = machineData.map((m) => {
    const activeBooking = (ownerBookings || []).find((b) =>
      b.machine_id === m.id
      && ['Dispatched', 'Extension_Pending', 'Completing'].includes(b.status),
    ) || null;
    return {
      id: m.machine_id, name: m.name, type: m.type, regNo: m.reg_no, year: m.year,
      status: m.status || 'Idle', client: 'N/A',
      rtoApplicable: m.reg_no !== 'TRACKED-NO-RTO',
      fuel: Number(m.fuel_level ?? 0),
      fuelCapacity: Number(m.fuel_capacity_litres || 0),
      hmr: activeBooking
        ? runningElapsedMs(activeBooking) / 3600000
        : 0,
      activeBooking,
      operator: m.operator || null,
    };
  });

  const totalMachineCount = displayMachines.length;
  const activeMachineCount = displayMachines.filter((m) => String(m.status).toLowerCase() === 'active').length;

  const monthNetByMachineUuid = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const mo = now.getMonth();
    const map = {};
    for (const b of ownerBookings || []) {
      const d = new Date(b.created_at);
      if (Number.isNaN(d.getTime()) || d.getFullYear() !== y || d.getMonth() !== mo) continue;
      const mid = b.machine_id;
      if (!mid) continue;
      map[mid] = (map[mid] || 0) + (Number(b.net_owner_amount) || 0);
    }
    return map;
  }, [ownerBookings]);

  const machinesMissingPurchasePrice = useMemo(
    () => machineData.filter((m) => {
      const price = Number(m.purchase_price);
      return m.purchase_price == null || m.purchase_price === ''
        || !Number.isFinite(price) || price <= 0;
    }),
    [machineData],
  );
  const missingPurchaseCount = machinesMissingPurchasePrice.length;

  const scrollToFirstMissingPurchase = () => {
    const targetId = machinesMissingPurchasePrice[0]?.id;
    if (!targetId) return;
    document.getElementById(`owner-machine-${targetId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const ownerKycRegistrationBlocked = ownerKycStatus === 'rejected';

  const renderOwnerKycBanner = () => {
    if (!ownerKycStatus || ownerKycStatus === 'approved') return null;
    let bannerStyle;
    let message;
    let showKycLink = false;
    if (ownerKycStatus === 'rejected') {
      bannerStyle = {
        background: 'rgba(233,69,96,0.12)',
        border: '1px solid rgba(233,69,96,0.35)',
        color: '#e94560',
      };
      message = t('kycRejectedWarning');
      showKycLink = true;
    } else if (ownerKycStatus === 'not_submitted') {
      bannerStyle = {
        background: 'rgba(255,152,0,0.12)',
        border: '1px solid rgba(255,152,0,0.35)',
        color: '#FF9800',
      };
      message = t('kycNotSubmittedHint');
      showKycLink = true;
    } else if (ownerKycStatus === 'pending') {
      bannerStyle = {
        background: 'rgba(33,150,243,0.12)',
        border: '1px solid rgba(33,150,243,0.35)',
        color: '#2196F3',
      };
      message = t('kycPendingHint');
    } else {
      return null;
    }
    return (
      <div
        style={{
          ...bannerStyle,
          borderRadius: '10px',
          padding: '10px 12px',
          marginBottom: '15px',
          fontSize: '12px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <p style={{ margin: 0, flex: '1 1 200px' }}>{message}</p>
        {showKycLink ? (
          <button
            type="button"
            onClick={() => setActiveTab('kyc')}
            style={{
              background: 'transparent',
              border: `1px solid ${bannerStyle.color}`,
              color: bannerStyle.color,
              borderRadius: '6px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            {t('kycGoToTab')} →
          </button>
        ) : null}
      </div>
    );
  };

  const handleLogout = () => {
    logoutAndRedirect(navigate);
  };

  const localAlerts = [];
  const localAlertTime = new Date().toISOString();
  if (ownerKycStatus !== 'approved' && ownerKycStatus !== 'rejected') {
    localAlerts.push({
      id: 'local_kyc',
      type: 'local_kyc',
      icon: '🪪',
      msg: ownerKycStatus === 'pending' ? t('kycPendingHint') : t('kycNotSubmittedHint'),
      time: localAlertTime,
      color: '#FF9800',
      link: 'kyc',
    });
  }
  const pushEnabled = readSessionUser()?.pushNotificationsEnabled;
  if (!pushEnabled) {
    localAlerts.push({
      id: 'local_push',
      type: 'local_push',
      icon: '🔔',
      msg: t('pushEnablePrompt'),
      time: localAlertTime,
      color: '#FF9800',
      link: 'alerts',
    });
  }
  const allAlerts = [...localAlerts, ...alerts];

  const navItemsWithBadge = navItems.map((item) => ({
    ...item,
    ...(item.id === 'account-hub' && allAlerts.length > 0
      ? { badge: allAlerts.length > 99 ? '99+' : allAlerts.length }
      : {}),
  }));

  const renderNavBadge = (badge) => {
    if (!badge) return null;
    return (
      <span
        style={{
          marginLeft: 'auto',
          minWidth: '18px',
          height: '18px',
          padding: '0 5px',
          borderRadius: '999px',
          background: '#e94560',
          color: '#fff',
          fontSize: '10px',
          fontWeight: 800,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        {badge}
      </span>
    );
  };

  const ownerHubCardBtn = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    minHeight: 56,
    padding: '16px 18px',
    borderRadius: 12,
    border: '1px solid rgba(201,168,76,0.28)',
    background: 'linear-gradient(135deg, #0f2040, #030810)',
    boxSizing: 'border-box',
  };

  const renderOwnerHubCards = (cards) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '20px' }}>
      {cards.filter((item) => !item.hidden).map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => setActiveTab(item.id)}
          style={ownerHubCardBtn}
        >
          <span style={{ fontSize: 22, lineHeight: 1, color: '#C9A84C' }}>{item.icon}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', color: '#e8e0d0', fontWeight: 700, fontSize: 14 }}>
              {(item.label && t(item.i18nKey) === item.i18nKey) ? item.label : t(item.i18nKey)}
            </span>
            <span style={{ display: 'block', fontSize: 12, color: '#8896a8', marginTop: 2 }}>{t(item.subtitleKey)}</span>
          </span>
          {item.badge ? renderNavBadge(item.badge) : null}
          <span style={{ color: '#C9A84C', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>→</span>
        </button>
      ))}
    </div>
  );

  const renderOwnerQuotationReview = () => {
    const reviewable = (pendingQuotations || []).filter((q) => ['owner_review', 'quoted'].includes(String(q?.status || '')));
    if (reviewable.length === 0) {
      return (
        <div id="owner-quotation-review" style={{
          background: 'rgba(201,168,76,0.04)',
          border: '1px solid rgba(201,168,76,0.18)',
          borderRadius: 12,
          padding: '14px 16px',
        }}
        >
          <p style={{ color: '#8896a8', fontSize: 12, margin: 0 }}>
            No pending quotations.
          </p>
        </div>
      );
    }
    const labelStyle = { color: '#8896a8', fontSize: 9, margin: '0 0 3px' };
    const valueStyle = { color: '#e8e0d0', fontSize: 12, fontWeight: 700, margin: 0 };
    const sectionTitleStyle = { color: '#c9a84c', fontSize: 11, fontWeight: 800, margin: '0 0 8px' };
    const sectionBoxStyle = {
      background: 'rgba(201,168,76,0.06)',
      border: '1px solid rgba(201,168,76,0.22)',
      borderRadius: 8,
      padding: 10,
      marginBottom: 8,
    };
    const gridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, minmax(0, 1fr))', gap: 8 };
    return (
      <div id="owner-quotation-review" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {reviewable.map((q) => {
          const busy = quotationActionId === q.id;
          const countdown = formatQuotationCountdown(q.ownerResponseDeadline, quotationClockTs);
          const expired = countdown === 'Expired';
          const rate = Number(q.fixedRatePerCycle || 0);
          const standbyPct = Number(q.standbyPercent ?? 50);
          const effectiveStandby = Math.round(rate * (standbyPct / 100));
          const machineLabel = q.machine?.name
            || q.machine?.machineId
            || 'Machine';
          return (
            <div
              key={q.id}
              style={{
                background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                border: '1px solid rgba(255,152,0,0.45)',
                borderRadius: 12,
                padding: 14,
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                marginBottom: 10,
              }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: '#c9a84c', fontSize: 13, fontWeight: 800, margin: '0 0 3px' }}>
                    {q.quotationRef || 'Contract quotation'}
                  </p>
                  <p style={{ color: '#e8e0d0', fontSize: 12, margin: '0 0 2px' }}>{machineLabel}</p>
                  <p style={{ color: '#FFB74D', fontSize: 10, margin: 0 }}>Awaiting your response</p>
                </div>
                {countdown && (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={labelStyle}>Time remaining</p>
                    <p style={{
                      color: expired ? '#e94560' : '#C9A84C',
                      fontSize: 16,
                      fontWeight: 800,
                      margin: 0,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    >
                      {countdown}
                    </p>
                  </div>
                )}
              </div>

              <div style={sectionBoxStyle}>
                <p style={sectionTitleStyle}>Contract terms</p>
                <div style={gridStyle}>
                  {[
                    { label: 'Start date', val: q.startDate || '—' },
                    { label: 'End date', val: q.endDate || '—' },
                    { label: 'Duration', val: formatContractSpanDays(q.startDate, q.endDate) },
                    { label: 'Billing cycle', val: q.billingCycle || '—' },
                    { label: 'Rate per cycle', val: rate > 0 ? fmtInr(rate) : '—' },
                    {
                      label: 'Guaranteed hours',
                      val: q.guaranteedHoursPerCycle != null
                        ? `${Number(q.guaranteedHoursPerCycle).toLocaleString('en-IN')} h/cycle`
                        : '—',
                    },
                    { label: 'Mobilization by', val: q.mobilizationTransportBy || '—' },
                  ].map((d, i) => (
                    <div key={i}>
                      <p style={labelStyle}>{d.label}</p>
                      <p style={valueStyle}>{d.val}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div style={sectionBoxStyle}>
                <button
                  type="button"
                  onClick={() => toggleStandbySection(q.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    marginBottom: isStandbyOpen(q.id) ? 8 : 0,
                  }}
                >
                  <span style={{ color: '#c9a84c', fontSize: 11, fontWeight: 800 }}>Standby terms</span>
                  <span style={{ color: '#C9A84C', fontSize: 11 }}>{isStandbyOpen(q.id) ? '▲' : '▼'}</span>
                </button>
                {isStandbyOpen(q.id) && (
                  <div style={gridStyle}>
                    <div>
                      <p style={labelStyle}>Standby rate</p>
                      <p style={valueStyle}>{standbyPct}% of rate</p>
                    </div>
                    <div>
                      <p style={labelStyle}>Standby period</p>
                      <p style={valueStyle}>
                        {formatMonthName(q.monsoonStartMonth)} – {formatMonthName(q.monsoonEndMonth)}
                      </p>
                    </div>
                    <div>
                      <p style={labelStyle}>Effective standby</p>
                      <p style={valueStyle}>
                        {rate > 0 ? `${fmtInr(effectiveStandby)}/cycle` : '—'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div style={sectionBoxStyle}>
                <p style={sectionTitleStyle}>Termination terms</p>
                <div style={gridStyle}>
                  <div>
                    <p style={labelStyle}>Notice period</p>
                    <p style={valueStyle}>{Number(q.terminationNoticeDays ?? 15)} days</p>
                  </div>
                  <div>
                    <p style={labelStyle}>Fee formula</p>
                    <p style={valueStyle}>
                      {formatTerminationFormula(q.terminationFeeFormula, q.terminationFeeFlat)}
                    </p>
                  </div>
                  <div>
                    <p style={labelStyle}>Client termination</p>
                    <p style={valueStyle}>{Number(q.clientTerminationPenaltyPct ?? 20)}%</p>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleAcceptQuotation(q)}
                  style={{
                    flex: '1 1 160px',
                    padding: '11px 16px',
                    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                    color: '#030810',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 800,
                    fontSize: 13,
                    cursor: busy ? 'wait' : 'pointer',
                    opacity: busy ? 0.7 : 1,
                  }}
                >
                  {busy ? t('loading') : '✓ Accept Contract'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setRejectQuotationReason('');
                    setRejectQuotationModal(q);
                  }}
                  style={{
                    flex: '0 1 120px',
                    padding: '11px 16px',
                    background: 'rgba(233,69,96,0.12)',
                    color: '#ff9aa8',
                    border: '1px solid rgba(233,69,96,0.45)',
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: busy ? 'wait' : 'pointer',
                    opacity: busy ? 0.7 : 1,
                  }}
                >
                  ✗ Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderGroupedNavButtons = (items, { onItemClick } = {}) => {
    const nodes = [];
    let index = 0;
    while (index < items.length) {
      const item = items[index];
      if (!item.sectionKey) {
        if (index > 0 && items[index - 1]?.sectionKey) {
          nodes.push(<div key={`section-end-${item.id}`} style={s.divider} />);
        }
        nodes.push(
          <button
            key={item.id}
            type="button"
            style={activeTab === item.id ? s.navActive : s.nav}
            onClick={() => {
              setActiveTab(item.id);
              if (typeof onItemClick === 'function') onItemClick(item);
            }}
          >
            <span>{item.icon}</span>
            <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
            {renderNavBadge(item.badge)}
          </button>,
        );
        index += 1;
        continue;
      }

      const sectionKey = item.sectionKey;
      const sectionLabel = item.sectionLabel || sectionKey;
      const groupItems = [];
      while (index < items.length && items[index].sectionKey === sectionKey) {
        groupItems.push(items[index]);
        index += 1;
      }
      const open = isNavSectionOpen(sectionKey);
      const groupBadge = sumNavBadges(groupItems);
      nodes.push(
        <div key={`section-${sectionKey}`} style={{ width: '100%' }}>
          <div style={s.divider} />
          <button
            type="button"
            style={s.navSectionToggle}
            aria-expanded={open}
            onClick={() => toggleNavSection(sectionKey)}
          >
            <span style={s.navSectionChevron} aria-hidden>{open ? '▼' : '▶'}</span>
            <span style={{ flex: 1, minWidth: 0 }}>{sectionLabel}</span>
            {groupBadge != null ? renderNavBadge(groupBadge) : null}
          </button>
          <div
            style={{
              maxHeight: open ? 320 : 0,
              opacity: open ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-height 300ms ease, opacity 300ms ease',
            }}
          >
            {groupItems.map((groupItem) => (
              <button
                key={groupItem.id}
                type="button"
                style={activeTab === groupItem.id ? s.navActive : s.nav}
                onClick={() => {
                  setActiveTab(groupItem.id);
                  if (typeof onItemClick === 'function') onItemClick(groupItem);
                }}
              >
                <span>{groupItem.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{groupItem.label}</span>
                {renderNavBadge(groupItem.badge)}
              </button>
            ))}
          </div>
        </div>,
      );
    }
    return nodes;
  };

  return (
    <div
      className={useMobileNav ? 'portal-dashboard-shell--phone' : ''}
      style={useMobileNav ? s.containerMobile : s.container}
    >
      {/* Mobile Nav */}
      {useMobileNav && (
        <MobileNav
          navItems={navItemsWithBadge}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          title={t('appName')}
          subtitle={t('ownerPortal')}
          drawerMode="full"
          drawerSectionTitle={t('drawerMenu')}
          hideBottomNav
          topContent={
            <div style={{ padding: '4px 0' }}>
              <p style={{ color: '#c9a84c', fontWeight: '700', fontSize: '13px', margin: '0 0 2px', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                {ownerDisplayName}
                <OwnerTierBadge tier={ownerTier} />
                <AccountStatusPill status={sessionUser.status} />
              </p>
              <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>🚜 {totalMachineCount} Machines · Since {ownerSince}</p>
            </div>
          }
          bottomContent={
            <button style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.3)', background: 'rgba(233,69,96,0.08)', color: '#e94560', cursor: 'pointer', fontSize: '13px', width: '100%' }} onClick={handleLogout}>🚪 {t('logout')}</button>
          }
        />
      )}

      {/* Tablet overlay sidebar */}
      {!useMobileNav && sidebarOverlay && (
        <>
          {sidebarOpen && (
            <div
              onClick={() => setSidebarOpen(false)}
              style={{
                position: 'fixed', inset: 0,
                zIndex: 30,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(1px)',
              }}
            />
          )}
          <div style={{
            ...s.sidebar,
            position: 'fixed',
            left: 0, top: 0,
            zIndex: 40,
            height: '100svh',
            maxHeight: '100svh',
            transform: sidebarOpen
              ? 'translateX(0)'
              : 'translateX(-100%)',
            transition: 'transform 300ms ease',
            pointerEvents: sidebarOpen ? 'auto' : 'none',
          }}>
            <div style={s.sidebarHeader}>
              <div style={s.sidebarHeaderBrand}>
                <div style={s.logoCircle}>DE</div>
                <div>
                  <p style={s.logoTitle}>{t('appName')}</p>
                  <p style={s.logoSub}>{t('ownerPortal')}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                style={s.sidebarToggleBtn}
                aria-label="Close sidebar"
              >
                <PanelLeftClose size={16} />
              </button>
            </div>
            <div style={s.divider} />
            <div style={s.sidebarNavScroll}>
            {navItemsWithBadge.map((item) => (
              <button
                key={item.id}
                type="button"
                style={activeTab === item.id ? s.navActive : s.nav}
                onClick={() => {
                  setActiveTab(item.id);
                  if (sidebarOverlay) setSidebarOpen(false);
                }}
              >
                <span>{item.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                {renderNavBadge(item.badge)}
              </button>
            ))}
            </div>
            <div style={s.divider} />
            <button style={s.logoutBtn} onClick={handleLogout}>🚪 {t('logout')}</button>
            <p style={s.sidebarFooter}>{t('sinceExcellence')}</p>
          </div>
        </>
      )}

      {!useMobileNav && !sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          style={s.sidebarOpenFab}
          aria-label="Open sidebar"
        >
          <PanelLeft size={16} />
        </button>
      )}

      {/* Desktop sticky sidebar */}
      {!useMobileNav && !sidebarOverlay && sidebarOpen && (
        <div style={s.sidebar}>
          <div style={s.sidebarHeader}>
            <div style={s.sidebarHeaderBrand}>
              <div style={s.logoCircle}>DE</div>
              <div>
                <p style={s.logoTitle}>{t('appName')}</p>
                <p style={s.logoSub}>{t('ownerPortal')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              style={s.sidebarToggleBtn}
              aria-label="Close sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
          </div>
          <div style={s.divider} />
          <div style={s.sidebarNavScroll}>
          {navItemsWithBadge.map((item) => (
            <button
              key={item.id}
              type="button"
              style={activeTab === item.id ? s.navActive : s.nav}
              onClick={() => setActiveTab(item.id)}
            >
              <span>{item.icon}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
              {renderNavBadge(item.badge)}
            </button>
          ))}
          </div>
          <div style={s.divider} />
          <button style={s.logoutBtn} onClick={handleLogout}>🚪 {t('logout')}</button>
          <p style={s.sidebarFooter}>{t('sinceExcellence')}</p>
        </div>
      )}

      {/* Main */}
      <div className={useMobileNav ? 'portal-dashboard-main-wrap--phone' : 'portal-dashboard-scroll-shell'} style={useMobileNav ? undefined : s.scrollShell}>
      <div
        className={useMobileNav ? 'portal-dashboard-main--phone' : 'portal-dashboard-main--locked'}
        style={{
          ...(useMobileNav ? s.mainMobile : s.main),
          ...(useMobileNav
            ? { padding: `calc(70px + env(safe-area-inset-top, 0px)) ${isTablet ? '16px' : '10px'} calc(16px + env(safe-area-inset-bottom, 0px))` }
            : isMobile
              ? { padding: '20px 16px' }
              : {
                  padding: !sidebarOpen ? '25px 25px 25px 60px' : '25px',
                  paddingTop: sidebarOverlay ? 'calc(20px + env(safe-area-inset-top, 0px))' : '25px',
                }),
        }}
      >
        {/* Header */}
        <div style={{ ...s.header, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : '0', alignItems: isMobile ? 'flex-start' : 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ minWidth: 0, maxWidth: '100%' }}>
              <h2 style={{ ...s.pageTitle, fontSize: isMobile ? '16px' : '20px' }}>
                {ownerTabTitleItems.find(n => n.id === activeTab)?.icon}{' '}{ownerTabTitleItems.find(n => n.id === activeTab)?.label}
              </h2>
              {!useMobileNav && (
              <p style={s.pageDate}>📅 {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '100%' }}>
            {!useMobileNav && <LanguageSelector compact={true} />}
            {!useMobileNav && (
              <div style={s.ownerBadge}>
                <span style={s.onlineDot}></span>
                🏗️ {ownerDisplayName}
                <OwnerTierBadge tier={ownerTier} />
                <AccountStatusPill status={sessionUser.status} />
              </div>
            )}
          </div>
        </div>
        {termsPrompt.visible && (
          <div style={{ marginBottom: '12px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '10px', padding: '10px 12px' }}>
            <p style={{ color: '#c9a84c', fontSize: '12px', margin: '0 0 6px', fontWeight: 700 }}>
              Terms update available
            </p>
            <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 8px', lineHeight: 1.45 }}>
              Please review the latest platform Terms & Conditions ({termsPrompt.currentVersion}) and acknowledge once.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => { sessionStorage.setItem('returnTab', activeTab); navigate('/terms'); }}
                style={{ textDecoration: 'none', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.45)', color: '#c9a84c', borderRadius: '8px', padding: '7px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
              >
                Open Terms
              </button>
              <button
                type="button"
                onClick={handleAcknowledgeTerms}
                disabled={termsPrompt.acknowledging}
                style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.5)', color: '#4CAF50', borderRadius: '8px', padding: '7px 12px', fontSize: '11px', fontWeight: 700, cursor: termsPrompt.acknowledging ? 'wait' : 'pointer' }}
              >
                {termsPrompt.acknowledging ? 'Saving...' : 'I have read and agree'}
              </button>
              <button
                type="button"
                onClick={() => setTermsPrompt((prev) => ({ ...prev, visible: false }))}
                style={{ background: 'transparent', border: '1px solid rgba(136,150,168,0.45)', color: '#8896a8', borderRadius: '8px', padding: '7px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
              >
                Later
              </button>
            </div>
            {termsPrompt.error ? (
              <p style={{ color: '#e94560', fontSize: '11px', margin: '8px 0 0' }}>{termsPrompt.error}</p>
            ) : null}
          </div>
        )}

        {activeTab === 'dashboard' && isBankIncomplete && (
          <div style={{
            background: 'rgba(255,152,0,0.12)',
            border: '1px solid rgba(255,152,0,0.4)',
            borderRadius: 10,
            padding: '10px 16px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
          >
            <div style={{ display: 'flex',
              alignItems: 'center', gap: 8 }}
            >
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <p style={{
                  color: '#FF9800',
                  fontWeight: 700,
                  fontSize: 13,
                  margin: 0,
                }}
                >
                  {t('ownerBankDetailsIncomplete')}
                </p>
                <p style={{
                  color: '#8896a8',
                  fontSize: 11,
                  margin: '2px 0 0',
                }}
                >
                  {t('ownerPayoutBlockedNoBank')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              style={{
                background: 'rgba(255,152,0,0.2)',
                border: '1px solid rgba(255,152,0,0.5)',
                color: '#FF9800',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: 12,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {t('ownerAddBankDetailsBtn')}
            </button>
          </div>
        )}

        {activeTab === 'dashboard' && ownerTier === 'lite' && !liteUpgradeDismissed && (
          <div style={{
            background: 'rgba(201,168,76,0.1)',
            border: '1px solid rgba(201,168,76,0.4)',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 12,
          }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 220px' }}>
                <p style={{ color: '#c9a84c', fontWeight: 700, fontSize: 13, margin: '0 0 6px' }}>
                  Upgrade to Verified to unlock contract bookings + higher search ranking.
                </p>
                <p style={{ color: '#8896a8', fontSize: 11, margin: 0, lineHeight: 1.45 }}>
                  Complete KYC → Add bank details → Get verified automatically.
                </p>
              </div>
              <button
                type="button"
                onClick={dismissLiteUpgradeBanner}
                aria-label="Dismiss"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(136,150,168,0.4)',
                  color: '#8896a8',
                  borderRadius: 6,
                  padding: '2px 8px',
                  fontSize: 12,
                  cursor: 'pointer',
                  lineHeight: 1.4,
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <button
                type="button"
                onClick={() => setActiveTab('kyc')}
                style={{
                  background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                  border: 'none',
                  color: '#0a1628',
                  borderRadius: 8,
                  padding: '7px 14px',
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Complete KYC →
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('settings')}
                style={{
                  background: 'rgba(201,168,76,0.12)',
                  border: '1px solid rgba(201,168,76,0.45)',
                  color: '#c9a84c',
                  borderRadius: 8,
                  padding: '7px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Add Bank Details →
              </button>
            </div>
          </div>
        )}

        {activeTab === 'dashboard' && ownerEntityTier === 1 && !entityTierNoteDismissed && (
          <div style={{
            background: 'rgba(201,168,76,0.06)',
            border: '1px solid rgba(201,168,76,0.22)',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            alignItems: 'flex-start',
          }}
          >
            <p style={{ color: '#8896a8', fontSize: 11, margin: 0, lineHeight: 1.45, flex: '1 1 220px' }}>
              GST reports and analytics are available for Partnership, LLP and Corporate owners.
            </p>
            <button
              type="button"
              onClick={dismissEntityTierNote}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: '1px solid rgba(136,150,168,0.4)',
                color: '#8896a8',
                borderRadius: 6,
                padding: '2px 8px',
                fontSize: 12,
                cursor: 'pointer',
                lineHeight: 1.4,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ═══ TAB: DASHBOARD ═══ */}
        {activeTab === 'bookings' && (() => {
          const now = new Date();
          const y = now.getFullYear();
          const availableYears = [y + 1, y, y - 1, y - 2];
          const MONTHS = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December',
          ];
          const STATUS_FILTERS = ['all', 'active', 'completed', 'disputed'];
          const ownerContractRows = ownerBookings.filter((b) => b.booking_type === 'contract');
          const filterByPeriod = (items, dateField) => items.filter((item) => {
            const d = new Date(item[dateField] || item.created_at);
            return (
              d.getFullYear() === Number(yearFilter)
              && d.getMonth() === Number(monthFilter)
            );
          });
          const periodStandard = filterByPeriod(
            ownerBookings.filter((b) => b.booking_type !== 'contract'),
            'created_at',
          );
          const periodContract = filterByPeriod(
            ownerContractRows || [],
            'created_at',
          );
          const statusFilter = (bk) => bookingFilter === 'all'
            || (bookingFilter === 'active'
              && ['active', 'dispatched', 'completing'].includes(
                bk.status?.toLowerCase(),
              ))
            || (bookingFilter === 'completed'
              && ['completed', 'settled'].includes(bk.status?.toLowerCase()))
            || (bookingFilter === 'disputed'
              && bk.status?.toLowerCase() === 'disputed');
          const visibleStandard = periodStandard.filter(statusFilter);
          const visibleContract = periodContract.filter(statusFilter);
          const totalNetStandard = visibleStandard.reduce(
            (sum, b) => sum + (Number(b.net_owner_amount) || 0), 0,
          );
          const activeContractCount = periodContract.filter((b) => ['active', 'dispatched', 'completing'].includes(
            b.status?.toLowerCase(),
          )).length;
          const actionCount = pendingQuotations?.length || 0;
          const statusIcon = (status) => {
            const sStatus = status?.toLowerCase();
            if (['completed', 'settled'].includes(sStatus)) return 'success';
            if (['active', 'dispatched', 'completing'].includes(sStatus)) return 'accent';
            if (sStatus === 'disputed') return 'danger';
            return 'warning';
          };
          const statusLabel = (status) => {
            const sStatus = status?.toLowerCase();
            if (sStatus === 'settled') return 'Settled';
            if (sStatus === 'completed') return 'Completed';
            if (['active', 'dispatched'].includes(sStatus)) return 'Active';
            if (sStatus === 'completing') return 'Completing';
            if (sStatus === 'disputed') return 'Disputed';
            return status || '—';
          };
          const selectStyle = {
            background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C9A84C' d='M1 1l5 5 5-5'/%3E%3C/svg%3E") no-repeat right 12px center, linear-gradient(135deg, #0f2040, #0a1628)`,
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: 8,
            color: '#e8e0d0',
            fontSize: 13,
            padding: '8px 32px 8px 12px',
            cursor: 'pointer',
            outline: 'none',
            boxSizing: 'border-box',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            colorScheme: 'dark',
          };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <style>{`
                .od-bk-select option {
                  background: #0a1628;
                  color: #C9A84C;
                }
                .od-bk-kpi {
                  height: 100%;
                  min-height: 96px;
                  display: flex;
                  flex-direction: column;
                  justify-content: space-between;
                  box-sizing: border-box;
                }
                .od-bk-row-left {
                  display: flex;
                  align-items: center;
                  gap: 14px;
                  min-width: 0;
                  flex: 1 1 auto;
                }
                .od-bk-row-amt {
                  text-align: right;
                  flex-shrink: 0;
                  align-self: center;
                }
                @media (max-width: 768px) {
                  .od-bk-filter {
                    flex-direction: column !important;
                    align-items: stretch !important;
                  }
                  .od-bk-filter .od-bk-select {
                    width: 100%;
                  }
                  .od-bk-status {
                    margin-left: 0 !important;
                    width: 100%;
                  }
                  .od-bk-kpi-grid {
                    grid-template-columns: 1fr !important;
                  }
                  .od-bk-row {
                    align-items: flex-start !important;
                    flex-direction: column !important;
                  }
                  .od-bk-row-amt {
                    width: 100%;
                    text-align: left !important;
                    padding-left: 50px;
                    align-self: stretch;
                  }
                }
              `}</style>
              {renderOwnerQuotationReview()}
              {/* Period selects */}
              <div
                className="od-bk-filter"
                style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
              >
                <select
                  className="od-bk-select"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(Number(e.target.value))}
                  style={selectStyle}
                >
                  {availableYears.map((yr) => (
                    <option key={yr} value={yr}>{yr}</option>
                  ))}
                </select>
                <select
                  className="od-bk-select"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(Number(e.target.value))}
                  style={{ ...selectStyle, minWidth: 120 }}
                >
                  {MONTHS.map((m, i) => (
                    <option key={i} value={i}>{m}</option>
                  ))}
                </select>
              </div>
              {/* KPI strip */}
              <div
                className="od-bk-kpi-grid"
                style={{
                display: 'grid',
                gridTemplateColumns: gridCols(1, 3, 3),
                gap: 10,
                alignItems: 'stretch',
              }}
              >
                {[
                  {
                    label: t('thisMonthEarned') || 'This month earned',
                    value: fmtInr(totalNetStandard),
                    sub: `${visibleStandard.length} ${t('standardBookings') || 'standard bookings'}`,
                    color: '#C9A84C',
                  },
                  {
                    label: t('activeContracts') || 'Active contracts',
                    value: activeContractCount,
                    sub: activeContractCount > 0
                      ? t('contractsOngoing') || 'Contracts ongoing'
                      : t('noActiveContracts') || 'None this period',
                    color: activeContractCount > 0 ? '#FF9800' : '#8896a8',
                  },
                  {
                    label: t('actionNeeded') || 'Action needed',
                    value: actionCount,
                    sub: actionCount > 0
                      ? t('quotationsPending') || 'Quotations awaiting reply'
                      : t('allUpToDate') || 'All bookings up to date',
                    color: actionCount > 0 ? '#e94560' : '#4CAF50',
                  },
                ].map((kpi) => (
                  <div
                    key={kpi.label}
                    className="od-bk-kpi"
                    style={{
                      ...s.tableCard,
                      padding: '14px 16px',
                    }}
                  >
                    <div style={{ fontSize: 11, color: '#8896a8', marginBottom: 4 }}>
                      {kpi.label}
                    </div>
                    <div style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: kpi.color,
                      lineHeight: 1.15,
                    }}
                    >
                      {kpi.value}
                    </div>
                    <div style={{ fontSize: 11, color: '#8896a8', marginTop: 4 }}>
                      {kpi.sub}
                    </div>
                  </div>
                ))}
              </div>
              {/* Status pills */}
              <div
                ref={filterBarRef}
                className="od-bk-status"
                style={{
                marginLeft: 'auto',
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                background: 'linear-gradient(135deg,#0f2040,#0a1628)',
                paddingTop: 8,
                paddingBottom: 8,
                marginBottom: 8,
              }}
              >
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => {
                      setBookingFilter(f);
                      setTimeout(() => {
                        filterBarRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'nearest',
                        });
                      }, 50);
                    }}
                    style={{
                      fontSize: 12,
                      padding: '5px 14px',
                      borderRadius: 20,
                      cursor: 'pointer',
                      border: bookingFilter === f
                        ? '1px solid rgba(201,168,76,0.5)'
                        : '1px solid rgba(255,255,255,0.1)',
                      background: bookingFilter === f
                        ? 'rgba(201,168,76,0.15)' : 'transparent',
                      color: bookingFilter === f ? '#C9A84C' : '#8896a8',
                      fontWeight: bookingFilter === f ? 600 : 400,
                      textTransform: 'capitalize',
                    }}
                  >
                    {f === 'all' ? t('all') || 'All'
                      : f === 'active' ? t('active')
                        : f === 'completed' ? t('completed')
                          : t('disputed')}
                  </button>
                ))}
              </div>
              {/* Standard bookings */}
              <div>
                <div style={{
                  fontSize: 11,
                  color: '#8896a8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 10,
                }}
                >
                  {t('standardBookings') || 'Standard bookings'} · {MONTHS[monthFilter]} {yearFilter}
                </div>
                {visibleStandard.length === 0 ? (
                  <div style={{
                    ...s.tableCard,
                    fontSize: 13,
                    color: '#8896a8',
                    textAlign: 'center',
                    padding: '24px',
                  }}
                  >
                    {t('noBookingsInPeriod') || 'No bookings in this period'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {visibleStandard.map((bk, i) => {
                      const role = statusIcon(bk.status);
                      const iconMap = {
                        success: '✅',
                        accent: '⚙️',
                        danger: '⚠️',
                        warning: '⏳',
                      };
                      const colorMap = {
                        success: '#4CAF50',
                        accent: '#C9A84C',
                        danger: '#e94560',
                        warning: '#FF9800',
                      };
                      const bgMap = {
                        success: 'rgba(76,175,80,0.1)',
                        accent: 'rgba(201,168,76,0.1)',
                        danger: 'rgba(233,69,96,0.1)',
                        warning: 'rgba(255,152,0,0.1)',
                      };
                      return (
                        <div
                          key={bk.id || i}
                          className="od-bk-row"
                          style={{
                            ...s.tableCard,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 10,
                            padding: '14px 18px',
                          }}
                        >
                          <div className="od-bk-row-left">
                            <div style={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              background: bgMap[role],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 18,
                              flexShrink: 0,
                            }}
                            >
                              {iconMap[role]}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#e8e0d0' }}>
                                {bk.machines?.name || bk.machine_name || t('machine')}
                              </div>
                              <div style={{ fontSize: 11, color: '#8896a8', marginTop: 2 }}>
                                {bk.booking_ref || bk.id} · {t('client') || 'Client'} · {new Date(bk.created_at).toLocaleDateString(
                                  'en-IN',
                                  {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  },
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="od-bk-row-amt">
                            <div style={{ fontSize: 16, fontWeight: 700, color: colorMap[role] }}>
                              {fmtInr(bk.net_owner_amount)}
                            </div>
                            <div style={{ fontSize: 11, color: '#8896a8', marginTop: 2 }}>
                              {statusLabel(bk.status)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Contract bookings */}
              <div>
                <div style={{
                  fontSize: 11,
                  color: '#8896a8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 10,
                }}
                >
                  {t('contractBookings') || 'Contract bookings'} · {MONTHS[monthFilter]} {yearFilter}
                </div>
                {visibleContract.length === 0 ? (
                  <div style={{
                    ...s.tableCard,
                    fontSize: 13,
                    color: '#8896a8',
                    textAlign: 'center',
                    padding: '24px',
                  }}
                  >
                    {t('noContractsInPeriod') || 'No contracts in this period'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {visibleContract.map((bk, i) => {
                      const role = statusIcon(bk.status);
                      const isActive = role === 'accent';
                      const colorMap = {
                        success: '#4CAF50',
                        accent: '#C9A84C',
                        danger: '#e94560',
                        warning: '#FF9800',
                      };
                      const iconMap = {
                        success: '✅',
                        accent: '⚙️',
                        danger: '⚠️',
                        warning: '⏳',
                      };
                      const bgMap = {
                        success: 'rgba(76,175,80,0.1)',
                        accent: 'rgba(201,168,76,0.1)',
                        danger: 'rgba(233,69,96,0.1)',
                        warning: 'rgba(255,152,0,0.1)',
                      };
                      return (
                        <div
                          key={bk.id || i}
                          style={{
                            ...s.tableCard,
                            padding: '14px 18px',
                            border: isActive
                              ? '1px solid rgba(201,168,76,0.4)'
                              : undefined,
                          }}
                        >
                          <div
                            className="od-bk-row"
                            style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 10,
                          }}
                          >
                            <div className="od-bk-row-left">
                              <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: bgMap[role],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 18,
                                flexShrink: 0,
                              }}
                              >
                                {iconMap[role]}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#e8e0d0' }}>
                                  {bk.machines?.name || bk.machine_name || t('machine')}
                                </div>
                                <div style={{ fontSize: 11, color: '#8896a8', marginTop: 2 }}>
                                  {bk.contract_ref || bk.booking_ref || bk.id} · {t('client') || 'Client'} · {new Date(bk.created_at).toLocaleDateString(
                                    'en-IN',
                                    {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    },
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="od-bk-row-amt">
                              <div style={{ fontSize: 16, fontWeight: 700, color: colorMap[role] }}>
                                {fmtInr(
                                  bk.contract_value
                                  || bk.net_owner_amount
                                  || bk.total_amount || 0,
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: '#8896a8', marginTop: 2 }}>
                                {statusLabel(bk.status)}
                              </div>
                            </div>
                          </div>
                          {/* Active contract expanded detail */}
                          {isActive && (
                            bk.mobilization_amount != null
                            || bk.ra_bill_count != null
                          ) && (
                            <div style={{
                              marginTop: 12,
                              paddingTop: 10,
                              borderTop: '1px solid rgba(255,255,255,0.06)',
                              display: 'flex',
                              gap: 24,
                              flexWrap: 'wrap',
                            }}
                            >
                              {bk.mobilization_amount != null && (
                                <div>
                                  <div style={{ fontSize: 10, color: '#8896a8', marginBottom: 2 }}>
                                    {t('mobilizationReleased') || 'Mobilization released'}
                                  </div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#4CAF50' }}>
                                    {fmtInr(bk.mobilization_amount)}
                                  </div>
                                </div>
                              )}
                              {bk.pending_release != null && (
                                <div>
                                  <div style={{ fontSize: 10, color: '#8896a8', marginBottom: 2 }}>
                                    {t('pendingRelease') || 'Pending release'}
                                  </div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#FF9800' }}>
                                    {fmtInr(bk.pending_release)}
                                  </div>
                                </div>
                              )}
                              {bk.ra_bill_count != null && (
                                <div>
                                  <div style={{ fontSize: 10, color: '#8896a8', marginBottom: 2 }}>
                                    {t('raBills') || 'RA-bills'}
                                  </div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#e8e0d0' }}>
                                    {bk.ra_bill_count} {t('cycles') || 'cycles'}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {(() => {
                            const awaitingQ = (awaitingMobQuotations || []).find((q) => (
                              String(q.bookingId || q.booking_id || '') === String(bk.id)
                            ));
                            if (!awaitingQ) return null;
                            const busy = mobilizationActionId === awaitingQ.id;
                            return (
                              <div style={{ marginTop: 12 }}>
                                <div style={{
                                  background: 'rgba(201,168,76,0.1)',
                                  border: '1px solid rgba(201,168,76,0.35)',
                                  borderRadius: '8px',
                                  padding: '10px 12px',
                                  marginBottom: '8px',
                                  textAlign: 'center',
                                }}
                                >
                                  <p style={{ color: '#C9A84C', fontWeight: 800, fontSize: '12px', margin: 0 }}>
                                    🚛 Machine dispatched — confirm mobilization to release advance
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => handleConfirmMobilization(awaitingQ, bk)}
                                  style={{
                                    width: '100%',
                                    padding: '11px 16px',
                                    background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
                                    color: '#0a1628',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    cursor: busy ? 'wait' : 'pointer',
                                    opacity: busy ? 0.7 : 1,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {busy ? t('loading') : 'Confirm Mobilization & Dispatch →'}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {/* ── DASHBOARD ── */}
        {activeTab === 'dashboard' && (() => {
              const now = new Date();
              const monthLabel = now.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
              const liveHourStatuses = ['Dispatched', 'Extension_Pending', 'Completing', 'Active'];
              const bookingHoursOf = (b) => {
                if (liveHourStatuses.includes(b.status)) {
                  return runningElapsedMs(b) / 3600000;
                }
                const n = Number(b.final_hours ?? b.operator_actual_hours ?? b.booked_hours ?? 0);
                return Number.isFinite(n) && n > 0 ? n : 0;
              };
              const bookingInMonth = (b) => {
                const d = parseBookingDate(b);
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
              };
              const bookingInToday = (b) => bookingInMonth(b) && parseBookingDate(b).getDate() === now.getDate();
              const machineTimeRows = machineData.map((m) => {
                const related = (ownerBookings || []).filter((b) => b.machine_id === m.id);
                const todayHmr = related.reduce((sum, b) => {
                  if (liveHourStatuses.includes(b.status) || bookingInToday(b)) return sum + bookingHoursOf(b);
                  return sum;
                }, 0);
                const monthHmr = related.reduce((sum, b) => {
                  if (liveHourStatuses.includes(b.status) || bookingInMonth(b)) return sum + bookingHoursOf(b);
                  return sum;
                }, 0);
                return {
                  machineId: m.id || m.machine_id,
                  machineName: m.name || m.machine_id,
                  machineCode: m.machine_id || '',
                  regNo: m.reg_no || '',
                  todayMinutes: Math.round(todayHmr * 60),
                  monthMinutes: Math.round(monthHmr * 60),
                };
              });
              const netReceived = Number(dashboardSettlementStats?.net || 0);
              const grossBilling = Number(dashboardSettlementStats?.gross || 0);
              const commission = Number(dashboardSettlementStats?.commission || 0);
              const tds = Number(dashboardSettlementStats?.tdsTcs || 0);
              const completedJobs = settlements.filter((r) => String(r.status || '').toLowerCase() === 'settled').length;
              const onSiteJobs = ownerBookings.filter((b) => b.status === 'Active').length;
              const pendingJobs = ownerBookings.filter((b) => b.status === 'Dispatched' || b.status === 'Completing').length;
              const disputedJobs = ownerBookings.filter((b) => b.status === 'Disputed').length;
              const pendingCount = pendingQuotations.length;
              const recentPayments = (recentSettlements || []).map((row) => ({
                id: row.id,
                booking_id: row.booking_ref,
                reference: row.booking_ref,
                amount: row.net_owner_amount,
                created_at: row.created_at,
                date: row.created_at,
                status: settlementStatusLabel(row.status),
              }));
              const complianceAlerts = (allAlerts || []).filter((a) => a.type === 'compliance');
              const complianceSummary = {
                hasExpired: complianceAlerts.some((a) => a.alertStatus === 'expired' || a.color === '#ef4444'),
                hasExpiring: complianceAlerts.some((a) => a.alertStatus !== 'expired' && (a.color === '#f97316' || a.color === '#FF9800')),
              };
              const hour = new Date().getHours();
              const greetKey = hour < 12 ? 'goodMorning' : hour < 17 ? 'goodAfternoon' : 'goodEvening';

              return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <OnboardingTracker isMobile={isMobile} />
      <div style={{
        marginBottom: 20,
        paddingBottom: 16,
        borderBottom: '1px solid rgba(201,168,76,0.15)',
      }}
      >
        <div style={{
          fontSize: isMobile ? 18 : 22,
          fontWeight: 700,
          color: '#e8e0d0',
          wordBreak: 'break-word',
        }}
        >
          {t(greetKey)},{' '}
          <span style={{ color: '#C9A84C' }}>{ownerDisplayName}</span>
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .od-settlement-strip {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .od-net-amount {
            font-size: clamp(18px, 7vw, 28px) !important;
            word-break: break-word;
          }
          .od-billing-col {
            width: 100%;
            align-items: stretch !important;
          }
          .od-billing-row {
            flex-direction: column !important;
            flex-wrap: nowrap !important;
            gap: 0 !important;
            justify-content: flex-start !important;
            width: 100%;
          }
          .od-billing-item {
            display: flex !important;
            flex-direction: row !important;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            text-align: left !important;
            padding: 10px 0;
            border-bottom: 1px solid rgba(255,255,255,0.08);
            gap: 12px;
          }
          .od-billing-item:last-child {
            border-bottom: none;
          }
          .od-billing-item > div:first-child {
            margin-bottom: 0 !important;
            flex: 1 1 auto;
            min-width: 0;
          }
          .od-billing-item > div:last-child {
            flex-shrink: 0;
            white-space: nowrap;
          }
          .od-fleet-col-today {
            display: none !important;
          }
          .od-fleet-table {
            min-width: 0 !important;
          }
          .od-recent-pmt {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 6px;
          }
          .od-recent-pmt-amt {
            padding-left: 0 !important;
          }
        }
      `}</style>

      {/* BLOCK A — Hero settlement strip */}
      <div
        className="od-settlement-strip"
        style={{
        ...s.tableCard,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 16,
      }}
      >
        <div>
          <div style={{
            fontSize: 11, color: '#8896a8',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: 6,
          }}
          >
            {ownerDisplayName} · {monthLabel}
          </div>
          <div
            className="od-net-amount"
            style={{
            fontSize: 28, fontWeight: 700,
            color: '#C9A84C', lineHeight: 1,
          }}
          >
            {fmtInr(netReceived)}
          </div>
          <div style={{
            fontSize: 12, color: '#8896a8', marginTop: 6,
          }}
          >
            Net received · after platform fee & TDS/TCS
          </div>
        </div>

        <div
          className="od-billing-col"
          style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end', gap: 8,
        }}
        >
          <div
            className="od-billing-row"
            style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'flex-end' }}
          >
            <div className="od-billing-item" style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#8896a8', marginBottom: 2 }}>
                Gross billing
              </div>
              <div style={{
                fontSize: 14, fontWeight: 600, color: '#e8e0d0',
              }}
              >
                {fmtInr(grossBilling)}
              </div>
            </div>
            <div className="od-billing-item" style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#8896a8', marginBottom: 2 }}>
                {t('commission15')} ({process.env.REACT_APP_DE_COMMISSION_PCT || '15'}%)
              </div>
              <div style={{
                fontSize: 14, fontWeight: 600, color: '#e94560',
              }}
              >
                −{fmtInr(commission)}
              </div>
            </div>
            <div className="od-billing-item" style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#8896a8', marginBottom: 2 }}>
                {t('tdsGstTcs')}
              </div>
              <div style={{
                fontSize: 14, fontWeight: 600, color: '#e94560',
              }}
              >
                −{fmtInr(tds)}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#556070' }}>
            Sec 194C TDS deducted at source · RBI compliant
          </div>
        </div>
      </div>

      {/* BLOCK B — Work status tiles */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: 10,
      }}
      >
        {[
          {
            label: t('sectionCompleted'),
            count: completedJobs,
            color: '#4CAF50',
            bg: 'rgba(76,175,80,0.1)',
            border: 'rgba(76,175,80,0.2)',
            icon: '✅',
          },
          {
            label: `${t('active')} / On site`,
            count: onSiteJobs,
            color: '#C9A84C',
            bg: 'rgba(201,168,76,0.1)',
            border: 'rgba(201,168,76,0.25)',
            icon: '⚙️',
          },
          {
            label: t('pending'),
            count: pendingJobs,
            color: '#FF9800',
            bg: 'rgba(255,152,0,0.1)',
            border: 'rgba(255,152,0,0.2)',
            icon: '⏳',
          },
          {
            label: t('statusDisputed') === 'statusDisputed' ? 'Disputed' : t('statusDisputed'),
            count: disputedJobs,
            color: disputedJobs > 0 ? '#e94560' : '#8896a8',
            bg: disputedJobs > 0
              ? 'rgba(233,69,96,0.1)' : 'rgba(255,255,255,0.03)',
            border: disputedJobs > 0
              ? 'rgba(233,69,96,0.2)' : 'rgba(255,255,255,0.08)',
            icon: '⚠️',
          },
        ].map((tile) => (
          <div key={tile.label} style={{
            background: tile.bg,
            border: `1px solid ${tile.border}`,
            borderRadius: 10,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: tile.bg,
              border: `1px solid ${tile.border}`,
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 16,
              flexShrink: 0,
            }}
            >
              {tile.icon}
            </div>
            <div>
              <div style={{
                fontSize: 24, fontWeight: 700,
                color: tile.color, lineHeight: 1,
              }}
              >
                {tile.count}
              </div>
              <div style={{
                fontSize: 11, color: '#8896a8', marginTop: 3,
              }}
              >
                {tile.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* BLOCK C — Fleet hours table + Action/Compliance */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '3fr 2fr',
        gap: 12,
      }}
      >

        {/* Fleet table */}
        <div style={s.tableCard}>
          <div style={{
            fontSize: 11, color: '#8896a8',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: 14,
          }}
          >
            Machine Time Usage · {monthLabel}
          </div>
          {machineTimeRows.length === 0 ? (
            <div style={{ fontSize: 12, color: '#8896a8' }}>
              {t('noMachinesYet')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table
                className="od-fleet-table"
                style={{
                minWidth: 320,
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
                tableLayout: 'fixed',
              }}
              >
                <thead>
                  <tr style={{
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                  >
                    {[
                      t('machine'),
                      t('todayText'),
                      t('ownerThisMonth') === 'ownerThisMonth' ? 'This month' : t('ownerThisMonth'),
                      t('status'),
                    ].map((h, i) => (
                      <th
                        key={h}
                        className={i === 1 ? 'od-fleet-col-today' : undefined}
                        style={{
                        textAlign: i === 0 ? 'left' : 'right',
                        fontSize: 10, fontWeight: 600,
                        color: '#8896a8',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        paddingBottom: 8,
                      }}
                      >{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {machineTimeRows.map((row, idx) => {
                    const monthHrs = Math.floor(row.monthMinutes / 60);
                    const monthMins = row.monthMinutes % 60;
                    const todayHrs = Math.floor(row.todayMinutes / 60);
                    const todayMins = row.todayMinutes % 60;
                    const isActive = row.todayMinutes > 0;

                    return (
                      <tr key={row.machineId || idx} style={{
                        borderBottom: idx < machineTimeRows.length - 1
                          ? '1px solid rgba(255,255,255,0.04)' : 'none',
                      }}
                      >
                        <td style={{ padding: '10px 0' }}>
                          <div style={{
                            fontSize: 13, color: '#e8e0d0',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                          }}
                          >
                            {row.machineName}
                          </div>
                          <div style={{
                            fontSize: 11, color: '#8896a8', marginTop: 2,
                          }}
                          >
                            {row.machineCode || row.regNo}
                          </div>
                        </td>
                        <td
                          className="od-fleet-col-today"
                          style={{
                          textAlign: 'right', padding: '10px 0',
                          fontSize: 12,
                          color: isActive ? '#e8e0d0' : '#556070',
                        }}
                        >
                          {todayHrs > 0
                            ? `${todayHrs} hr ${todayMins} min`
                            : todayMins > 0
                              ? `${todayMins} min`
                              : '0 min'}
                        </td>
                        <td style={{
                          textAlign: 'right', padding: '10px 0',
                          fontSize: 12,
                          color: monthHrs > 0 || monthMins > 0 ? '#e8e0d0' : '#556070',
                          fontWeight: monthHrs > 0 || monthMins > 0 ? 600 : 400,
                        }}
                        >
                          {monthHrs > 0
                            ? `${monthHrs} hr ${monthMins} min`
                            : monthMins > 0
                              ? `${monthMins} min`
                              : '0 hr'}
                        </td>
                        <td style={{
                          textAlign: 'right', padding: '10px 0',
                        }}
                        >
                          <span style={{
                            fontSize: 11, fontWeight: 600,
                            color: isActive ? '#C9A84C' : '#556070',
                          }}
                          >
                            {isActive ? 'On site' : 'Idle'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action + Compliance column */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 10,
        }}
        >

          {/* Action needed — quotations pending */}
          {pendingCount > 0 && (
            <div style={{
              ...s.tableCard,
              border: '1px solid rgba(255,152,0,0.3)',
              flex: 1,
            }}
            >
              <div style={{
                fontSize: 11, color: '#8896a8',
                textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 10,
              }}
              >
                Action needed
              </div>
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}
              >
                <span style={{ fontSize: 18, lineHeight: 1.2 }}>📋</span>
                <div>
                  <div style={{
                    fontSize: 13, color: '#FF9800', fontWeight: 600,
                  }}
                  >
                    {pendingCount} {t('pending')}
                  </div>
                  <div style={{
                    fontSize: 11, color: '#8896a8', marginTop: 3,
                  }}
                  >
                    Client is waiting · reply to confirm
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Compliance status */}
          {typeof complianceSummary !== 'undefined' && complianceSummary && (
            <div style={{ ...s.tableCard, flex: 1 }}>
              <div style={{
                fontSize: 11, color: '#8896a8',
                textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 10,
              }}
              >
                {t('complianceDates')}
              </div>
              {complianceSummary.hasExpired ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
                >
                  <span style={{ fontSize: 16 }}>🔴</span>
                  <div style={{
                    fontSize: 13, color: '#e94560', fontWeight: 600,
                  }}
                  >
                    {t('complianceExpired')}
                  </div>
                </div>
              ) : complianceSummary.hasExpiring ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
                >
                  <span style={{ fontSize: 16 }}>🟡</span>
                  <div style={{
                    fontSize: 13, color: '#FF9800', fontWeight: 600,
                  }}
                  >
                    {t('complianceExpiringSoon')}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
                >
                  <span style={{ fontSize: 16 }}>🟢</span>
                  <div style={{
                    fontSize: 13, color: '#4CAF50', fontWeight: 600,
                  }}
                  >
                    All documents valid
                  </div>
                </div>
              )}
              <div style={{
                fontSize: 11, color: '#8896a8', marginTop: 6,
                paddingLeft: 24,
              }}
              >
                Insurance · RC · PUC
              </div>
            </div>
          )}

          {/* Alerts — only if exist */}
          {allAlerts.length > 0 && (
            <div style={{ ...s.tableCard, flex: 1 }}>
              <div style={{
                fontSize: 11, color: '#8896a8',
                textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 10,
              }}
              >
                {t('recentAlerts')}
              </div>
              {allAlerts.slice(0, 2).map((alert, i) => (
                <div key={alert.id || i} style={{
                  fontSize: 12, color: '#e8e0d0',
                  padding: '6px 0',
                  borderBottom: i < 1 && allAlerts.length > 1
                    ? '1px solid rgba(255,255,255,0.06)' : 'none',
                }}
                >
                  {ownerAlertMsg(alert, t)}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      {/* BLOCK D — Recent payments */}
      <div style={s.tableCard}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 14, gap: 8, flexWrap: 'wrap',
        }}
        >
          <div style={{
            fontSize: 11, color: '#8896a8',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}
          >
            {t('ownerRecentPayments')}
          </div>
          <button
            type="button"
            onClick={() => setActiveTab('reports')}
            style={{
              background: 'transparent', border: 'none',
              color: '#C9A84C', fontSize: 12,
              cursor: 'pointer', padding: 0, fontWeight: 600,
            }}
          >
            {t('ownerViewAllPayments')} → {t('reports')}
          </button>
        </div>

        {recentPayments.length === 0 ? (
          <div style={{ fontSize: 12, color: '#8896a8' }}>
            {t('ownerNoPaymentsYet')}
          </div>
        ) : (
          recentPayments.slice(0, 3).map((pmt, i) => (
            <div
              key={pmt.id || i}
              className="od-recent-pmt"
              style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 0',
              borderBottom: i < Math.min(recentPayments.length, 3) - 1
                ? '1px solid rgba(255,255,255,0.06)' : 'none',
            }}
            >
              <div>
                <div style={{
                  fontSize: 13, color: '#e8e0d0', fontWeight: 600,
                }}
                >
                  Booking settlement
                </div>
                <div style={{
                  fontSize: 11, color: '#8896a8', marginTop: 2,
                }}
                >
                  {pmt.booking_id || pmt.reference || '—'} · {
                    pmt.created_at || pmt.date
                      ? new Date(pmt.created_at || pmt.date)
                        .toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                      : '—'
                  } · {pmt.status || 'Settled'}
                </div>
              </div>
              <div
                className="od-recent-pmt-amt"
                style={{
                fontSize: 15, fontWeight: 700,
                color: '#C9A84C', paddingLeft: 16,
                whiteSpace: 'nowrap',
              }}
              >
                {fmtInr(pmt.amount)}
              </div>
            </div>
          ))
        )}
      </div>

    </div>
              );
            })()}

        {/* ═══ TAB: MY MACHINES / EDIT REGISTRATION INFO ═══ */}
        {(activeTab === 'machines' || activeTab === 'edit-registration') && (
          <div>
            {activeTab === 'machines' && renderOwnerKycBanner()}
            {activeTab === 'machines' && displayMachines.length === 0 && (
              <div style={{ ...s.tableCard, textAlign: 'center', padding: '32px' }}>
                <p style={{ fontSize: '32px', margin: '0 0 8px' }}>{String.fromCodePoint(0x1F69C)}</p>
                <p style={{ color: '#c9a84c', fontWeight: 700, margin: '0 0 8px' }}>{t('noMachinesYet')}</p>
                {ownerKycRegistrationBlocked ? (
                  <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 12px' }}>{t('kycRejectedWarning')}</p>
                ) : null}
                <button
                  type="button"
                  style={{
                    ...s.confirmBtn,
                    ...(ownerKycRegistrationBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  disabled={ownerKycRegistrationBlocked}
                  onClick={() => setActiveTab('register')}
                >
                  + {t('registerMachine')}
                </button>
              </div>
            )}
            {displayMachines.map((m, i) => {
              const raw = machineData.find((r) => r.machine_id === m.id);
              const machineUuid = raw?.id;
              const isExpanded = editRegSelectedId === machineUuid;
              const activeEditTab = editRegActiveTab[machineUuid] ?? 'rates';
              const EDIT_TABS = [
                { id: 'rates', emoji: '💰', label: t('myRates') || 'My Rates' },
                { id: 'attachments', emoji: '🔗', label: t('attachments') || 'Attachments' },
                { id: 'payment', emoji: '💳', label: t('paymentTerms') || 'Payment Terms' },
                { id: 'location', emoji: '📍', label: t('location') || 'Location' },
                { id: 'schedule', emoji: '🕐', label: t('schedule') || 'Schedule' },
                { id: 'diesel', emoji: '⛽', label: t('diesel') || 'Diesel' },
                { id: 'market', emoji: '📊', label: t('marketIntel') || 'Market Intel' },
                { id: 'compliance', emoji: '📋', label: t('compliance') || 'Compliance' },
                { id: 'service', emoji: '🛠', label: t('serviceHistory') || 'Service' },
                { id: 'identity', emoji: '🔒', label: t('identity') || 'Identity' },
              ];
              return (
              <div
                key={i}
                id={machineUuid ? `owner-machine-${machineUuid}` : undefined}
                style={{ ...s.tableCard, marginBottom: '15px' }}
              >
                {activeTab === 'machines' && (
                <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                    <p style={{
                      color: String(m.status).toLowerCase() === 'active' ? '#C9A84C' : '#e8e0d0',
                      fontWeight: '700',
                      fontSize: '15px',
                      margin: '0 0 3px',
                    }}
                    >
                      {m.name}
                    </p>
                    <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 2px' }}>{m.id} · {m.regNo} · {m.year}</p>
                    {m.activeBooking ? (
                      <p style={{ color: '#C9A84C', fontSize: '11px', fontWeight: 600, margin: 0 }}>On Site</p>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    {(() => {
                      const st = String(m.status || '').toLowerCase();
                      const chip = st === 'active'
                        ? { bg: 'rgba(76,175,80,0.15)', border: '#4CAF50', color: '#4CAF50', label: statusLabel(m.status, t) }
                        : st === 'maintenance'
                          ? { bg: 'rgba(255,152,0,0.15)', border: '#FF9800', color: '#FF9800', label: 'Maintenance' }
                          : st === 'inactive'
                            ? { bg: 'rgba(136,150,168,0.15)', border: '#8896a8', color: '#8896a8', label: 'Inactive' }
                            : { bg: 'rgba(255,152,0,0.15)', border: '#FF9800', color: '#FF9800', label: statusLabel(m.status, t) };
                      return (
                        <span style={{
                          ...s.statusBadge,
                          background: chip.bg,
                          border: `1px solid ${chip.border}`,
                          color: chip.color,
                        }}
                        >
                          {chip.label}
                        </span>
                      );
                    })()}
                    {(() => {
                      const available = raw?.is_available !== false;
                      const startLabel = formatWorkingHoursClock(raw?.working_hours_start);
                      const endLabel = formatWorkingHoursClock(raw?.working_hours_end);
                      const hoursLabel = startLabel && endLabel ? `${startLabel} – ${endLabel}` : '';
                      return (
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            ...s.statusBadge,
                            background: available ? 'rgba(76,175,80,0.15)' : 'rgba(233,69,96,0.15)',
                            border: `1px solid ${available ? '#4CAF50' : '#e94560'}`,
                            color: available ? '#4CAF50' : '#e94560',
                          }}
                          >
                            {available ? 'Available' : 'Unavailable'}
                          </span>
                          {hoursLabel ? (
                            <p style={{ color: '#8896a8', fontSize: 10, margin: '4px 0 0' }}>{hoursLabel}</p>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: gridCols(1, 2, 2), gap: '10px', marginBottom: '12px' }}>
                  {(() => {
                    const logs = machineUuid ? (serviceLogs[machineUuid] || []) : [];
                    const latestService = logs[0];
                    const nextService = latestService?.next_service_date
                      ? (formatComplianceDateLabel(latestService.next_service_date) || latestService.next_service_date)
                      : 'Not set';
                    const c = machineUuid ? complianceData[machineUuid] : null;
                    const insEnd = c?.insurance_expiry;
                    const insSt = insEnd ? complianceDateStatus(insEnd) : 'not_set';
                    let insLabel = 'Not set';
                    if (insSt === 'valid') insLabel = 'Valid';
                    else if (insSt === 'expired') insLabel = 'Expired';
                    else if (insSt === 'expiring_critical' || insSt === 'expiring_soon') insLabel = 'Expiring';
                    const insDate = insEnd ? (formatComplianceDateLabel(insEnd) || insEnd) : '';
                    const monthNet = machineUuid ? (monthNetByMachineUuid[machineUuid] || 0) : 0;
                    const stats = [
                      { label: `⛽ ${t('fuel')}`, val: fuelDisplay(m.fuel, m.fuelCapacity) },
                      { label: `⏱ ${t('hmrToday')}`, val: formatHoursHm(m.hmr) },
                      { label: '💰 This month', val: fmtInr(monthNet) },
                      { label: '🔧 Next service', val: nextService },
                      { label: '🛡 Insurance', val: insDate ? `${insLabel} · ${insDate}` : insLabel },
                    ];
                    return stats.map((d) => (
                      <div key={d.label} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px' }}>
                        <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 3px' }}>{d.label}</p>
                        <p style={{ color: '#e8e0d0', fontWeight: '600', fontSize: '13px', margin: 0 }}>{d.val}</p>
                      </div>
                    ));
                  })()}
                </div>
                </>
                )}

                {activeTab === 'edit-registration' && (
                <>
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', padding: '4px 0', marginBottom: isExpanded ? 12 : 0,
                    flexWrap: 'wrap', gap: 8,
                  }}
                  onClick={() => {
                    if (isExpanded) {
                      setEditRegSelectedId(null);
                      setEditingMachineId(null);
                      return;
                    }
                    setEditRegSelectedId(machineUuid);
                    setEditingMachineId(machineUuid);
                    setEditMachineForm({
                      rate_per_hour: raw?.rate_per_hour != null ? String(raw.rate_per_hour) : '',
                      minimum_hours: raw?.minimum_hours != null ? String(raw.minimum_hours) : '',
                      rate_per_day: raw?.rate_per_day != null ? String(raw.rate_per_day) : '',
                      rate_per_week: raw?.rate_per_week != null ? String(raw.rate_per_week) : '',
                      rate_per_month: raw?.rate_per_month != null ? String(raw.rate_per_month) : '',
                      fuel_capacity_litres: raw?.fuel_capacity_litres != null
                        ? String(raw.fuel_capacity_litres) : '',
                      is_negotiable: Boolean(raw?.is_negotiable),
                      minimum_rate_per_cycle: raw?.minimum_rate_per_cycle != null
                        ? String(raw.minimum_rate_per_cycle) : '',
                      attachments: Array.isArray(raw?.attachments) ? [...raw.attachments] : [],
                      attachment_rates: (
                        raw?.attachment_rates
                        && typeof raw.attachment_rates === 'object'
                        && !Array.isArray(raw.attachment_rates)
                      ) ? { ...raw.attachment_rates } : {},
                      attachment_min_hours: (
                        raw?.attachment_min_hours
                        && typeof raw.attachment_min_hours === 'object'
                        && !Array.isArray(raw.attachment_min_hours)
                      ) ? { ...raw.attachment_min_hours } : {},
                      primary_attachment: raw?.primary_attachment || '',
                      attachment_notes: raw?.attachment_notes || '',
                      pincode: raw?.pincode ? String(raw.pincode) : '',
                      working_hours_start: workingHoursFormValue(raw?.working_hours_start, '09:00'),
                      working_hours_end: workingHoursFormValue(raw?.working_hours_end, '18:00'),
                      working_days: Array.isArray(raw?.working_days) && raw.working_days.length
                        ? [...raw.working_days]
                        : [...DEFAULT_WORKING_DAYS],
                      payment_due_days: raw?.payment_due_days != null
                        ? String(raw.payment_due_days) : '',
                      early_payment_days: raw?.early_payment_days != null
                        ? String(raw.early_payment_days) : '',
                      early_payment_discount_pct: raw?.early_payment_discount_pct != null
                        ? String(raw.early_payment_discount_pct) : '',
                      late_payment_fee_pct: raw?.late_payment_fee_pct != null
                        ? String(raw.late_payment_fee_pct) : '',
                    });
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 3, height: 40, borderRadius: 2,
                      background: 'linear-gradient(180deg, #C9A84C, rgba(201,168,76,0.2))',
                    }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#e8e0d0' }}>
                        {m.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#8896a8', marginTop: 2 }}>
                        {m.id} · {m.regNo} · {m.year}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span style={s.statusBadge}>
                      {m.status === 'Active' ? '● Active' : `● ${m.status}`}
                    </span>
                    <span style={{ fontSize: 13, color: '#C9A84C', fontWeight: 700, marginLeft: 4 }}>
                      {isExpanded ? 'Close ✕' : 'Edit →'}
                    </span>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{
                    height: 1,
                    background: 'rgba(201,168,76,0.2)',
                    marginBottom: 16,
                  }} />
                )}
                </>
                )}

                {activeTab === 'machines' && (
                  <div style={{
                    marginTop: 12,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center',
                  }}
                  >
                    {machineUuid ? (
                      <button
                        type="button"
                        disabled={Boolean(availabilityToggling[machineUuid])}
                        onClick={() => handleToggleAvailability(machineUuid, raw?.is_available !== false)}
                        style={{
                          background: raw?.is_available !== false
                            ? 'rgba(233,69,96,0.12)'
                            : 'rgba(76,175,80,0.12)',
                          border: raw?.is_available !== false
                            ? '1px solid rgba(233,69,96,0.5)'
                            : '1px solid rgba(76,175,80,0.5)',
                          color: raw?.is_available !== false ? '#e94560' : '#4CAF50',
                          borderRadius: 8,
                          padding: '10px 16px',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: availabilityToggling[machineUuid] ? 'wait' : 'pointer',
                          whiteSpace: 'nowrap',
                          opacity: availabilityToggling[machineUuid] ? 0.7 : 1,
                        }}
                      >
                        {raw?.is_available !== false ? '🔴 Mark Unavailable' : '🟢 Mark Available'}
                      </button>
                    ) : null}
                    {machineUuid ? (
                      <button
                        type="button"
                        onClick={() => openEquipmentProfile(machineUuid, raw)}
                        style={{
                          background: 'rgba(201,168,76,0.12)',
                          border: '1px solid rgba(201,168,76,0.5)',
                          color: '#C9A84C',
                          borderRadius: 8,
                          padding: '10px 16px',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Equipment Profile →
                      </button>
                    ) : null}
                  </div>
                )}

                {activeTab === 'edit-registration' && machineUuid && editRegSelectedId === machineUuid && (
                <>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  margin: '16px 0 12px',
                }}>
                  {EDIT_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setEditRegActiveTab((prev) => ({ ...prev, [machineUuid]: tab.id }))}
                      style={{
                        fontSize: 12,
                        padding: '6px 14px',
                        minHeight: 44,
                        borderRadius: 20,
                        cursor: 'pointer',
                        border: activeEditTab === tab.id
                          ? '1px solid rgba(201,168,76,0.6)'
                          : '1px solid rgba(255,255,255,0.1)',
                        background: activeEditTab === tab.id
                          ? 'rgba(201,168,76,0.15)' : 'transparent',
                        color: activeEditTab === tab.id ? '#C9A84C' : '#8896a8',
                        fontWeight: activeEditTab === tab.id ? 600 : 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tab.emoji} {tab.label}
                    </button>
                  ))}
                </div>
                {activeEditTab === 'rates' && (
                  <div style={{
                    marginTop: 10,
                    padding: 12,
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 8,
                    border: '1px solid rgba(201,168,76,0.2)',
                  }}>
                    {[
                      { key: 'rate_per_hour', label: t('ratePerHourLabel') },
                      { key: 'minimum_hours', label: t('minimumHoursLabel') },
                      { key: 'rate_per_day', label: t('ratePerDayLabel') },
                      { key: 'rate_per_week', label: t('ratePerWeekLabel') || 'Weekly Rate' },
                      { key: 'rate_per_month', label: t('ratePerMonthLabel') || 'Monthly Rate' },
                      { key: 'fuel_capacity_litres', label: t('fuelCapacityLabel') },
                    ].map((field) => (
                      <div key={field.key} style={{ marginBottom: 8 }}>
                        <label style={{
                          color: '#8896a8',
                          fontSize: 11,
                          display: 'block',
                          marginBottom: 3,
                        }}>
                          {field.label}
                        </label>
                        <input
                          type="number"
                          className="de-owner-input"
                          value={editMachineForm[field.key] ?? ''}
                          onChange={(e) => setEditMachineForm((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))}
                        />
                      </div>
                    ))}
                    <div style={{ marginBottom: 8 }}>
                      <label style={{
                        color: '#8896a8',
                        fontSize: 11,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 3,
                        cursor: 'pointer',
                      }}>
                        <input
                          type="checkbox"
                          checked={Boolean(editMachineForm.is_negotiable)}
                          onChange={(e) => setEditMachineForm((prev) => ({
                            ...prev,
                            is_negotiable: e.target.checked,
                            ...(e.target.checked
                              ? {}
                              : { minimum_rate_per_cycle: prev.minimum_rate_per_cycle }),
                          }))}
                        />
                        Rates negotiable (contract / custom)
                      </label>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{
                        color: '#8896a8',
                        fontSize: 11,
                        display: 'block',
                        marginBottom: 3,
                      }}>
                        Minimum rate per cycle (₹)
                      </label>
                      <input
                        type="number"
                        className="de-owner-input"
                        min="0"
                        value={editMachineForm.minimum_rate_per_cycle ?? ''}
                        onChange={(e) => setEditMachineForm((prev) => ({
                          ...prev,
                          minimum_rate_per_cycle: e.target.value,
                        }))}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSaveMachine(machineUuid)}
                      style={{
                        background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
                        border: 'none',
                        borderRadius: 8,
                        color: '#0a1628',
                        fontWeight: 700,
                        fontSize: 13,
                        padding: '10px 16px',
                        minHeight: 44,
                        cursor: 'pointer',
                        marginTop: 8,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t('save') || 'Save'}
                    </button>
                  </div>
                )}
                {activeEditTab === 'attachments' && (
                  <div style={{
                    marginTop: 10,
                    padding: 12,
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: 8,
                    border: '1px solid rgba(201,168,76,0.2)',
                  }}>
                    <div style={{ marginBottom: 10, marginTop: 4 }}>
                      <p style={{
                        color: '#8896a8',
                        fontSize: 11,
                        margin: '0 0 6px',
                        fontWeight: 600,
                      }}>
                        Available Attachments (optional)
                      </p>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: 6,
                      }}>
                        {ATTACHMENT_OPTIONS.map((opt) => {
                          const selected = Array.isArray(editMachineForm.attachments)
                            ? editMachineForm.attachments
                            : [];
                          return (
                            <label
                              key={opt.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                color: '#e8e0d0',
                                fontSize: 11,
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selected.includes(opt.id)}
                                onChange={(e) => {
                                  setEditMachineForm((prev) => {
                                    const current = Array.isArray(prev.attachments)
                                      ? prev.attachments
                                      : [];
                                    const nextAttachments = e.target.checked
                                      ? [...current, opt.id]
                                      : current.filter((id) => id !== opt.id);
                                    const nextRates = { ...(prev.attachment_rates || {}) };
                                    const nextMins = { ...(prev.attachment_min_hours || {}) };
                                    if (!e.target.checked) {
                                      delete nextRates[opt.id];
                                      delete nextMins[opt.id];
                                    }
                                    const nextPrimary = prev.primary_attachment
                                      && nextAttachments.includes(prev.primary_attachment)
                                      ? prev.primary_attachment
                                      : '';
                                    return {
                                      ...prev,
                                      attachments: nextAttachments,
                                      attachment_rates: nextRates,
                                      attachment_min_hours: nextMins,
                                      primary_attachment: nextPrimary,
                                    };
                                  });
                                }}
                              />
                              {opt.label}
                            </label>
                          );
                        })}
                      </div>
                      {(Array.isArray(editMachineForm.attachments)
                        ? editMachineForm.attachments
                        : []).length > 1 && (
                        <div style={{ marginTop: 8 }}>
                          <label style={{
                            color: '#8896a8',
                            fontSize: 11,
                            display: 'block',
                            marginBottom: 3,
                          }}>
                            Primary attachment (default)
                          </label>
                          <select
                            className="de-owner-input"
                            value={editMachineForm.primary_attachment || ''}
                            onChange={(e) => setEditMachineForm((prev) => ({
                              ...prev,
                              primary_attachment: e.target.value,
                            }))}
                            style={{ width: '100%' }}
                          >
                            <option value="">Select primary</option>
                            {(editMachineForm.attachments || []).map((id) => (
                              <option key={id} value={id}>
                                {ATTACHMENT_OPTIONS.find((o) => o.id === id)?.label || id}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {(Array.isArray(editMachineForm.attachments)
                        ? editMachineForm.attachments
                        : []).some((a) => ATTACHMENT_RATE_ELIGIBLE.includes(a)) && (
                        <div style={{ marginTop: 10 }}>
                          <p style={{
                            color: '#8896a8',
                            fontSize: 11,
                            margin: '0 0 4px',
                            fontWeight: 600,
                          }}>
                            Attachment Extra Rates
                          </p>
                          <p style={{
                            color: '#8896a8',
                            fontSize: 10,
                            margin: '0 0 8px',
                          }}>
                            Extra per hour when this attachment is in use (over base hourly rate)
                          </p>
                          {(editMachineForm.attachments || [])
                            .filter((a) => ATTACHMENT_RATE_ELIGIBLE.includes(a))
                            .map((attachId) => {
                              const label = ATTACHMENT_OPTIONS.find((o) => o.id === attachId)?.label
                                || attachId;
                              return (
                                <div key={attachId} style={{ marginBottom: 8 }}>
                                  <label style={{
                                    color: '#8896a8',
                                    fontSize: 11,
                                    display: 'block',
                                    marginBottom: 3,
                                  }}>
                                    {label} — extra rate (₹/hr)
                                  </label>
                                  <input
                                    type="number"
                                    className="de-owner-input"
                                    min="0"
                                    placeholder="Optional"
                                    value={
                                      (editMachineForm.attachment_rates || {})[attachId] ?? ''
                                    }
                                    onChange={(e) => {
                                      const nextVal = e.target.value;
                                      setEditMachineForm((prev) => {
                                        const nextRates = { ...(prev.attachment_rates || {}) };
                                        const nextMins = { ...(prev.attachment_min_hours || {}) };
                                        if (nextVal === '') {
                                          delete nextRates[attachId];
                                          delete nextMins[attachId];
                                        } else {
                                          nextRates[attachId] = nextVal;
                                        }
                                        return {
                                          ...prev,
                                          attachment_rates: nextRates,
                                          attachment_min_hours: nextMins,
                                        };
                                      });
                                    }}
                                  />
                                  <label style={{
                                    color: '#8896a8',
                                    fontSize: 11,
                                    display: 'block',
                                    margin: '6px 0 3px',
                                  }}>
                                    Minimum usage hours for this attachment
                                  </label>
                                  <input
                                    type="number"
                                    className="de-owner-input"
                                    min="0"
                                    step="0.01"
                                    placeholder="Optional"
                                    value={
                                      (editMachineForm.attachment_min_hours || {})[attachId] ?? ''
                                    }
                                    onChange={(e) => {
                                      const nextVal = e.target.value;
                                      setEditMachineForm((prev) => {
                                        const nextMins = { ...(prev.attachment_min_hours || {}) };
                                        if (nextVal === '') delete nextMins[attachId];
                                        else nextMins[attachId] = nextVal;
                                        return { ...prev, attachment_min_hours: nextMins };
                                      });
                                    }}
                                  />
                                  <p style={{
                                    color: '#8896a8',
                                    fontSize: 10,
                                    margin: '3px 0 0',
                                  }}>
                                    Shortest attachment usage you will accept when this attachment is needed. Blank = no restriction.
                                  </p>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 11, color: '#8896a8' }}>
                        {t('attachmentNotes') || 'Attachment notes (visible to clients)'}
                      </label>
                      <textarea
                        value={editMachineForm.attachment_notes || ''}
                        onChange={(e) => setEditMachineForm((f) => ({
                          ...f,
                          attachment_notes: e.target.value.slice(0, 500),
                        }))}
                        rows={3}
                        maxLength={500}
                        className="de-owner-input"
                        style={{ width: '100%', marginTop: 4, resize: 'vertical' }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSaveMachine(machineUuid)}
                      style={{
                        background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
                        border: 'none',
                        borderRadius: 8,
                        color: '#0a1628',
                        fontWeight: 700,
                        fontSize: 13,
                        padding: '10px 16px',
                        minHeight: 44,
                        cursor: 'pointer',
                        marginTop: 8,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t('save') || 'Save'}
                    </button>
                  </div>
                )}
                {activeEditTab === 'payment' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {[
                      {
                        key: 'payment_due_days',
                        label: t('paymentDueDays') || 'Payment due days',
                        hint: 'Days after RA-Bill for client to pay',
                        min: 7,
                        max: 45,
                      },
                      {
                        key: 'early_payment_days',
                        label: t('earlyPaymentDays') || 'Early payment days',
                        hint: 'Days before due date for early discount',
                        min: 1,
                        max: 7,
                      },
                      {
                        key: 'early_payment_discount_pct',
                        label: t('earlyPaymentDiscountPct') || 'Early payment discount %',
                        hint: '% discount for early payment',
                        min: 0,
                        max: 5,
                        step: '0.1',
                      },
                      {
                        key: 'late_payment_fee_pct',
                        label: t('latePaymentFeePct') || 'Late payment fee %',
                        hint: '% fee after due date',
                        min: 0,
                        max: 2,
                        step: '0.1',
                      },
                    ].map((field) => (
                      <div key={field.key}>
                        <label style={{ fontSize: 11, color: '#8896a8', display: 'block', marginBottom: 4 }}>
                          {field.label}
                        </label>
                        <input
                          type="number"
                          className="de-owner-input"
                          min={field.min}
                          max={field.max}
                          step={field.step || '1'}
                          value={editMachineForm[field.key] ?? ''}
                          onChange={(e) => setEditMachineForm((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))}
                          style={{ maxWidth: 160 }}
                        />
                        <div style={{ fontSize: 11, color: '#8896a8', marginTop: 4 }}>
                          {field.hint}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleSaveMachine(machineUuid)}
                      style={{
                        background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
                        border: 'none',
                        borderRadius: 8,
                        color: '#0a1628',
                        fontWeight: 700,
                        fontSize: 13,
                        padding: '10px 16px',
                        minHeight: 44,
                        cursor: 'pointer',
                        marginTop: 8,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t('save') || 'Save'}
                    </button>
                  </div>
                )}
                {activeEditTab === 'location' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={{ fontSize: 11, color: '#8896a8' }}>
                      {t('pincode') || 'Pincode'}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={editMachineForm.pincode || ''}
                      onChange={(e) => setEditMachineForm((f) => ({
                        ...f,
                        pincode: e.target.value.replace(/\D/g, '').slice(0, 6),
                      }))}
                      className="de-owner-input"
                      style={{ maxWidth: 160 }}
                    />
                    <div style={{ fontSize: 11, color: '#8896a8' }}>
                      {t('pincodeHint') || 'Update if your machine is deployed at a new location. Coordinates will be updated automatically.'}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSaveMachine(machineUuid)}
                      style={{
                        background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
                        border: 'none',
                        borderRadius: 8,
                        color: '#0a1628',
                        fontWeight: 700,
                        fontSize: 13,
                        padding: '10px 16px',
                        minHeight: 44,
                        cursor: 'pointer',
                        marginTop: 8,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t('save') || 'Save'}
                    </button>
                  </div>
                )}
                {activeEditTab === 'schedule' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#8896a8', marginBottom: 8 }}>
                        {t('workingDays') || 'Working days'}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => {
                          const selected = (editMachineForm.working_days || DEFAULT_WORKING_DAYS).includes(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const current = editMachineForm.working_days || DEFAULT_WORKING_DAYS;
                                const updated = selected
                                  ? current.filter((d) => d !== day)
                                  : [...current, day];
                                setEditMachineForm((f) => ({ ...f, working_days: updated }));
                              }}
                              style={{
                                fontSize: 12,
                                padding: '6px 14px',
                                borderRadius: 20,
                                cursor: 'pointer',
                                border: selected
                                  ? '1px solid rgba(201,168,76,0.6)'
                                  : '1px solid rgba(255,255,255,0.1)',
                                background: selected ? 'rgba(201,168,76,0.15)' : 'transparent',
                                color: selected ? '#C9A84C' : '#8896a8',
                                fontWeight: selected ? 600 : 400,
                                minHeight: 36,
                              }}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {(() => {
                        const parseToAmPmLocal = (hhmm) => {
                          const parsed = parseToAmPm(hhmm || '09:00');
                          return parsed;
                        };
                        const toHHMMLocal = (hour, minute, period) => toHHMM(hour, minute, period);
                        const selStyle = {
                          background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                          border: '1px solid rgba(201,168,76,0.3)',
                          borderRadius: 8,
                          color: '#e8e0d0',
                          fontSize: 13,
                          padding: '8px 10px',
                          cursor: 'pointer',
                          outline: 'none',
                        };
                        const optStyle = { background: '#0a1628', color: '#C9A84C' };
                        const startVal = editMachineForm.working_hours_start || '09:00';
                        const endVal = editMachineForm.working_hours_end || '18:00';
                        const sp = parseToAmPmLocal(startVal);
                        const ep = parseToAmPmLocal(endVal);
                        const hours = Array.from({ length: 12 }, (_, i) => i + 1);
                        const minutes = [0, 15, 30, 45];
                        return (
                          <>
                            <div style={{ flex: '1 1 200px' }}>
                              <div style={{ fontSize: 11, color: '#8896a8', marginBottom: 6 }}>
                                {t('startTime') || 'Start time'}
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <select
                                  value={sp.hour}
                                  style={selStyle}
                                  onChange={(e) => setEditMachineForm((f) => ({
                                    ...f,
                                    working_hours_start: toHHMMLocal(e.target.value, sp.minute, sp.period),
                                  }))}
                                >
                                  {hours.map((h) => (
                                    <option key={h} value={h} style={optStyle}>{h}</option>
                                  ))}
                                </select>
                                <span style={{ color: '#8896a8' }}>:</span>
                                <select
                                  value={sp.minute}
                                  style={selStyle}
                                  onChange={(e) => setEditMachineForm((f) => ({
                                    ...f,
                                    working_hours_start: toHHMMLocal(sp.hour, e.target.value, sp.period),
                                  }))}
                                >
                                  {minutes.map((m) => (
                                    <option key={m} value={m} style={optStyle}>
                                      {String(m).padStart(2, '0')}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  value={sp.period}
                                  style={selStyle}
                                  onChange={(e) => setEditMachineForm((f) => ({
                                    ...f,
                                    working_hours_start: toHHMMLocal(sp.hour, sp.minute, e.target.value),
                                  }))}
                                >
                                  <option value="AM" style={optStyle}>AM</option>
                                  <option value="PM" style={optStyle}>PM</option>
                                </select>
                              </div>
                            </div>
                            <div style={{ flex: '1 1 200px' }}>
                              <div style={{ fontSize: 11, color: '#8896a8', marginBottom: 6 }}>
                                {t('endTime') || 'End time'}
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <select
                                  value={ep.hour}
                                  style={selStyle}
                                  onChange={(e) => setEditMachineForm((f) => ({
                                    ...f,
                                    working_hours_end: toHHMMLocal(e.target.value, ep.minute, ep.period),
                                  }))}
                                >
                                  {hours.map((h) => (
                                    <option key={h} value={h} style={optStyle}>{h}</option>
                                  ))}
                                </select>
                                <span style={{ color: '#8896a8' }}>:</span>
                                <select
                                  value={ep.minute}
                                  style={selStyle}
                                  onChange={(e) => setEditMachineForm((f) => ({
                                    ...f,
                                    working_hours_end: toHHMMLocal(ep.hour, e.target.value, ep.period),
                                  }))}
                                >
                                  {minutes.map((m) => (
                                    <option key={m} value={m} style={optStyle}>
                                      {String(m).padStart(2, '0')}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  value={ep.period}
                                  style={selStyle}
                                  onChange={(e) => setEditMachineForm((f) => ({
                                    ...f,
                                    working_hours_end: toHHMMLocal(ep.hour, ep.minute, e.target.value),
                                  }))}
                                >
                                  <option value="AM" style={optStyle}>AM</option>
                                  <option value="PM" style={optStyle}>PM</option>
                                </select>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSaveSchedule(machineUuid)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: 'rgba(201,168,76,0.15)',
                        border: '1px solid rgba(201,168,76,0.4)',
                        color: '#C9A84C',
                        fontSize: 13,
                        fontWeight: 600,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t('save') || 'Save'}
                    </button>
                  </div>
                )}
                {activeEditTab === 'diesel' && renderDieselSection(machineUuid, raw)}
                {activeEditTab === 'market' && renderMarketSection(machineUuid, raw)}
                {activeEditTab === 'compliance' && (
                <div style={{
                  marginTop: 12,
                  borderTop: '1px solid rgba(201,168,76,0.15)',
                  paddingTop: 12,
                }}>
                  {(() => {
                    const compliance = complianceData[machineUuid] || {
                      insurance_start: null,
                      insurance_expiry: null,
                      rc_start: null,
                      rc_expiry: null,
                      puc_start: null,
                      puc_expiry: null,
                    };
                    const statusLabelFor = (status) => {
                      if (status === 'expired') return t('complianceExpired');
                      if (status === 'expiring_critical') return t('complianceExpiringCritical');
                      if (status === 'expiring_soon') return t('complianceExpiringSoon');
                      if (status === 'valid') return t('complianceValid');
                      return t('complianceNotSet');
                    };
                    const fields = [
                      {
                        startKey: 'insurance_start',
                        endKey: 'insurance_expiry',
                        label: t('complianceInsurance'),
                        docType: 'insurance',
                        show: true,
                      },
                      {
                        startKey: 'rc_start',
                        endKey: 'rc_expiry',
                        label: t('complianceRcBook'),
                        docType: 'rc',
                        show: m.rtoApplicable,
                      },
                      {
                        startKey: 'puc_start',
                        endKey: 'puc_expiry',
                        label: t('compliancePuc'),
                        docType: 'puc',
                        show: m.rtoApplicable,
                      },
                    ].filter((f) => f.show);
                    const isEditingCompliance = editingComplianceId === machineUuid;
                    return (
                      <>
                        {!isEditingCompliance && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingComplianceId(machineUuid);
                                setComplianceForm({
                                  insurance_start: compliance.insurance_start || '',
                                  insurance_expiry: compliance.insurance_expiry || '',
                                  rc_start: compliance.rc_start || '',
                                  rc_expiry: compliance.rc_expiry || '',
                                  puc_start: compliance.puc_start || '',
                                  puc_expiry: compliance.puc_expiry || '',
                                });
                              }}
                              style={{
                                background: 'rgba(201,168,76,0.1)',
                                border: '1px solid rgba(201,168,76,0.3)',
                                color: '#C9A84C',
                                borderRadius: 6,
                                padding: '4px 10px',
                                fontSize: 11,
                                cursor: 'pointer',
                              }}
                            >
                              ✏️ Edit
                            </button>
                          </div>
                        )}
                        {fields.map((field) => {
                          const endVal = compliance[field.endKey];
                          const status = complianceDateStatus(endVal);
                          const badge = complianceStatusBadgeStyle(status);
                          const badgeEl = (
                            <span style={{
                              ...s.statusBadge,
                              background: badge.bg,
                              border: `1px solid ${badge.border}`,
                              color: badge.color,
                              fontSize: 10,
                              flexShrink: 0,
                            }}>
                              {statusLabelFor(status)}
                            </span>
                          );
                          if (isEditingCompliance) {
                            const isExtracting = Boolean(complianceExtracting[field.endKey]);
                            const extractMessage = complianceExtractMessage[field.endKey];
                            const extractInputId = `compliance-extract-${machineUuid}-${field.endKey}`;
                            return (
                              <div
                                key={field.endKey}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  flexWrap: 'wrap',
                                  marginBottom: 8,
                                }}
                              >
                                <label style={{
                                  color: '#c9a84c',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}>
                                  {field.label}
                                  <input
                                    id={extractInputId}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      const selected = e.target.files?.[0];
                                      if (selected) {
                                        handleComplianceExtract(machineUuid, field, selected);
                                      }
                                      e.target.value = '';
                                    }}
                                  />
                                  <button
                                    type="button"
                                    title={t('complianceExtractBtn')}
                                    disabled={isExtracting}
                                    onClick={() => document.getElementById(extractInputId)?.click()}
                                    style={{
                                      background: 'rgba(255,255,255,0.05)',
                                      border: '1px solid rgba(255,255,255,0.15)',
                                      borderRadius: 4,
                                      color: '#C9A84C',
                                      padding: '2px 6px',
                                      fontSize: 11,
                                      cursor: isExtracting ? 'wait' : 'pointer',
                                      lineHeight: 1,
                                    }}
                                  >
                                    📎
                                  </button>
                                </label>
                                {isExtracting && (
                                  <span style={{ color: '#8896a8', fontSize: 10, flexShrink: 0 }}>
                                    {t('complianceExtracting')}
                                  </span>
                                )}
                                {!isExtracting && extractMessage === 'success' && (
                                  <span style={{ color: '#22c55e', fontSize: 10, flexShrink: 0 }}>
                                    {t('complianceExtractSuccess')}
                                  </span>
                                )}
                                {!isExtracting && extractMessage === 'error' && (
                                  <span style={{ color: '#ef4444', fontSize: 10, flexShrink: 0 }}>
                                    {t('complianceExtractError')}
                                  </span>
                                )}
                                <input
                                  type="date"
                                  className="de-owner-input"
                                  style={{ flex: '1 1 140px', minWidth: 0 }}
                                  value={complianceForm[field.startKey] || ''}
                                  onChange={(e) => setComplianceForm((prev) => ({
                                    ...prev,
                                    [field.startKey]: e.target.value,
                                  }))}
                                />
                                <span style={{ color: '#8896a8', flexShrink: 0 }}>→</span>
                                <input
                                  type="date"
                                  className="de-owner-input"
                                  style={{ flex: '1 1 140px', minWidth: 0 }}
                                  value={complianceForm[field.endKey] || ''}
                                  onChange={(e) => setComplianceForm((prev) => ({
                                    ...prev,
                                    [field.endKey]: e.target.value,
                                  }))}
                                />
                                {badgeEl}
                              </div>
                            );
                          }
                          const rangeLabel = formatComplianceDateRange(
                            compliance[field.startKey],
                            endVal,
                          );
                          return (
                            <div
                              key={field.endKey}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexWrap: 'wrap',
                                marginBottom: 8,
                              }}
                            >
                              <span style={{
                                color: '#c9a84c',
                                fontSize: 11,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}>
                                {field.label}:
                              </span>
                              <span style={{ color: '#e8e0d0', fontSize: 12, flex: '1 1 auto' }}>
                                {rangeLabel || t('complianceNotSet')}
                              </span>
                              {badgeEl}
                            </div>
                          );
                        })}
                        {isEditingCompliance && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button
                              type="button"
                              disabled={complianceSaving}
                              onClick={() => handleSaveCompliance(machineUuid, m.rtoApplicable)}
                              style={{
                                background: 'rgba(201,168,76,0.2)',
                                border: '1px solid rgba(201,168,76,0.4)',
                                color: '#C9A84C',
                                borderRadius: 6,
                                padding: '6px 16px',
                                fontSize: 12,
                                cursor: complianceSaving ? 'wait' : 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              {t('complianceSaveBtn')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingComplianceId(null);
                                setComplianceForm({});
                                setComplianceExtracting({});
                                setComplianceExtractMessage({});
                              }}
                              style={{
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                color: '#8896a8',
                                borderRadius: 6,
                                padding: '6px 16px',
                                fontSize: 12,
                                cursor: 'pointer',
                              }}
                            >
                              {t('cancel')}
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                )}
                {activeEditTab === 'service' && (
                <div style={{
                  marginTop: 12,
                  borderTop: '1px solid rgba(201,168,76,0.15)',
                  paddingTop: 12,
                }}>
                  {serviceLoading[machineUuid] ? (
                    <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>{t('loading')}</p>
                  ) : (() => {
                    const logs = serviceLogs[machineUuid] || [];
                    const latest = logs[0] || null;
                    const nextDueStatus = latest?.next_service_date
                      ? serviceDueDateStatus(latest.next_service_date)
                      : 'not_set';
                    const nextDueBadge = serviceDueStatusBadgeStyle(nextDueStatus);
                    const nextDueLabel = nextDueStatus === 'overdue'
                      ? t('serviceOverdue')
                      : nextDueStatus === 'due_soon'
                        ? t('serviceDueSoon')
                        : nextDueStatus === 'ok'
                          ? t('complianceValid')
                          : null;
                    return (
                      <>
                        <p style={{ color: '#e8e0d0', fontSize: 12, margin: '0 0 8px' }}>
                          {latest
                            ? `${t('serviceLastService')}: ${formatComplianceDateLabel(latest.service_date) || latest.service_date} — ${serviceTypeLabel(latest.service_type, t)}`
                            : t('serviceNoRecords')}
                        </p>
                        {(latest?.next_service_date || latest?.next_service_hmr != null) && (
                          <div style={{ marginBottom: 10 }}>
                            {latest?.next_service_date && (
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 6,
                                flexWrap: 'wrap',
                              }}>
                                <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                                  {t('serviceNextDue')}: {formatComplianceDateLabel(latest.next_service_date) || latest.next_service_date}
                                </p>
                                {nextDueLabel && (
                                  <span style={{
                                    ...s.statusBadge,
                                    background: nextDueBadge.bg,
                                    border: `1px solid ${nextDueBadge.border}`,
                                    color: nextDueBadge.color,
                                    fontSize: 10,
                                  }}>
                                    {nextDueLabel}
                                  </span>
                                )}
                              </div>
                            )}
                            {latest?.next_service_hmr != null && (
                              <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                                {t('serviceNextHmr')} {Number(latest.next_service_hmr)} {t('hmrHrs')}
                              </p>
                            )}
                          </div>
                        )}
                        {serviceFormOpen === machineUuid ? (
                          <div style={{
                            marginTop: 8,
                            padding: 12,
                            background: 'rgba(255,255,255,0.03)',
                            borderRadius: 8,
                            border: '1px solid rgba(201,168,76,0.2)',
                          }}>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                                {t('serviceDate')} *
                              </label>
                              <input
                                type="date"
                                className="de-owner-input"
                                value={serviceFormData.service_date}
                                onChange={(e) => setServiceFormData((prev) => ({
                                  ...prev,
                                  service_date: e.target.value,
                                }))}
                              />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                                {t('serviceType')} *
                              </label>
                              <select
                                className="de-owner-select"
                                value={serviceFormData.service_type}
                                onChange={(e) => setServiceFormData((prev) => ({
                                  ...prev,
                                  service_type: e.target.value,
                                }))}
                              >
                                <option value="routine">{t('serviceTypeRoutine')}</option>
                                <option value="major">{t('serviceTypeMajor')}</option>
                                <option value="breakdown">{t('serviceTypeBreakdown')}</option>
                                <option value="pdi">{t('serviceTypePdi')}</option>
                                <option value="other">{t('serviceTypeOther')}</option>
                              </select>
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                                {t('serviceHmr')}
                              </label>
                              <input
                                type="number"
                                className="de-owner-input"
                                value={serviceFormData.hmr_at_service}
                                onChange={(e) => setServiceFormData((prev) => ({
                                  ...prev,
                                  hmr_at_service: e.target.value,
                                }))}
                              />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                                {t('serviceNextDate')}
                              </label>
                              <input
                                type="date"
                                className="de-owner-input"
                                value={serviceFormData.next_service_date}
                                onChange={(e) => setServiceFormData((prev) => ({
                                  ...prev,
                                  next_service_date: e.target.value,
                                }))}
                              />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                                {t('serviceWorkDone')}
                              </label>
                              <input
                                type="text"
                                className="de-owner-input"
                                value={serviceFormData.work_done}
                                onChange={(e) => setServiceFormData((prev) => ({
                                  ...prev,
                                  work_done: e.target.value,
                                }))}
                              />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ color: '#8896a8', fontSize: 11, display: 'block', marginBottom: 3 }}>
                                {t('serviceNotes')}
                              </label>
                              <textarea
                                className="de-owner-input"
                                style={{ resize: 'vertical' }}
                                value={serviceFormData.notes}
                                onChange={(e) => setServiceFormData((prev) => ({
                                  ...prev,
                                  notes: e.target.value,
                                }))}
                                rows={3}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                              <button
                                type="button"
                                disabled={serviceSaving}
                                onClick={() => handleAddServiceLog(machineUuid)}
                                style={{
                                  background: 'rgba(201,168,76,0.2)',
                                  border: '1px solid rgba(201,168,76,0.4)',
                                  color: '#C9A84C',
                                  borderRadius: 6,
                                  padding: '6px 16px',
                                  fontSize: 12,
                                  cursor: serviceSaving ? 'wait' : 'pointer',
                                  fontWeight: 600,
                                }}
                              >
                                {t('complianceSaveBtn')}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setServiceFormOpen(null);
                                  setServiceFormData({ ...EMPTY_SERVICE_FORM });
                                }}
                                style={{
                                  background: 'rgba(255,255,255,0.05)',
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  color: '#8896a8',
                                  borderRadius: 6,
                                  padding: '6px 16px',
                                  fontSize: 12,
                                  cursor: 'pointer',
                                }}
                              >
                                {t('cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setServiceFormOpen(machineUuid);
                              setServiceFormData({ ...EMPTY_SERVICE_FORM });
                            }}
                            style={{
                              background: 'rgba(201,168,76,0.1)',
                              border: '1px solid rgba(201,168,76,0.3)',
                              color: '#C9A84C',
                              borderRadius: 6,
                              padding: '5px 12px',
                              fontSize: 11,
                              cursor: 'pointer',
                              marginBottom: 10,
                            }}
                          >
                            + {t('serviceAddLog')}
                          </button>
                        )}
                        {logs.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            {logs.slice(0, 5).map((log) => (
                              <div
                                key={log.id}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)',
                                  gap: 6,
                                  padding: '8px 0',
                                  borderTop: '1px solid rgba(255,255,255,0.06)',
                                  fontSize: 11,
                                }}
                              >
                                <span style={{ color: '#e8e0d0' }}>
                                  {formatComplianceDateLabel(log.service_date) || log.service_date}
                                </span>
                                <span style={{ color: '#c9a84c' }}>{serviceTypeLabel(log.service_type, t)}</span>
                                <span style={{ color: '#8896a8' }}>
                                  {log.hmr_at_service != null ? `${Number(log.hmr_at_service)} ${t('hmrHrs')}` : '—'}
                                </span>
                                <span style={{ color: '#8896a8' }}>{log.work_done || '—'}</span>
                                <span style={{ color: '#8896a8' }}>{log.notes || '—'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                )}
                {activeEditTab === 'identity' && (
                <div style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10,
                  padding: 16,
                }}>
                  <div style={{ fontSize: 12, color: '#8896a8', marginBottom: 12 }}>
                    {t('lockedFieldsNote') || 'Locked to prevent identity fraud. Contact admin to make changes.'}
                  </div>
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  background: 'rgba(233,69,96,0.06)',
                  borderRadius: 8,
                  border: '1px solid rgba(233,69,96,0.25)',
                }}>
                  <p style={{
                    color: '#e94560',
                    fontWeight: 700,
                    fontSize: 12,
                    margin: '0 0 6px',
                  }}>
                    {t('lockedRegistrationFieldsTitle')}
                  </p>
                  <p style={{
                    color: '#8896a8',
                    fontSize: 11,
                    margin: '0 0 10px',
                    lineHeight: 1.45,
                  }}>
                    {t('lockedRegistrationFieldsNote')}
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                    gap: 8,
                    marginBottom: 10,
                  }}>
                    {[
                      { label: t('surveyColName'), val: raw?.name || m.name || '—' },
                      { label: t('type'), val: raw?.type || m.type || '—' },
                      {
                        label: t('lockedFieldRegNo'),
                        val: (raw?.reg_no || m.regNo) === 'TRACKED-NO-RTO'
                          ? t('crawlerNoRto')
                          : (raw?.reg_no || m.regNo || '—'),
                      },
                      {
                        label: t('machineYearOfManufacture'),
                        val: raw?.year || m.year || '—',
                      },
                    ].map((row) => (
                      <div key={row.label} style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 6, padding: 8 }}>
                        <p style={{ color: '#8896a8', fontSize: 10, margin: '0 0 2px' }}>{row.label}</p>
                        <p style={{ color: '#e8e0d0', fontSize: 12, fontWeight: 600, margin: 0 }}>{row.val}</p>
                      </div>
                    ))}
                  </div>
                  <p style={{ color: '#8896a8', fontSize: 11, fontWeight: 600, margin: '0 0 6px' }}>
                    {t('lockedRegistrationDocuments')}
                  </p>
                  {(() => {
                    const matchReg = (pendingRegistrations || []).find((reg) => (
                      reg.status === 'approved'
                      && (
                        (raw?.reg_no && reg.reg_no && reg.reg_no === raw.reg_no)
                        || (raw?.name && reg.machine_name && reg.machine_name === raw.name)
                      )
                    ));
                    const docs = matchReg?.documents && typeof matchReg.documents === 'object'
                      ? matchReg.documents
                      : null;
                    const entries = docs
                      ? Object.entries(docs).filter(([, meta]) => {
                        if (!meta) return false;
                        if (typeof meta === 'string') return Boolean(meta.trim());
                        if (typeof meta === 'object') {
                          return Boolean(meta.url || meta.signedUrl || meta.path || meta.storage_path);
                        }
                        return false;
                      })
                      : [];
                    if (entries.length === 0) {
                      return (
                        <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                          {t('lockedRegistrationDocumentsNone')}
                        </p>
                      );
                    }
                    return (
                      <ul style={{ margin: 0, paddingLeft: 18, color: '#e8e0d0', fontSize: 11 }}>
                        {entries.map(([key, meta]) => {
                          const url = typeof meta === 'string'
                            ? meta
                            : (meta.url || meta.signedUrl || null);
                          const label = key.replace(/_/g, ' ');
                          return (
                            <li key={key} style={{ marginBottom: 4 }}>
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#C9A84C' }}
                                >
                                  {label}
                                </a>
                              ) : (
                                <span>{label} — {t('lockedRegistrationDocumentOnFile')}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    );
                  })()}
                </div>
                </div>
                )}
                </>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* ═══ TAB: REGISTER MACHINE ═══ */}
        {activeTab === 'register' && (
          <div style={s.tableCard}>
            {ownerKycRegistrationBlocked ? (
              <div
                style={{
                  background: 'rgba(233,69,96,0.12)',
                  border: '1px solid rgba(233,69,96,0.35)',
                  color: '#e94560',
                  borderRadius: '10px',
                  padding: '10px 12px',
                  fontSize: '12px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                <p style={{ margin: 0, flex: '1 1 200px' }}>{t('kycRejectedWarning')}</p>
                <button
                  type="button"
                  onClick={() => setActiveTab('kyc')}
                  style={{
                    background: 'transparent',
                    border: '1px solid #e94560',
                    color: '#e94560',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  {t('kycGoToTab')} →
                </button>
              </div>
            ) : (
              <>
            <h3 style={{ ...s.tableTitle, margin: '0 0 12px' }}>📝 {t('machineRegistrationTitle')}</h3>

            {regSuccess && (
              <div style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.4)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <p style={{ color: '#4CAF50', margin: 0, fontSize: '13px' }}>{String.fromCodePoint(0x2705)} {regSuccess}</p>
              </div>
            )}
            {regError && (
              <div style={{ background: 'rgba(233,69,96,0.1)', border: '1px solid rgba(233,69,96,0.4)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <p style={{ color: '#e94560', margin: 0, fontSize: '13px' }}>{regError}</p>
              </div>
            )}

            {/* Steps */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', overflowX: 'auto' }}>
              {REG_STEP_KEYS.map((stepKey, i) => (
                <div key={stepKey} style={{ textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px', fontWeight: '700', fontSize: '13px', background: regStep > i + 1 ? '#4CAF50' : regStep === i + 1 ? '#c9a84c' : 'rgba(255,255,255,0.1)', color: regStep >= i + 1 ? '#0a1628' : '#8896a8', border: regStep === i + 1 ? '2px solid #c9a84c' : 'none' }}>
                    {regStep > i + 1 ? '✓' : i + 1}
                  </div>
                  <p style={{ color: regStep === i + 1 ? '#c9a84c' : '#8896a8', fontSize: '9px', margin: 0, whiteSpace: 'nowrap' }}>{t(stepKey)}</p>
                </div>
              ))}
            </div>

            {/* Step 1 */}
            {regStep === 1 && (
              <div>
                <h4 style={{ color: '#c9a84c', marginBottom: '15px' }}>👤 {t('ownerInfo')}</h4>
                {[
                  { label: t('fullName'), key: 'ownerName', placeholder: 'Rajesh Patil' },
                  { label: t('phone'), key: 'ownerPhone', placeholder: '+91-XXXXXXXXXX' },
                  { label: t('email'), key: 'ownerEmail', placeholder: 'email@gmail.com' },
                ].map(field => (
                  <div key={field.key} style={{ marginBottom: '12px' }}>
                    <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 5px' }}>{field.label}</p>
                    <input style={s.input} placeholder={field.placeholder} value={regData[field.key]} onChange={e => setRegData(prev => ({ ...prev, [field.key]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}

            {/* Step 2 — premium shadcn machine selection */}
            {regStep === 2 && (
              <MachineRegistrationStep2
                ref={regStep2Ref}
                regData={regData}
                setRegData={setRegData}
                setRegDocuments={setRegDocuments}
                setRegError={setRegError}
                emptyRegDocumentsForMobility={emptyRegDocumentsForMobility}
                isMobile={isMobile}
              />
            )}

            {/* Step 3 */}
            {regStep === 3 && (
              <div>
                <h4 style={{ color: '#c9a84c', marginBottom: '15px' }}>🔧 {t('pdiTitle')}</h4>
                {!isRtoApplicable(regData.mobilityClass) && (
                  <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 12px', lineHeight: 1.5 }}>
                    {t('pdiCrawlerHint')}
                  </p>
                )}
                {getPdiChecksForMobility(regData.mobilityClass).map((item) => (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '14px', cursor: 'pointer' }} onClick={() => setRegData(prev => ({ ...prev, [item.key]: !prev[item.key] }))}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '4px', border: '2px solid rgba(201,168,76,0.5)', background: regData[item.key] ? '#c9a84c' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {regData[item.key] && <span style={{ color: '#0a1628', fontSize: '14px', fontWeight: '900' }}>✓</span>}
                    </div>
                    <p style={{ color: '#e8e0d0', fontSize: '13px', margin: 0 }}>{t(item.labelKey)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Step 4 */}
            {regStep === 4 && (
              <div>
                <h4 style={{ color: '#c9a84c', marginBottom: '15px' }}>📄 {t('documentUploadTitle')}</h4>
                <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 12px', lineHeight: 1.5 }}>
                  {isRtoApplicable(regData.mobilityClass)
                    ? t('regDocWheeledHint')
                    : t('regDocCrawlerHint')}
                </p>
                {getDocFieldsForMobility(regData.mobilityClass).map((doc) => {
                  const file = regDocuments[doc.key];
                  const inputId = `owner-reg-doc-${doc.key}`;
                  return (
                    <div key={doc.key} style={{ marginBottom: '10px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ color: '#e8e0d0', fontSize: '13px', margin: '0 0 4px' }}>
                            {t(doc.labelKey)}
                            {!doc.required && <span style={{ color: '#8896a8', fontWeight: 400 }}> {t('optionalLabel')}</span>}
                          </p>
                          {file ? (
                            <p style={{ color: '#4CAF50', fontSize: '11px', margin: 0 }}>
                              {String.fromCodePoint(0x2705)} {file.name} · {formatFileSize(file.size)}
                            </p>
                          ) : (
                            <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{t('noFileSelected')}</p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            id={inputId}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              handleDocumentSelect(doc.key, e.target.files?.[0] || null);
                              e.target.value = '';
                            }}
                          />
                          <button
                            type="button"
                            style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                            onClick={() => document.getElementById(inputId)?.click()}
                          >
                            {file ? `🔄 ${t('replaceFile')}` : `📤 ${t('uploadFile')}`}
                          </button>
                          {file && (
                            <button
                              type="button"
                              style={{ background: 'rgba(233,69,96,0.1)', border: '1px solid rgba(233,69,96,0.3)', color: '#e94560', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                              onClick={() => setRegDocuments((prev) => ({ ...prev, [doc.key]: null }))}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Step 5 */}
            {regStep === 5 && (
              <div>
                <h4 style={{ color: '#c9a84c', marginBottom: '12px' }}>📋 {t('platformAgreement')}</h4>
                <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '14px', marginBottom: '15px', maxHeight: '200px', overflowY: 'auto' }}>
                  {AGREEMENT_TERM_KEYS.map((termKey) => (
                    <p key={termKey} style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 8px', lineHeight: '1.6' }}>• {t(termKey)}</p>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => setRegData(prev => ({ ...prev, agreed: !prev.agreed }))}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '4px', border: '2px solid rgba(201,168,76,0.5)', background: regData.agreed ? '#c9a84c' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {regData.agreed && <span style={{ color: '#0a1628', fontSize: '14px', fontWeight: '900' }}>✓</span>}
                  </div>
                  <p style={{ color: '#e8e0d0', fontSize: '13px', margin: 0 }}>{t('agreeTermsCheckbox')}</p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-5 flex gap-2.5">
              {regStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-white/15 bg-white/[0.04] text-white/80 hover:border-[#C9A84C]/40 hover:bg-white/[0.08] hover:text-white"
                  onClick={() => { setRegError(''); setRegStep((s) => s - 1); }}
                >
                  ← {t('back')}
                </Button>
              )}
              {regStep < 5 ? (
                <Button
                  type="button"
                  className={cn('flex-1 font-bold shadow-[0_4px_20px_rgba(201,168,76,0.25)]', 'bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#d4b65a]')}
                  onClick={handleRegNext}
                >
                  {t('nextText')} →
                </Button>
              ) : (
                <Button
                  type="button"
                  className={cn(
                    'flex-1 font-bold shadow-[0_4px_20px_rgba(201,168,76,0.25)]',
                    'bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#d4b65a]',
                    (!regData.agreed || regSubmitting) && 'opacity-50',
                  )}
                  disabled={!regData.agreed || regSubmitting}
                  onClick={handleRegSubmit}
                >
                  {regSubmitting ? t('loading') : t('regSubmitBtn')}
                </Button>
              )}
            </div>

            {pendingRegistrations.length > 0 && (
              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                <h4 style={{ color: '#c9a84c', margin: '0 0 12px', fontSize: '13px' }}>📋 {t('submittedRegistrations')}</h4>
                {pendingRegistrations.map((reg) => (
                  <div key={reg.id} style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '12px', marginBottom: '8px', border: '1px solid rgba(201,168,76,0.12)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                      <div>
                        <p style={{ color: '#e8e0d0', fontWeight: 600, fontSize: '12px', margin: '0 0 4px' }}>
                          {reg.machine_name} · {reg.reg_no && reg.reg_no !== 'TRACKED-NO-RTO' ? reg.reg_no : t('crawlerNoRto')}
                          {reg.mobility_class === 'tracked' ? ' · 🛤️' : reg.mobility_class === 'wheeled' ? ' · 🛞' : ''}
                        </p>
                        <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{new Date(reg.created_at).toLocaleString('en-IN')}</p>
                        {reg.status === 'rejected' && reg.admin_note ? (
                          <p style={{
                            color: '#e94560',
                            fontSize: '11px',
                            margin: '6px 0 0',
                            lineHeight: 1.4,
                          }}
                          >
                            Reason: {reg.admin_note}
                          </p>
                        ) : null}
                      </div>
                      <span style={{
                        ...s.statusBadge,
                        background: reg.status === 'approved' ? 'rgba(76,175,80,0.15)' : reg.status === 'rejected' ? 'rgba(233,69,96,0.15)' : 'rgba(255,152,0,0.15)',
                        border: `1px solid ${reg.status === 'approved' ? '#4CAF50' : reg.status === 'rejected' ? '#e94560' : '#FF9800'}`,
                        color: reg.status === 'approved' ? '#4CAF50' : reg.status === 'rejected' ? '#e94560' : '#FF9800',
                      }}>
                        {reg.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* ═══ TAB: OPERATORS ═══ */}
        {activeTab === 'operators' && (
          <div style={{ padding: useMobileNav ? 16 : 32 }}>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 24,
              flexWrap: 'wrap',
              gap: 12,
            }}>
              <h2 style={{
                color: '#C9A84C',
                fontSize: 22,
                fontWeight: 700,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <Users size={22} strokeWidth={2} aria-hidden />
                Operators
              </h2>
              <button
                type="button"
                style={s.confirmBtn}
                onClick={() => {
                  setShowAddForm((v) => !v);
                  setOpError('');
                  setOpSuccess('');
                }}
              >
                {showAddForm ? '✕ Cancel' : '+ Add Operator'}
              </button>
            </div>

            <div style={{
              border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              background: 'rgba(201,168,76,0.03)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: 12,
            }}>
              <div>
                <p style={{ ...s.label, marginBottom: 6 }}>{t('payrollExportMonth')}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <select
                    style={{ ...s.input, width: 140, marginBottom: 0 }}
                    value={payrollExportMonthNum}
                    onChange={(e) => setPayrollExportParts(payrollExportYear, Number(e.target.value))}
                  >
                    {PAYROLL_EXPORT_MONTH_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <select
                    style={{ ...s.input, width: 100, marginBottom: 0 }}
                    value={payrollExportYear}
                    onChange={(e) => setPayrollExportParts(Number(e.target.value), payrollExportMonthNum)}
                  >
                    {payrollExportYearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="button"
                style={{
                  ...s.downloadBtn,
                  padding: '10px 16px',
                  fontSize: 12,
                  opacity: payrollExportLoading ? 0.6 : 1,
                }}
                disabled={payrollExportLoading || !payrollExportMonth}
                onClick={handleExportPayrollCsv}
              >
                {payrollExportLoading ? t('loading') : t('exportPayrollCsv')}
              </button>
              {payrollExportError && (
                <p style={{ color: '#e94560', fontSize: 12, margin: 0, flexBasis: '100%' }}>
                  {payrollExportError}
                </p>
              )}
            </div>

            {opSuccess && (
              <div style={{
                border: '1px solid #4CAF50',
                borderRadius: 8,
                padding: 12,
                color: '#4CAF50',
                marginBottom: 16,
              }}>
                {String.fromCodePoint(0x2713)} {opSuccess}
              </div>
            )}
            {opError && (
              <div style={{
                border: '1px solid #e94560',
                borderRadius: 8,
                padding: 12,
                color: '#e94560',
                marginBottom: 16,
              }}>
                {opError}
              </div>
            )}

            {showAddForm && (
              <div style={{
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 12,
                padding: 20,
                marginBottom: 24,
                background: 'rgba(201,168,76,0.03)',
              }}>
                {opStep === 1 && (
                  <div style={{ maxWidth: 480 }}>
                    <p style={{ color: '#aaa', marginBottom: 20 }}>
                      Step 1 of 2 — Operator Details
                    </p>

                    <p style={s.label}>Full Name *</p>
                    <input
                      style={s.input}
                      placeholder={t('operatorFullNamePlaceholder')}
                      value={opData.name}
                      onChange={(e) => setOpData((p) => ({ ...p, name: e.target.value }))}
                    />

                    <p style={s.label}>Mobile Number *</p>
                    <input
                      style={s.input}
                      placeholder="10-digit mobile"
                      maxLength={10}
                      value={opData.phone}
                      onChange={(e) => setOpData((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    />

                    <p style={s.label}>Password *</p>
                    <input
                      style={s.input}
                      type="password"
                      placeholder={t('minPasswordPlaceholder')}
                      value={opData.password}
                      onChange={(e) => setOpData((p) => ({ ...p, password: e.target.value }))}
                    />

                    <p style={s.label}>
                      Years of Experience *
                    </p>
                    <input
                      type="number"
                      style={s.input}
                      placeholder="e.g. 5"
                      min="0"
                      max="50"
                      value={opData.experienceYears}
                      onChange={(e) => setOpData((p) => ({
                        ...p,
                        experienceYears: e.target.value,
                      }))}
                    />

                    <p style={s.label}>{t('monthlySalaryLabel')} *</p>
                    <input
                      type="number"
                      style={s.input}
                      placeholder={t('monthlySalaryPlaceholder')}
                      min="1"
                      step="1"
                      value={opData.monthlySalary}
                      onChange={(e) => setOpData((p) => ({
                        ...p,
                        monthlySalary: e.target.value.replace(/\D/g, '').slice(0, 7),
                      }))}
                    />
                    <p style={{ color: '#556070', fontSize: '11px', margin: '4px 0 0' }}>
                      {t('dailyPayrollHint')}
                    </p>

                    <p style={{ ...s.label, marginTop: 16 }}>
                      Machine Types (select all that apply) *
                    </p>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      marginBottom: 12,
                    }}
                    >
                      {[
                        'JCB', 'Excavator', 'Crane',
                        'Dozer', 'Grader', 'Roller',
                        'Tipper', 'Transit Mixer',
                      ].map((type) => (
                        <label
                          key={type}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            borderRadius: 20,
                            border: opData.experienceMachineTypes.includes(type)
                              ? '1px solid #c9a84c'
                              : '1px solid rgba(255,255,255,0.15)',
                            background: opData.experienceMachineTypes.includes(type)
                              ? 'rgba(201,168,76,0.15)'
                              : 'transparent',
                            color: opData.experienceMachineTypes.includes(type)
                              ? '#c9a84c'
                              : '#8896a8',
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          <input
                            type="checkbox"
                            style={{ display: 'none' }}
                            checked={opData.experienceMachineTypes.includes(type)}
                            onChange={(e) => {
                              setOpData((p) => ({
                                ...p,
                                experienceMachineTypes: e.target.checked
                                  ? [...p.experienceMachineTypes, type]
                                  : p.experienceMachineTypes.filter((t) => t !== type),
                              }));
                            }}
                          />
                          {type}
                        </label>
                      ))}
                    </div>

                    <p style={s.label}>Profile Photo (optional)</p>
                    <input
                      type="file"
                      id="op-photo-input"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        handleOpFileUpload('photo', e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      style={s.confirmBtn}
                      onClick={() => document.getElementById('op-photo-input')?.click()}
                    >
                      {opFiles.photo ? `${String.fromCodePoint(0x2713)} ${opFiles.photo.name}` : 'Upload Photo'}
                    </button>

                    <button
                      type="button"
                      style={{ ...s.confirmBtn, marginTop: 24, width: '100%' }}
                      onClick={() => {
                        const phoneClean = opData.phone.replace(/\D/g, '');
                        if (!opData.name.trim() || opData.name.trim().length < 2) {
                          setOpError('Name required (min 2 characters)');
                          return;
                        }
                        if (phoneClean.length !== 10) {
                          setOpError('Valid 10-digit mobile required');
                          return;
                        }
                        if (opData.password.length < 8) {
                          setOpError('Password min 8 characters');
                          return;
                        }
                        if (!opData.experienceYears || Number(opData.experienceYears) < 0) {
                          setOpError('Experience years required');
                          return;
                        }
                        if (!opData.monthlySalary || Number(opData.monthlySalary) <= 0) {
                          setOpError(t('monthlySalaryRequired'));
                          return;
                        }
                        if (opData.experienceMachineTypes.length === 0) {
                          setOpError('Select at least one machine type');
                          return;
                        }
                        setOpError('');
                        setOpStep(2);
                      }}
                    >
                      Next → Documents
                    </button>
                  </div>
                )}

                {opStep === 2 && (
                  <div style={{ maxWidth: 480 }}>
                    <p style={{ color: '#aaa', marginBottom: 20 }}>
                      Step 2 of 2 — Documents
                    </p>

                    <p style={s.label}>Driving License (PDF/Image) — optional</p>
                    <input
                      type="file"
                      id="op-license-input"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        handleOpFileUpload('license', e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      style={s.confirmBtn}
                      onClick={() => document.getElementById('op-license-input')?.click()}
                    >
                      {opFiles.license ? `${String.fromCodePoint(0x2713)} ${opFiles.license.name}` : 'Upload License'}
                    </button>

                    <p style={{ ...s.label, marginTop: 16 }}>
                      Aadhaar Card — Front Side *
                    </p>
                    <input
                      type="file"
                      id="op-aadhaar-front-input"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        handleOpFileUpload('aadhaarFront', e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      style={s.confirmBtn}
                      onClick={() => document.getElementById('op-aadhaar-front-input')?.click()}
                    >
                      {opFiles.aadhaarFront
                        ? `${String.fromCodePoint(0x2713)} ${opFiles.aadhaarFront.name}`
                        : 'Upload Aadhaar Front'}
                    </button>

                    <p style={{ ...s.label, marginTop: 16 }}>
                      Aadhaar Card — Back Side *
                    </p>
                    <input
                      type="file"
                      id="op-aadhaar-back-input"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        handleOpFileUpload('aadhaarBack', e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      style={s.confirmBtn}
                      onClick={() => document.getElementById('op-aadhaar-back-input')?.click()}
                    >
                      {opFiles.aadhaarBack
                        ? `${String.fromCodePoint(0x2713)} ${opFiles.aadhaarBack.name}`
                        : 'Upload Aadhaar Back'}
                    </button>

                    <p style={{ ...s.label, marginTop: 16 }}>
                      Police Verification Certificate (PDF/Image) — optional
                    </p>
                    <input
                      type="file"
                      id="op-police-input"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        handleOpFileUpload('police', e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      style={s.confirmBtn}
                      onClick={() => document.getElementById('op-police-input')?.click()}
                    >
                      {opFiles.police
                        ? `${String.fromCodePoint(0x2713)} ${opFiles.police.name}`
                        : 'Upload Police Verification'}
                    </button>

                    <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                      <button
                        type="button"
                        style={s.cancelBtn}
                        onClick={() => {
                          setOpStep(1);
                          setOpError('');
                        }}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        style={s.confirmBtn}
                        onClick={handleOpSubmit}
                        disabled={opSubmitting}
                      >
                        {opSubmitting ? 'Creating...' : 'Create Operator'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {opListLoading ? (
              <p style={{ color: '#8896a8' }}>{t('loading')}</p>
            ) : operatorList.length === 0 ? (
              <p style={{ color: '#8896a8' }}>
                No operators yet. Add your first operator.
              </p>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                {operatorList.map((op) => {
                  const selectedPayrollMonth = getPayrollMonthForOperator(op.id);
                  const payrollKey = payrollMonthKey(op.id, selectedPayrollMonth);
                  const payrollPreviewData = payrollPreviewByKey[payrollKey];
                  const payrollPreviewIsLoading = payrollPreviewLoading[payrollKey];
                  const payrollPreviewErr = payrollPreviewError[payrollKey];
                  const payrollIsRunning = payrollRunning[payrollKey];
                  const payrollAlreadyDone = payrollRunCompleted[payrollKey];
                  const payrollCurrentMonth = getCurrentPayrollMonthStr();
                  const payrollPrevMonth = getPreviousPayrollMonthStr(payrollCurrentMonth);
                  const payrollHistoryRows = payrollHistoryByOperator[op.id] || [];
                  const payrollHistoryIsLoading = payrollHistoryLoading[op.id];
                  const payrollHistoryErr = payrollHistoryError[op.id];
                  const attendanceSummary = payrollPreviewData?.attendance_summary || {};
                  const selectedAttendanceMonth = getAttendanceMonthForOperator(op.id);
                  const attendanceKey = payrollMonthKey(op.id, selectedAttendanceMonth);
                  const attendanceData = attendanceByKey[attendanceKey];
                  const attendanceIsLoading = attendanceLoading[attendanceKey];
                  const attendanceErr = attendanceError[attendanceKey];
                  const attendanceRows = attendanceData?.attendance || [];
                  const attendanceMonthSummary = summarizeOwnerAttendanceRows(attendanceRows);
                  const attendanceCurrentMonth = getCurrentPayrollMonthStr();
                  const attendancePrevMonth = getPreviousPayrollMonthStr(attendanceCurrentMonth);
                  const operatorMonthlySalary = Number(op.monthly_salary);
                  const operatorHasSalary = Number.isFinite(operatorMonthlySalary) && operatorMonthlySalary > 0;
                  const operatorDailyRate = operatorHasSalary
                    ? Math.round(operatorMonthlySalary / 26)
                    : null;
                  const plQuotaData = plQuotaByOperator[op.id];
                  const plQuotaIsLoading = plQuotaLoading[op.id];
                  const plQuotaErr = plQuotaError[op.id];

                  return (
                  <div
                    key={op.id}
                    style={{
                      border: '1px solid rgba(201,168,76,0.2)',
                      borderRadius: 10,
                      padding: 16,
                      background: 'rgba(255,255,255,0.02)',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}>
                    <div>
                      <p style={{
                        color: '#e8e0d0',
                        fontWeight: 700,
                        margin: '0 0 4px',
                      }}>
                        {op.name}
                      </p>
                      <p style={{
                        color: '#8896a8',
                        fontSize: 12,
                        margin: '0 0 4px',
                      }}>
                        {op.phone}
                      </p>
                      <div style={{ margin: '6px 0 0' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 8,
                        }}
                        >
                          <p style={{
                            color: '#c9a84c',
                            fontSize: 11,
                            margin: 0,
                            fontWeight: 600,
                          }}
                          >
                            {t('monthlySalaryLabel')}:{' '}
                            {operatorHasSalary
                              ? `₹${operatorMonthlySalary.toLocaleString('en-IN')}${t('perMonthSuffix')}`
                              : t('complianceNotSet')}
                            {' | '}
                            {operatorHasSalary
                              ? `₹${operatorDailyRate.toLocaleString('en-IN')}${t('perDaySuffix')}`
                              : '—'}
                          </p>
                          <button
                            type="button"
                            style={{
                              padding: '2px 8px',
                              borderRadius: 6,
                              fontSize: 10,
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: '1px solid rgba(201,168,76,0.35)',
                              background: salaryEditOperatorId === op.id
                                ? 'rgba(201,168,76,0.2)'
                                : 'transparent',
                              color: '#c9a84c',
                            }}
                            onClick={() => handleOpenSalaryEdit(op.id, op.monthly_salary)}
                          >
                            ✏️ {t('ownerSalaryEditBtn')}
                          </button>
                        </div>
                        {!operatorHasSalary && (
                          <p style={{ color: '#ff9800', fontSize: 10, margin: '4px 0 0' }}>
                            {t('ownerSalaryNotSet')}
                          </p>
                        )}
                        {salaryUpdateSuccess[op.id] && (
                          <p style={{ color: '#4caf50', fontSize: 10, margin: '4px 0 0' }}>
                            {salaryUpdateSuccess[op.id]}
                          </p>
                        )}
                        {salaryEditOperatorId === op.id && (
                          <div style={{
                            marginTop: 8,
                            padding: '10px 12px',
                            borderRadius: 8,
                            border: '1px solid rgba(201,168,76,0.25)',
                            background: 'rgba(201,168,76,0.05)',
                          }}
                          >
                            {salaryEditError && (
                              <p style={{ color: '#e94560', fontSize: 11, margin: '0 0 8px' }}>
                                {salaryEditError}
                              </p>
                            )}
                            <p style={{ ...s.label, marginBottom: 4, fontSize: 11 }}>
                              {t('monthlySalaryLabel')}
                            </p>
                            <div style={{
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap',
                              alignItems: 'center',
                            }}
                            >
                              <input
                                type="number"
                                style={{ ...s.input, fontSize: 12, padding: '6px 10px', width: 120 }}
                                placeholder={t('monthlySalaryPlaceholder')}
                                min="1"
                                value={salaryEditAmount}
                                onChange={(e) => setSalaryEditAmount(
                                  e.target.value.replace(/\D/g, '').slice(0, 7),
                                )}
                              />
                              <span style={{ color: '#8896a8', fontSize: 12 }}>₹</span>
                              <button
                                type="button"
                                style={{ ...s.confirmBtn, fontSize: 11, padding: '6px 12px' }}
                                disabled={salaryEditSubmitting}
                                onClick={() => handleSaveOperatorSalary(op.id)}
                              >
                                {salaryEditSubmitting ? t('saving') : t('ownerAttendanceSave')}
                              </button>
                              <button
                                type="button"
                                style={{ ...s.cancelBtn, fontSize: 11, padding: '6px 12px' }}
                                disabled={salaryEditSubmitting}
                                onClick={handleCancelSalaryEdit}
                              >
                                {t('cancel')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <p style={{
                        color: Number(op.advance_balance) > 0 ? '#ff9800' : '#8896a8',
                        fontSize: 11,
                        margin: '4px 0 0',
                        fontWeight: Number(op.advance_balance) > 0 ? 600 : 400,
                      }}
                      >
                        {t('advanceBalance')}: ₹{Number(op.advance_balance || 0).toLocaleString('en-IN')}
                      </p>
                      {Number(op.advance_balance) > 0 && (
                        <p style={{
                          color: '#8896a8',
                          fontSize: 10,
                          margin: '2px 0 0',
                          lineHeight: 1.4,
                        }}
                        >
                          Recoverable from next salary payment
                        </p>
                      )}
                      <span style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 20,
                        background: op.status === 'active'
                          ? 'rgba(76,175,80,0.15)'
                          : 'rgba(255,152,0,0.15)',
                        color: op.status === 'active'
                          ? '#4caf50'
                          : '#ff9800',
                      }}>
                        {op.status === 'active'
                          ? '✓ Active'
                          : '⏳ Pending Approval'}
                      </span>
                      {op.experience_years > 0 && (
                        <p style={{
                          color: '#8896a8',
                          fontSize: 11,
                          margin: '4px 0 0',
                        }}
                        >
                          {op.experience_years} yr exp
                          {op.experience_machine_types?.length > 0
                            ? ` · ${op.experience_machine_types.slice(0, 2).join(', ')}${op.experience_machine_types.length > 2 ? ` +${op.experience_machine_types.length - 2}` : ''}`
                            : ''}
                        </p>
                      )}
                      {op.created_at && (
                        <p style={{
                          color: '#556070',
                          fontSize: 11,
                          margin: '2px 0 0',
                        }}
                        >
                          {getDaysWithUs(op.created_at)} days with DE
                        </p>
                      )}
                      {(() => {
                        const star = getOperatorStar(op);
                        return star.label !== 'New' ? (
                          <p style={{
                            color: star.color,
                            fontSize: 11,
                            margin: '2px 0 0',
                            fontWeight: 600,
                          }}
                          >
                            {star.stars} {star.label}
                          </p>
                        ) : null;
                      })()}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12, flexShrink: 0, minWidth: 140 }}>
                      {assignError && assigningOperatorId === op.id && (
                        <p style={{ color: '#e94560', fontSize: 11, margin: '0 0 4px' }}>
                          {assignError}
                        </p>
                      )}

                      {op.assignedMachine ? (
                        <div>
                          <p style={{ color: '#4caf50', margin: '0 0 6px' }}>
                            ✓ {op.assignedMachine.name}
                          </p>
                          {op.status === 'active' && (
                            <button
                              type="button"
                              style={{ ...s.cancelBtn, fontSize: 11, padding: '3px 10px' }}
                              onClick={() => handleUnassignMachine(op.assignedMachine.id)}
                            >
                              Unassign
                            </button>
                          )}
                        </div>
                      ) : (
                        <div>
                          <p style={{ color: '#8896a8', margin: '0 0 6px' }}>
                            No machine assigned
                          </p>
                          {op.status === 'active' && (
                            assigningOperatorId === op.id ? (
                              <div>
                                <select
                                  style={{ ...s.input, fontSize: 12, padding: '4px 8px', marginBottom: 6 }}
                                  defaultValue=""
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleAssignMachine(op.id, e.target.value);
                                    }
                                  }}
                                >
                                  <option value="" disabled>
                                    Select machine...
                                  </option>
                                  {machineData
                                    .filter((m) => !m.operator_id && m.owner_id === (sessionUser.id || sessionUser.user_id))
                                    .map((m) => (
                                      <option key={m.id} value={m.id}>
                                        {m.name} ({m.machine_id})
                                      </option>
                                    ))}
                                </select>
                                <button
                                  type="button"
                                  style={{ ...s.cancelBtn, fontSize: 11, padding: '3px 10px' }}
                                  onClick={() => {
                                    setAssigningOperatorId(null);
                                    setAssignError('');
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                style={{ ...s.confirmBtn, fontSize: 11, padding: '4px 12px' }}
                                onClick={() => {
                                  setAssigningOperatorId(op.id);
                                  setAssignError('');
                                }}
                              >
                                Assign Machine
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                    </div>

                    {op.status === 'active' && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: '1px solid rgba(201,168,76,0.15)',
                      }}
                      >
                        <button
                          type="button"
                          style={{
                            ...operatorCardActionBtn(
                              payrollPanelOperatorId === op.id,
                              '#4caf50',
                            ),
                            ...(!operatorHasSalary ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                          }}
                          disabled={!operatorHasSalary}
                          title={!operatorHasSalary ? t('ownerSalaryNotSet') : undefined}
                          onClick={() => handleOpenPayrollPanel(op.id)}
                        >
                          {t('payrollPreview')}
                        </button>
                        <button
                          type="button"
                          style={operatorCardActionBtn(
                            advancePanelOperatorId === op.id,
                            '#e94560',
                          )}
                          onClick={() => handleOpenAdvancePanel(op.id)}
                        >
                          {advancePanelOperatorId === op.id ? t('closeAdvance') : t('giveAdvance')}
                        </button>
                        <button
                          type="button"
                          style={operatorCardActionBtn(
                            attendancePanelOperatorId === op.id,
                            '#2196f3',
                          )}
                          onClick={() => handleOpenAttendancePanel(op.id)}
                        >
                          {t('ownerAttendanceTitle')}
                        </button>
                      </div>
                    )}

                    {advancePanelOperatorId === op.id && (
                      <div style={{
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: '1px solid rgba(201,168,76,0.15)',
                      }}
                      >
                        {advanceError && (
                          <p style={{ color: '#e94560', fontSize: 12, margin: '0 0 8px' }}>
                            {advanceError}
                          </p>
                        )}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: useMobileNav ? '1fr' : '1fr 1fr 1fr auto',
                          gap: 10,
                          alignItems: 'end',
                          marginBottom: 16,
                        }}
                        >
                          <div>
                            <p style={{ ...s.label, marginBottom: 4 }}>{t('amountLabel')} *</p>
                            <input
                              type="number"
                              style={s.input}
                              placeholder={t('amountPlaceholder')}
                              min="1"
                              value={advanceForm.amount}
                              onChange={(e) => setAdvanceForm((p) => ({
                                ...p,
                                amount: e.target.value.replace(/\D/g, '').slice(0, 7),
                              }))}
                            />
                          </div>
                          <div>
                            <p style={{ ...s.label, marginBottom: 4 }}>{t('noteOptional')}</p>
                            <input
                              style={s.input}
                              placeholder={t('advanceNotePlaceholder')}
                              maxLength={500}
                              value={advanceForm.note}
                              onChange={(e) => setAdvanceForm((p) => ({ ...p, note: e.target.value }))}
                            />
                          </div>
                          <div style={{
                            padding: '10px 12px',
                            borderRadius: 8,
                            background: 'rgba(201,168,76,0.08)',
                            border: '1px solid rgba(201,168,76,0.2)',
                          }}
                          >
                            <p style={{ color: '#8896a8', fontSize: 10, margin: '0 0 2px' }}>{t('outstandingBalance')}</p>
                            <p style={{ color: '#c9a84c', fontWeight: 700, margin: 0, fontSize: 14 }}>
                              ₹{Number(
                                advanceLedger[op.id]?.advance_balance ?? op.advance_balance ?? 0,
                              ).toLocaleString('en-IN')}
                            </p>
                          </div>
                          <button
                            type="button"
                            style={{ ...s.confirmBtn, padding: '10px 16px' }}
                            disabled={advanceSubmitting}
                            onClick={() => handleGiveAdvance(op.id)}
                          >
                            {advanceSubmitting ? t('saving') : t('recordAdvance')}
                          </button>
                        </div>

                        <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 8px', fontWeight: 600 }}>
                          {t('recentTransactions')}
                        </p>
                        {advanceLedgerLoading[op.id] ? (
                          <p style={{ color: '#8896a8', fontSize: 12 }}>{t('loading')}</p>
                        ) : (advanceLedger[op.id]?.advances || []).length === 0 ? (
                          <p style={{ color: '#556070', fontSize: 12 }}>{t('noAdvancesYet')}</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {(advanceLedger[op.id]?.advances || []).slice(0, 10).map((txn) => (
                              <div
                                key={txn.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  gap: 8,
                                  flexWrap: 'wrap',
                                  padding: '8px 10px',
                                  borderRadius: 6,
                                  background: 'rgba(0,0,0,0.2)',
                                  fontSize: 11,
                                }}
                              >
                                <div>
                                  <span style={{
                                    color: txn.txn_type === 'advance' ? '#ff9800' : '#4caf50',
                                    fontWeight: 600,
                                  }}
                                  >
                                    {txn.txn_type === 'advance' ? t('advanceTxnLabel') : t('deductionTxnLabel')}
                                  </span>
                                  {txn.note && (
                                    <span style={{ color: '#8896a8', marginLeft: 8 }}>{txn.note}</span>
                                  )}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <span style={{ color: '#e8e0d0', fontWeight: 600 }}>
                                    ₹{Number(txn.amount).toLocaleString('en-IN')}
                                  </span>
                                  <span style={{ color: '#556070', marginLeft: 8 }}>
                                    {new Date(txn.created_at).toLocaleDateString('en-IN')}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {payrollPanelOperatorId === op.id && (
                      <div style={{
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: '1px solid rgba(76,175,80,0.2)',
                      }}
                      >
                        <p style={{ color: '#c9a84c', fontSize: 12, fontWeight: 600, margin: '0 0 10px' }}>
                          {t('payrollPreview')}
                        </p>

                        {!operatorHasSalary && (
                          <p style={{
                            color: '#ff9800',
                            fontSize: 12,
                            margin: '0 0 10px',
                            padding: '8px 12px',
                            borderRadius: 8,
                            background: 'rgba(255,152,0,0.1)',
                            border: '1px solid rgba(255,152,0,0.35)',
                          }}
                          >
                            {t('ownerSalaryNotSet')}
                          </p>
                        )}

                        <p style={{ ...s.label, marginBottom: 6 }}>{t('payrollMonth')}</p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                          <button
                            type="button"
                            style={{
                              ...s.cancelBtn,
                              fontSize: 11,
                              padding: '6px 12px',
                              borderColor: selectedPayrollMonth === payrollPrevMonth ? '#4caf50' : undefined,
                              color: selectedPayrollMonth === payrollPrevMonth ? '#4caf50' : undefined,
                            }}
                            onClick={() => handlePayrollMonthChange(op.id, payrollPrevMonth)}
                          >
                            {payrollPrevMonth}
                          </button>
                          <button
                            type="button"
                            style={{
                              ...s.cancelBtn,
                              fontSize: 11,
                              padding: '6px 12px',
                              borderColor: selectedPayrollMonth === payrollCurrentMonth ? '#4caf50' : undefined,
                              color: selectedPayrollMonth === payrollCurrentMonth ? '#4caf50' : undefined,
                            }}
                            onClick={() => handlePayrollMonthChange(op.id, payrollCurrentMonth)}
                          >
                            {payrollCurrentMonth}
                          </button>
                        </div>

                        {payrollPreviewIsLoading && (
                          <p style={{ color: '#8896a8', fontSize: 12 }}>{t('loading')}</p>
                        )}
                        {payrollPreviewErr && (
                          <p style={{ color: '#e94560', fontSize: 12, margin: '0 0 8px' }}>{payrollPreviewErr}</p>
                        )}
                        {payrollRunError[op.id] && (
                          <p style={{ color: '#e94560', fontSize: 12, margin: '0 0 8px' }}>{payrollRunError[op.id]}</p>
                        )}
                        {payrollRunSuccess[op.id] && (
                          <p style={{ color: '#4caf50', fontSize: 12, margin: '0 0 8px' }}>{payrollRunSuccess[op.id]}</p>
                        )}

                        {payrollPreviewData && !payrollPreviewIsLoading && (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: useMobileNav ? '1fr' : '1fr 1fr',
                            gap: 10,
                            marginBottom: 14,
                            fontSize: 12,
                          }}
                          >
                            <p style={{ color: '#8896a8', margin: 0 }}>
                              {t('monthlySalaryLabel')}:{' '}
                              <strong style={{ color: '#e8e0d0' }}>
                                ₹{Number(payrollPreviewData.monthly_salary || 0).toLocaleString('en-IN')}
                              </strong>
                            </p>
                            <p style={{ color: '#8896a8', margin: 0 }}>
                              {t('payrollDailyRate')}:{' '}
                              <strong style={{ color: '#e8e0d0' }}>
                                ₹{Number(payrollPreviewData.daily_rate || 0).toLocaleString('en-IN')}
                              </strong>
                            </p>
                            <p style={{ color: '#8896a8', margin: 0, gridColumn: useMobileNav ? 'auto' : '1 / -1' }}>
                              {t('attendanceStatusSection')}:{' '}
                              {t('presentLabel')}: {attendanceSummary.present ?? 0}
                              {' | '}
                              {t('halfDayLabel')}: {attendanceSummary.halfday ?? 0}
                              {' | '}
                              {t('paidLeaveLabel')}: {attendanceSummary.paid_leave ?? 0}
                              {' | '}
                              {t('unpaidLeaveLabel')}: {attendanceSummary.unpaid_leave ?? 0}
                              {' | '}
                              {t('absentLabel')}: {attendanceSummary.absent ?? 0}
                            </p>
                            <p style={{ color: '#8896a8', margin: 0 }}>
                              {t('payrollGross')}:{' '}
                              <strong style={{ color: '#c9a84c' }}>
                                ₹{Number(payrollPreviewData.gross_payable || 0).toLocaleString('en-IN')}
                              </strong>
                            </p>
                            <p style={{ color: '#8896a8', margin: 0 }}>
                              {t('advanceBalance')}:{' '}
                              <strong style={{ color: '#ff9800' }}>
                                ₹{Number(payrollPreviewData.advance_balance || 0).toLocaleString('en-IN')}
                              </strong>
                            </p>
                            <p style={{
                              color: '#8896a8',
                              margin: 0,
                              gridColumn: useMobileNav ? 'auto' : '1 / -1',
                              padding: '10px 12px',
                              borderRadius: 8,
                              background: 'rgba(76,175,80,0.1)',
                              border: '1px solid rgba(76,175,80,0.35)',
                            }}
                            >
                              {t('payrollNet')}:{' '}
                              <strong style={{ color: '#4caf50', fontSize: 14 }}>
                                ₹{Number(payrollPreviewData.net_payable || 0).toLocaleString('en-IN')}
                              </strong>
                            </p>
                          </div>
                        )}

                        <button
                          type="button"
                          style={{
                            ...s.confirmBtn,
                            fontSize: 12,
                            padding: '8px 16px',
                            opacity: (payrollIsRunning || payrollAlreadyDone || payrollPreviewIsLoading || !operatorHasSalary) ? 0.6 : 1,
                          }}
                          disabled={
                            !operatorHasSalary
                            || payrollIsRunning
                            || payrollAlreadyDone
                            || payrollPreviewIsLoading
                            || !payrollPreviewData
                            || Number(payrollPreviewData?.gross_payable || 0) <= 0
                          }
                          onClick={() => handleRunPayroll(op.id)}
                        >
                          {payrollIsRunning
                            ? t('loading')
                            : payrollAlreadyDone
                              ? t('payrollAlreadyRun')
                              : `${t('payrollRun')} — ${selectedPayrollMonth}`}
                        </button>

                        <div style={{ marginTop: 16 }}>
                          <p style={{ color: '#c9a84c', fontSize: 12, fontWeight: 600, margin: '0 0 10px' }}>
                            {t('payrollHistory')}
                          </p>
                          {payrollHistoryIsLoading && (
                            <p style={{ color: '#8896a8', fontSize: 12, margin: 0 }}>{t('loading')}</p>
                          )}
                          {payrollHistoryErr && (
                            <p style={{ color: '#e94560', fontSize: 12, margin: '0 0 8px' }}>{payrollHistoryErr}</p>
                          )}
                          {!payrollHistoryIsLoading && !payrollHistoryErr && payrollHistoryRows.length === 0 && (
                            <p style={{ color: '#8896a8', fontSize: 12, margin: 0 }}>{t('payrollNoHistory')}</p>
                          )}
                          {!payrollHistoryIsLoading && payrollHistoryRows.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480, fontSize: 11 }}>
                                <thead>
                                  <tr>
                                    {[
                                      t('payrollColMonth'),
                                      t('payrollColGross'),
                                      t('payrollColDeduction'),
                                      t('payrollColNet'),
                                      t('payrollColDate'),
                                    ].map((h) => (
                                      <th
                                        key={h}
                                        style={{
                                          textAlign: 'left',
                                          padding: '8px 6px',
                                          color: '#8896a8',
                                          borderBottom: '1px solid rgba(201,168,76,0.15)',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {payrollHistoryRows.map((row) => (
                                    <tr key={row.id}>
                                      <td style={{ padding: '8px 6px', color: '#e8e0d0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        {row.month || '—'}
                                      </td>
                                      <td style={{ padding: '8px 6px', color: '#c9a84c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        ₹{Number(row.gross_payable || 0).toLocaleString('en-IN')}
                                      </td>
                                      <td style={{ padding: '8px 6px', color: '#ff9800', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        ₹{Number(row.advance_deducted || 0).toLocaleString('en-IN')}
                                      </td>
                                      <td style={{ padding: '8px 6px', color: '#4caf50', fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        ₹{Number(row.net_pay || 0).toLocaleString('en-IN')}
                                      </td>
                                      <td style={{ padding: '8px 6px', color: '#8896a8', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        {row.created_at
                                          ? new Date(row.created_at).toLocaleDateString('en-IN', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                          })
                                          : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {attendancePanelOperatorId === op.id && (
                      <div style={{
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: '1px solid rgba(33,150,243,0.25)',
                      }}
                      >
                        <p style={{ color: '#2196f3', fontSize: 12, fontWeight: 600, margin: '0 0 10px' }}>
                          {t('ownerAttendanceTitle')}
                        </p>

                        <div style={{ marginBottom: 14 }}>
                          <p style={{ color: '#8896a8', fontSize: 10, fontWeight: 600, margin: '0 0 6px' }}>
                            {t('plQuotaLabel')}
                          </p>
                          {plQuotaIsLoading && (
                            <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>{t('loading')}</p>
                          )}
                          {plQuotaErr && (
                            <p style={{ color: '#e94560', fontSize: 11, margin: '0 0 6px' }}>{plQuotaErr}</p>
                          )}
                          {plQuotaData && !plQuotaIsLoading && (
                            <div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 8,
                              }}
                              >
                                <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                                  {t('paidLeaveLabel')}: {plQuotaData.pl_used} {t('plUsed')} / {plQuotaData.annual_pl_quota} {t('plAllowed')} ({plQuotaData.pl_remaining} {t('plRemaining')})
                                </p>
                                <button
                                  type="button"
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    border: '1px solid rgba(33,150,243,0.35)',
                                    background: plQuotaEditOperatorId === op.id
                                      ? 'rgba(33,150,243,0.2)'
                                      : 'transparent',
                                    color: '#2196f3',
                                  }}
                                  onClick={() => handleOpenPlQuotaEdit(op.id, plQuotaData.annual_pl_quota)}
                                >
                                  ✏️
                                </button>
                              </div>
                              {plQuotaUpdateSuccess[op.id] && (
                                <p style={{ color: '#4caf50', fontSize: 10, margin: '4px 0 0' }}>
                                  {plQuotaUpdateSuccess[op.id]}
                                </p>
                              )}
                              {plQuotaEditOperatorId === op.id && (
                                <div style={{
                                  marginTop: 8,
                                  padding: '10px 12px',
                                  borderRadius: 8,
                                  border: '1px solid rgba(33,150,243,0.25)',
                                  background: 'rgba(33,150,243,0.05)',
                                }}
                                >
                                  {plQuotaEditError && (
                                    <p style={{ color: '#e94560', fontSize: 11, margin: '0 0 8px' }}>
                                      {plQuotaEditError}
                                    </p>
                                  )}
                                  <p style={{ ...s.label, marginBottom: 4, fontSize: 11 }}>
                                    {t('plQuotaLabel')}
                                  </p>
                                  <div style={{
                                    display: 'flex',
                                    gap: 8,
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                  }}
                                  >
                                    <input
                                      type="number"
                                      style={{ ...s.input, fontSize: 12, padding: '6px 10px', width: 80 }}
                                      min="0"
                                      value={plQuotaEditAmount}
                                      onChange={(e) => setPlQuotaEditAmount(
                                        e.target.value.replace(/\D/g, '').slice(0, 3),
                                      )}
                                    />
                                    <button
                                      type="button"
                                      style={{ ...s.confirmBtn, fontSize: 11, padding: '6px 12px' }}
                                      disabled={plQuotaEditSubmitting}
                                      onClick={() => handleSavePlQuota(op.id)}
                                    >
                                      {plQuotaEditSubmitting ? t('saving') : t('ownerAttendanceSave')}
                                    </button>
                                    <button
                                      type="button"
                                      style={{ ...s.cancelBtn, fontSize: 11, padding: '6px 12px' }}
                                      disabled={plQuotaEditSubmitting}
                                      onClick={handleCancelPlQuotaEdit}
                                    >
                                      {t('cancel')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                          <button
                            type="button"
                            style={{
                              ...s.cancelBtn,
                              fontSize: 11,
                              padding: '6px 12px',
                              borderColor: selectedAttendanceMonth === attendancePrevMonth ? '#2196f3' : undefined,
                              color: selectedAttendanceMonth === attendancePrevMonth ? '#2196f3' : undefined,
                            }}
                            onClick={() => handleAttendanceMonthChange(op.id, attendancePrevMonth)}
                          >
                            {attendancePrevMonth}
                          </button>
                          <button
                            type="button"
                            style={{
                              ...s.cancelBtn,
                              fontSize: 11,
                              padding: '6px 12px',
                              borderColor: selectedAttendanceMonth === attendanceCurrentMonth ? '#2196f3' : undefined,
                              color: selectedAttendanceMonth === attendanceCurrentMonth ? '#2196f3' : undefined,
                            }}
                            onClick={() => handleAttendanceMonthChange(op.id, attendanceCurrentMonth)}
                          >
                            {attendanceCurrentMonth}
                          </button>
                        </div>

                        {attendanceIsLoading && (
                          <p style={{ color: '#8896a8', fontSize: 12 }}>{t('loading')}</p>
                        )}
                        {attendanceErr && (
                          <p style={{ color: '#e94560', fontSize: 12, margin: '0 0 8px' }}>{attendanceErr}</p>
                        )}
                        {attendanceSaveSuccess[op.id] && (
                          <p style={{ color: '#4caf50', fontSize: 12, margin: '0 0 8px' }}>
                            {attendanceSaveSuccess[op.id]}
                          </p>
                        )}

                        {!attendanceIsLoading && !attendanceErr && attendanceRows.length === 0 && (
                          <p style={{ color: '#8896a8', fontSize: 12, margin: '0 0 8px' }}>
                            {t('ownerAttendanceNoRecords')}
                          </p>
                        )}

                        {!attendanceIsLoading && attendanceRows.length > 0 && (
                          <>
                            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520, fontSize: 11 }}>
                                <thead>
                                  <tr>
                                    {[t('date'), t('status'), t('clockInLabel'), t('clockOutLabel'), t('hmrCol'), t('ownerAttendanceEdit')].map((h) => (
                                      <th
                                        key={h}
                                        style={{
                                          textAlign: 'left',
                                          padding: '8px 6px',
                                          color: '#8896a8',
                                          borderBottom: '1px solid rgba(201,168,76,0.15)',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {attendanceRows.map((row) => {
                                    const dateKey = String(row.date || '').slice(0, 10);
                                    const statusKey = String(row.status || 'absent').toLowerCase();
                                    const draftStatus = attendanceEditDraft[op.id]?.[dateKey] ?? statusKey;
                                    const rowSaveKey = `${op.id}:${dateKey}`;
                                    const isSavingRow = attendanceSavingKey === rowSaveKey;
                                    const statusChanged = draftStatus !== statusKey;
                                    return (
                                      <tr key={row.id || dateKey}>
                                        <td style={{ padding: '8px 6px', color: '#e8e0d0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                          {dateKey
                                            ? new Date(`${dateKey}T00:00:00`).toLocaleDateString('en-IN', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric',
                                            })
                                            : '—'}
                                        </td>
                                        <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                          <span style={{
                                            display: 'inline-block',
                                            padding: '2px 8px',
                                            borderRadius: 12,
                                            fontSize: 10,
                                            fontWeight: 600,
                                            color: ownerAttendanceStatusColor(statusKey),
                                            background: `${ownerAttendanceStatusColor(statusKey)}18`,
                                            border: `1px solid ${ownerAttendanceStatusColor(statusKey)}33`,
                                          }}
                                          >
                                            {formatOwnerAttendanceStatus(statusKey, t)}
                                          </span>
                                        </td>
                                        <td style={{ padding: '8px 6px', color: '#8896a8', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <span>
                                              {row.check_in ? `${formatTimeHHMM(row.check_in)} IST` : '—'}
                                              {row.owner_proposed_check_in
                                                && !row.operator_correction_confirmed
                                                && !row.correction_rejected
                                                ? (
                                                  <span style={{ color: '#c9a84c', marginLeft: 6 }}>
                                                    → {formatTimeHHMM(row.owner_proposed_check_in)} (pending)
                                                  </span>
                                                )
                                                : null}
                                            </span>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                              <input
                                                type="time"
                                                step="1"
                                                style={{ ...s.input, fontSize: 11, padding: '4px 8px', width: 120 }}
                                                value={
                                                  attendanceProposeDraft[op.id]?.[dateKey]
                                                  ?? (row.check_in ? String(row.check_in).slice(0, 8) : '')
                                                }
                                                onChange={(e) => {
                                                  setAttendanceProposeDraft((prev) => ({
                                                    ...prev,
                                                    [op.id]: {
                                                      ...(prev[op.id] || {}),
                                                      [dateKey]: e.target.value,
                                                    },
                                                  }));
                                                }}
                                              />
                                              <button
                                                type="button"
                                                style={{ ...s.confirmBtn, fontSize: 10, padding: '4px 10px' }}
                                                disabled={
                                                  attendanceProposeSavingKey === `${op.id}:${dateKey}`
                                                  || !(attendanceProposeDraft[op.id]?.[dateKey]
                                                    || row.check_in)
                                                }
                                                onClick={() => handleProposeAttendanceCorrection(
                                                  op.id,
                                                  dateKey,
                                                  row.check_in ? String(row.check_in).slice(0, 8) : '',
                                                )}
                                              >
                                                {attendanceProposeSavingKey === `${op.id}:${dateKey}`
                                                  ? t('saving')
                                                  : 'Propose Correction'}
                                              </button>
                                            </div>
                                            {attendanceProposeError[`${op.id}:${dateKey}`] ? (
                                              <span style={{ color: '#e94560', fontSize: 10 }}>
                                                {attendanceProposeError[`${op.id}:${dateKey}`]}
                                              </span>
                                            ) : null}
                                          </div>
                                        </td>
                                        <td style={{ padding: '8px 6px', color: '#8896a8', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                          {row.check_out ? `${formatTimeHHMM(row.check_out)} IST` : '—'}
                                        </td>
                                        <td style={{
                                          padding: '8px 6px',
                                          color: '#c9a84c',
                                          fontWeight: 700,
                                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                                        }}
                                        >
                                          {formatOwnerAttendanceHmrDisplay(row.hmr)}
                                        </td>
                                        <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <select
                                              style={{ ...s.input, fontSize: 11, padding: '4px 8px', minWidth: 110 }}
                                              value={draftStatus}
                                              onChange={(e) => {
                                                setAttendanceEditDraft((prev) => ({
                                                  ...prev,
                                                  [op.id]: {
                                                    ...(prev[op.id] || {}),
                                                    [dateKey]: e.target.value,
                                                  },
                                                }));
                                              }}
                                            >
                                              {OWNER_ATTENDANCE_STATUSES.map((statusOption) => (
                                                <option key={statusOption} value={statusOption}>
                                                  {formatOwnerAttendanceStatus(statusOption, t)}
                                                </option>
                                              ))}
                                            </select>
                                            <button
                                              type="button"
                                              style={{ ...s.confirmBtn, fontSize: 10, padding: '4px 10px' }}
                                              disabled={!statusChanged || isSavingRow}
                                              onClick={() => handleSaveAttendanceStatus(op.id, dateKey)}
                                            >
                                              {isSavingRow ? t('saving') : t('ownerAttendanceSave')}
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                              {t('presentLabel')}: {attendanceMonthSummary.present}
                              {' | '}
                              {t('halfDayLabel')}: {attendanceMonthSummary.halfday}
                              {' | '}
                              {t('absentLabel')}: {attendanceMonthSummary.absent}
                              {' | '}
                              {t('paidLeaveLabel')}/{t('unpaidLeaveLabel')}: {attendanceMonthSummary.leave}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ TAB: GPS TRACKING ═══ */}
        {activeTab === 'tracking' && (
          <div>
            {displayMachines.length === 0 && (
              <div style={{ ...s.tableCard, textAlign: 'center', padding: '32px' }}>
                <p style={{ fontSize: '32px', margin: '0 0 8px' }}>{String.fromCodePoint(0x1F4CD)}</p>
                <p style={{ color: '#c9a84c', fontWeight: 700, margin: '0 0 4px' }}>{t('noMachinesTrack')}</p>
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('noMachinesTrackHint')}</p>
              </div>
            )}
            {displayMachines.map((m, i) => {
              const siteLat = Number(m.activeBooking?.site_lat);
              const siteLng = Number(m.activeBooking?.site_lng);
              const hasSiteCoords = Number.isFinite(siteLat) && Number.isFinite(siteLng);
              const isActiveStatus = String(m.status).toLowerCase() === 'active';
              return (
                <div key={i} style={{ ...s.tableCard, marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    <h3 style={{ color: '#c9a84c', margin: 0, fontSize: '14px' }}>📍 {m.id} — {m.name}</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                      <span style={{
                        background: isActiveStatus ? 'rgba(76,175,80,0.15)' : 'rgba(136,150,168,0.15)',
                        border: `1px solid ${isActiveStatus ? '#4CAF50' : '#8896a8'}`,
                        color: isActiveStatus ? '#4CAF50' : '#8896a8',
                        padding: '3px 10px',
                        borderRadius: '20px',
                        fontSize: '11px',
                      }}
                      >
                        {statusLabel(m.status, t)}
                      </span>
                      {m.activeBooking && bookingNeedsLiveSync(m.activeBooking) && (
                        <span style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid #4CAF50', color: '#4CAF50', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{t('gpsLiveLabel')}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '8px', marginBottom: '10px' }}>
                    {[
                      { label: `🔑 ${t('ignition')}`, val: m.status === 'Active' ? t('on') : t('off') },
                      { label: `⛽ ${t('fuel')}`, val: fuelDisplay(m.fuel, m.fuelCapacity) },
                      { label: `⏱️ ${t('hmrToday')}`, val: formatHoursHm(m.hmr) },
                      { label: `👷 ${t('operator')}`, val: m.operator?.name || '--' },
                    ].map((d, j) => (
                      <div key={j} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                        <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 2px' }}>{d.label}</p>
                        <p style={{ color: '#c9a84c', fontWeight: '700', fontSize: '13px', margin: 0 }}>{d.val}</p>
                      </div>
                    ))}
                  </div>
                  {m.activeBooking ? (
                    <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '8px', padding: '12px', border: '1px solid rgba(201,168,76,0.15)', marginBottom: '10px' }}>
                      <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{t('siteAddressLabel')}</p>
                      <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 600, margin: '0 0 8px', lineHeight: 1.4, wordBreak: 'break-word' }}>
                        {m.activeBooking.site_address || t('siteLocationNotCaptured')}
                      </p>
                      {hasSiteCoords && (
                        <a
                          href={`https://www.google.com/maps?q=${siteLat},${siteLng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'inline-block', marginBottom: '8px', color: '#c9a84c', fontSize: '12px', fontWeight: 600 }}
                        >
                          {t('navigateToSite')}
                        </a>
                      )}
                      <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>
                        {t('bookingRefLabel')}: <span style={{ color: '#c9a84c' }}>{m.activeBooking.booking_ref || '—'}</span>
                      </p>
                    </div>
                  ) : (
                    <p style={{ color: '#8896a8', fontSize: '12px', textAlign: 'center', margin: '0 0 10px', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                      {t('noActiveBookingIdle')}
                    </p>
                  )}
                  <button
                    type="button"
                    style={{ width: '100%', padding: '9px', background: 'rgba(233,69,96,0.1)', border: '1px solid rgba(233,69,96,0.4)', color: '#e94560', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                    onClick={() => alert(t('remoteLockAlert'))}
                  >
                    {t('remoteLockMachine')}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ TAB: PAYOUT WALLET ═══ */}
        {settlementFlash && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <div style={{ ...s.tableCard, maxWidth: '400px', width: '100%', textAlign: 'center', border: '2px solid #c9a84c' }}>
              <p style={{ fontSize: '40px', margin: '0 0 8px' }}>{String.fromCodePoint(0x1F4B0)}</p>
              <h2 style={{ color: '#c9a84c', margin: '0 0 10px', fontSize: '20px' }}>{t('ownerPaymentReceivedTitle')}</h2>
              <p style={{ color: '#e8e0d0', fontSize: '13px', lineHeight: 1.5, margin: '0 0 12px' }}>
                {t('ownerPaymentReceivedBody')
                  .replace('{gross}', settlementFlash.gross.toLocaleString('en-IN'))
                  .replace('{net}', settlementFlash.net.toLocaleString('en-IN'))}
              </p>
              {settlementFlash.pending && (
                <p style={{ color: '#FF9800', fontSize: '11px', margin: '0 0 12px' }}>{t('ownerPaymentPendingWallet')}</p>
              )}
              <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 14px' }}>{settlementFlash.bookingRef}</p>
              <button type="button" style={{ padding: '10px 24px', background: '#c9a84c', color: '#0a1628', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }} onClick={() => setSettlementFlash(null)}>{t('okLabel')}</button>
            </div>
          </div>
        )}

        {activeTab === 'wallet' && (
          <div>
            <div style={{ ...s.tableCard, marginBottom: '16px', background: 'linear-gradient(135deg, #0f2040, #0a1628)' }}>
              <p style={{ color: '#8896a8', fontSize: '11px', letterSpacing: '2px', margin: '0 0 6px' }}>{t('ownerPayoutWalletBalance')}</p>
              <h2 style={{ color: walletError ? '#e8a0a0' : '#c9a84c', fontSize: isMobile ? '32px' : '42px', fontWeight: 900, margin: '0 0 8px' }}>
                {walletLoading ? '…' : walletError ? walletError : `₹${walletBalance.toLocaleString('en-IN')}`}
              </h2>
              <p style={{ color: '#8896a8', fontSize: '11px', margin: 0, lineHeight: 1.45 }}>{t('ownerPayoutWalletHint')}</p>
            </div>

            <div style={{ ...s.tableCard, marginBottom: '16px' }}>
              <h3 style={{ ...s.tableTitle, marginBottom: '10px' }}>{t('ownerPayoutHowTitle')}</h3>
              <ul style={{ color: '#8896a8', fontSize: '11px', lineHeight: 1.55, margin: 0, paddingLeft: '18px' }}>
                <li>{t('ownerPayoutHow1')}</li>
                <li>{t('ownerPayoutHow2')}</li>
                <li>{t('ownerPayoutHow3')}</li>
              </ul>
            </div>

            <div style={{ ...s.tableCard, marginBottom: '16px' }}>
              <h3 style={{ ...s.tableTitle, marginBottom: '10px' }}>{t('ownerWithdrawTitle')}</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <input
                  type="number"
                  min="1000"
                  value={withdrawAmt}
                  onChange={(e) => setWithdrawAmt(e.target.value)}
                  placeholder={t('ownerWithdrawAmount')}
                  style={{ flex: '1 1 140px', padding: '10px', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(0,0,0,0.35)', color: '#e8e0d0' }}
                />
                <button
                  type="button"
                  disabled={withdrawSubmitting || walletBalance < 1000}
                  onClick={handleWithdrawRequest}
                  style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #c9a84c, #8b6914)', color: '#0a1628', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: withdrawSubmitting ? 'wait' : 'pointer', opacity: withdrawSubmitting ? 0.7 : 1 }}
                >
                  {withdrawSubmitting ? t('pleaseWait') : t('ownerWithdrawSubmit')}
                </button>
              </div>
              <p style={{ color: '#8896a8', fontSize: '10px', margin: '8px 0 0' }}>{t('ownerWithdrawMin')}</p>
            </div>

            <div style={s.tableCard}>
              <h3 style={{ ...s.tableTitle, marginBottom: '12px' }}>{t('ownerPayoutTxnTitle')}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '10px', marginBottom: '12px' }}>
                <div>
                  <p style={{ ...s.label, margin: '0 0 5px' }}>{t('ownerPayoutLedgerYear')}</p>
                  <select
                    style={{
                      background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                      border: '1px solid rgba(201,168,76,0.3)',
                      borderRadius: '8px',
                      color: '#e8e0d0',
                      fontSize: '13px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      outline: 'none',
                      minWidth: '100px',
                      boxSizing: 'border-box',
                    }}
                    value={ledgerYear}
                    onChange={(e) => setLedgerYear(Number(e.target.value))}
                  >
                    {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p style={{ ...s.label, margin: '0 0 5px' }}>{t('ownerPayoutLedgerMonth')}</p>
                  <select
                    style={{
                      background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                      border: '1px solid rgba(201,168,76,0.3)',
                      borderRadius: '8px',
                      color: '#e8e0d0',
                      fontSize: '13px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      outline: 'none',
                      minWidth: '100px',
                      boxSizing: 'border-box',
                    }}
                    value={ledgerMonth}
                    onChange={(e) => setLedgerMonth(Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1, 1).toLocaleString('en-IN', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  style={{
                    background: 'rgba(201,168,76,0.12)',
                    border: '1px solid rgba(201,168,76,0.5)',
                    borderRadius: '8px',
                    color: '#C9A84C',
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    opacity: ledgerPdfBusy || walletLoading ? 0.5 : 1,
                  }}
                  disabled={walletLoading || ledgerPdfBusy}
                  onClick={async () => {
                    setLedgerPdfBusy(true);
                    try {
                      await generateOwnerPayoutLedgerPDF({
                        ownerName: sessionUser.name,
                        year: ledgerYear,
                        month: ledgerMonth,
                        openingBalance: ledgerOpening,
                        closingBalance: ledgerClosing,
                        creditsTotal: ledgerCredits,
                        debitsTotal: ledgerDebits,
                        transactions: walletTxns,
                      });
                    } finally {
                      setLedgerPdfBusy(false);
                    }
                  }}
                >
                  {ledgerPdfBusy ? t('pleaseWait') : t('ownerPayoutDownloadLedger')}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
                <div>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{t('ownerPayoutOpening')}</p>
                  <p style={{ color: '#e8e0d0', fontSize: '13px', fontWeight: 700, margin: 0 }}>
                    {ledgerOpening !== null ? fmtInr(ledgerOpening) : '—'}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{t('ownerPayoutCreditsTotal')}</p>
                  <p style={{ color: '#4CAF50', fontSize: '13px', fontWeight: 700, margin: 0 }}>+{fmtInr(ledgerCredits)}</p>
                </div>
                <div>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{t('ownerPayoutDebitsTotal')}</p>
                  <p style={{ color: '#e94560', fontSize: '13px', fontWeight: 700, margin: 0 }}>-{fmtInr(ledgerDebits)}</p>
                </div>
                <div>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{t('ownerPayoutClosing')}</p>
                  <p style={{ color: '#c9a84c', fontSize: '13px', fontWeight: 700, margin: 0 }}>
                    {ledgerClosing !== null ? fmtInr(ledgerClosing) : '—'}
                  </p>
                </div>
              </div>
              {walletLoading ? (
                <p style={{ color: '#8896a8', fontSize: '12px' }}>{t('loading')}</p>
              ) : walletTxns.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('ownerPayoutNoTxnsInPeriod')}</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ ...s.table, minWidth: '480px' }}>
                    <thead>
                      <tr>
                        {[t('date'), t('txnDescription'), t('amount'), t('status')].map((h) => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {walletTxns.map((tx) => {
                        const isCredit = String(tx.type || '').toLowerCase() === 'credit';
                        const amt = Number(tx.amount || 0);
                        return (
                          <tr key={tx.id || `${tx.reference}-${tx.created_at}`} style={s.tr}>
                            <td style={s.td}>{tx.created_at ? new Date(tx.created_at).toLocaleString('en-IN') : '—'}</td>
                            <td style={{ ...s.td, fontSize: '11px' }}>{cleanDesc(tx.description, tx.reference || '') || '—'}</td>
                            <td style={{ ...s.td, color: isCredit ? '#4CAF50' : '#e94560', fontWeight: 700 }}>
                              {isCredit ? '+' : '-'} ₹{amt.toLocaleString('en-IN')}
                            </td>
                            <td style={s.td}>{tx.type === 'credit' ? 'Credit' : 'Debit'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ TAB: REPORTS & PAY ═══ */}
        {activeTab === 'reports' && (
          <div>
            <div style={{ ...s.cardRow, gridTemplateColumns: gridCols(2, 3, 4) }}>
              {[
                { icon: '💰', val: fmtInr(reportSettlementStats.totalEarned), label: t('ownerTotalEarned') },
                { icon: '📅', val: fmtInr(reportSettlementStats.thisMonth), label: t('ownerThisMonth') },
                { icon: '⏳', val: fmtInr(reportSettlementStats.pending), label: t('ownerPendingSettlements') },
                { icon: '📋', val: `${reportSettlementStats.totalBookings}`, label: t('settlements') },
              ].map((c, i) => (
                <div key={i} style={s.card}>
                  <p style={s.cardIcon}>{c.icon}</p>
                  <h3 style={{ ...s.cardVal, fontSize: isMobile ? '14px' : '18px' }}>{c.val}</h3>
                  <p style={s.cardLbl}>{c.label}</p>
                </div>
              ))}
            </div>

            <div style={s.tableCard}>
              <h3 style={{ ...s.tableTitle, marginBottom: '12px' }}>💳 {t('paymentHistoryTitle')}</h3>
              {settlementsLoading ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
              ) : settlementsError ? (
                <div>
                  <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 10px' }}>{settlementsError}</p>
                  <button
                    type="button"
                    style={s.downloadBtn}
                    onClick={() => setSettlementsReload((n) => n + 1)}
                  >
                    Try again
                  </button>
                </div>
              ) : settlements.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('ownerSettlementsEmpty')}</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ ...s.table, minWidth: '800px' }}>
                    <thead>
                      <tr>
                        {[t('ownerBookingRef'), t('date'), t('gross'), t('commission'), 'TDS', 'TCS', t('netPaid'), t('status'), t('ownerReceiptPdf')].map((h) => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {settlements.map((row) => {
                        const gross = Number(row.gross_amount || 0);
                        const commission = Number(row.commission_amount || 0);
                        const tds = Number(row.tds_amount || 0);
                        const tcs = Number(row.gst_tcs_amount || 0);
                        const net = Number(row.net_owner_amount || 0);
                        const rowId = row.id || row.booking_ref;
                        const dateLabel = row.created_at
                          ? new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—';
                        return (
                          <tr key={rowId} style={s.tr}>
                            <td style={{ ...s.td, color: '#c9a84c', fontSize: '11px' }}>{row.booking_ref || '—'}</td>
                            <td style={s.td}>{dateLabel}</td>
                            <td style={s.td}>{fmtInr(gross)}</td>
                            <td style={{ ...s.td, color: '#e94560' }}>- {fmtInr(commission)}</td>
                            <td style={{ ...s.td, color: '#e94560' }}>- {fmtInr(tds)}</td>
                            <td style={{ ...s.td, color: '#e94560' }}>- {fmtInr(tcs)}</td>
                            <td style={{ ...s.td, color: '#4CAF50', fontWeight: '700' }}>{fmtInr(net)}</td>
                            <td style={{ ...s.td, color: settlementStatusColor(row.status), fontWeight: 600, fontSize: '11px' }}>
                              {settlementStatusLabel(row.status)}
                            </td>
                            <td style={s.td}>
                              <button
                                type="button"
                                style={{ ...s.downloadBtn, padding: '6px 10px', fontSize: '10px' }}
                                disabled={receiptGeneratingId === rowId}
                                onClick={() => handleSettlementReceipt(row)}
                              >
                                {receiptGeneratingId === rowId ? t('ownerReceiptGenerating') : '⬇ PDF'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Bank Details */}
            <div style={{ ...s.tableCard, background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
              <h3 style={{ ...s.tableTitle, marginBottom: '12px' }}>🏦 {t('bankDetailsTitle')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                {[
                  { label: t('ownerBankName'), val: ownerBankProfile?.bank_name || '—' },
                  { label: t('accountNumber'), val: maskAccountNumber(ownerBankProfile?.account_number) },
                  { label: t('ifsc'), val: ownerBankProfile?.ifsc || '—' },
                  { label: t('ownerPanNumber'), val: maskPan(ownerBankProfile?.pan_number) },
                ].map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ color: '#8896a8', fontSize: '12px' }}>{d.label}</span>
                    <span style={{ color: '#e8e0d0', fontWeight: '600', fontSize: '12px' }}>{d.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══ TAB: FLEET ANALYTICS ═══ */}
        {activeTab === 'analytics' && (
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {[
                { months: 3, labelKey: 'analytics3Months' },
                { months: 6, labelKey: 'analytics6Months' },
                { months: 12, labelKey: 'analytics12Months' },
              ].map(({ months, labelKey }) => {
                const active = analyticsMonths === months;
                return (
                  <button
                    key={months}
                    type="button"
                    onClick={() => setAnalyticsMonths(months)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${active ? '#c9a84c' : 'rgba(201,168,76,0.35)'}`,
                      background: active ? 'rgba(201,168,76,0.2)' : 'rgba(0,0,0,0.2)',
                      color: active ? '#c9a84c' : '#8896a8',
                      cursor: 'pointer',
                      fontWeight: active ? 700 : 500,
                      fontSize: '12px',
                    }}
                  >
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>

            <div style={{ ...s.cardRow, gridTemplateColumns: gridCols(2, 2, 4) }}>
              {analyticsLoading ? (
                <div style={{ ...s.card, gridColumn: '1 / -1' }}>
                  <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
                </div>
              ) : analyticsError ? (
                <div style={{ ...s.card, gridColumn: '1 / -1' }}>
                  <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 10px' }}>{analyticsError}</p>
                  <button type="button" style={s.downloadBtn} onClick={loadAnalytics}>
                    {t('retryLabel')}
                  </button>
                </div>
              ) : (
                [
                  { icon: '💰', val: fmtInr(analyticsData?.summary?.total_revenue), label: t('analyticsRevenue') },
                  { icon: '📋', val: `${analyticsData?.summary?.total_bookings ?? 0}`, label: t('analyticsBookings') },
                  { icon: '⏱️', val: formatHoursHm(analyticsData?.summary?.total_hours ?? 0), label: t('analyticsHours') },
                  { icon: '🚜', val: `${analyticsData?.summary?.active_machines ?? 0}`, label: t('analyticsActiveMachines') },
                ].map((c, i) => (
                  <div key={i} style={s.card}>
                    <p style={s.cardIcon}>{c.icon}</p>
                    <h3 style={{ ...s.cardVal, fontSize: isMobile ? '14px' : '18px' }}>{c.val}</h3>
                    <p style={s.cardLbl}>{c.label}</p>
                  </div>
                ))
              )}
            </div>

            <div style={{ ...s.tableCard, marginBottom: '16px' }}>
              <h3 style={{ ...s.tableTitle, marginBottom: '12px' }}>📈 {t('analyticsMonthlyTrend')}</h3>
              {analyticsLoading ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
              ) : analyticsError ? (
                <p style={{ color: '#e94560', fontSize: '12px', margin: 0 }}>{analyticsError}</p>
              ) : analyticsTrendSeries.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('analyticsNoData')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={ANALYTICS_CHART_HEIGHT}>
                  <BarChart data={analyticsTrendSeries} margin={{ top: 12, right: 12, left: 4, bottom: 4 }} barCategoryGap="20%">
                    <defs>
                      <linearGradient id="ownerFleetBarGoldGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={ANALYTICS_CHART_GOLD_LIGHT} />
                        <stop offset="100%" stopColor={ANALYTICS_CHART_GOLD} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={ANALYTICS_CHART_GRID} strokeDasharray="4 4" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: ANALYTICS_CHART_AXIS, fontSize: 11 }} axisLine={{ stroke: ANALYTICS_CHART_GRID }} tickLine={false} />
                    <YAxis
                      tick={{ fill: ANALYTICS_CHART_AXIS, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatAnalyticsRevenue}
                      domain={[0, analyticsMaxRevenue * 1.1]}
                    />
                    <Tooltip content={<FleetAnalyticsTooltip bookingsLabel={t('analyticsBookings')} />} />
                    <Bar dataKey="revenue" fill="url(#ownerFleetBarGoldGrad)" radius={[8, 8, 0, 0]} maxBarSize={56} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={s.tableCard}>
              <h3 style={{ ...s.tableTitle, marginBottom: '12px' }}>🚜 {t('analyticsMachineTable')}</h3>
              {analyticsLoading ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
              ) : analyticsError ? (
                <p style={{ color: '#e94560', fontSize: '12px', margin: 0 }}>{analyticsError}</p>
              ) : displayFleetMachines.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('analyticsNoData')}</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ ...s.table, minWidth: '720px' }}>
                    <thead>
                      <tr>
                        {[t('machine'), t('revenue'), t('analyticsBookings'), t('analyticsHours'), t('analyticsLastBooking'), t('status')].map((h) => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayFleetMachines.map((row) => {
                        const lastBookingLabel = row.last_booking_date
                          ? new Date(row.last_booking_date).toLocaleDateString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })
                          : '—';
                        const machineLabel = row.machine_name
                          ? `${row.machine_id} · ${row.machine_name}`
                          : row.machine_id;
                        return (
                          <tr key={row.machine_uuid || row.machine_id} style={s.tr}>
                            <td style={{ ...s.td, color: '#c9a84c', fontWeight: 700 }}>{machineLabel}</td>
                            <td style={{ ...s.td, color: '#c9a84c', fontWeight: 700 }}>{fmtInr(row.total_revenue)}</td>
                            <td style={s.td}>{row.total_bookings ?? 0}</td>
                            <td style={s.td}>{formatHoursHm(row.total_hours ?? 0)}</td>
                            <td style={s.td}>{lastBookingLabel}</td>
                            <td style={s.td}>
                              <span style={{
                                ...s.statusBadge,
                                background: row.status === 'Active' ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)',
                                border: `1px solid ${row.status === 'Active' ? '#4CAF50' : '#FF9800'}`,
                                color: row.status === 'Active' ? '#4CAF50' : '#FF9800',
                              }}
                              >
                                {statusLabel(row.status, t)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'recovery' && (
          <div style={{ padding: '20px' }}>
            {machineData.length === 0 && (
              <p style={{ color: '#8896a8', fontSize: 13, margin: 0 }}>
                {t('noMachinesYet')}
              </p>
            )}
            {displayMachines.map((m, idx) => {
              const raw = machineData.find(r => r.machine_id === m.id);
              const machineUuid = raw?.id;
              return (
                <div key={machineUuid || idx} style={{ marginBottom: 24 }}>
                  {/* Machine label */}
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: 10, marginBottom: 12
                  }}>
                    <div style={{
                      width: 3, height: 20, borderRadius: 2,
                      background: 'linear-gradient(180deg, #C9A84C, rgba(201,168,76,0.2))'
                    }} />
                    <div>
                      <div style={{
                        fontSize: 14, fontWeight: 700, color: '#e8e0d0'
                      }}>{m.name}</div>
                      <div style={{
                        fontSize: 11, color: '#8896a8', marginTop: 2
                      }}>{m.id} · {m.regNo}</div>
                    </div>
                  </div>
                {/* Recovery Plan — Corporate Dashboard */}
                {(() => {
                  const recovery = recoveryData[machineUuid];
                  const recoveryPct = Math.min(100, Math.max(0, Number(recovery?.recovery_percent) || 0));
                  return (
                <div style={{
                  marginTop: 16,
                  background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}>
                  {/* Header bar */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 18px',
                    borderBottom: '1px solid rgba(201,168,76,0.1)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 3,
                        height: 16,
                        borderRadius: 2,
                        background: 'linear-gradient(180deg, #C9A84C, rgba(201,168,76,0.3))',
                      }} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#e8e0d0' }}>
                        {t('recoveryPlan')}
                      </span>
                    </div>
                    {!recoveryLoading[machineUuid]
                      && recovery?.recovery_available
                      && editingFinancialMachineId !== machineUuid && (
                      <button
                        type="button"
                        onClick={() => openFinancialEdit(machineUuid, raw)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#C9A84C',
                          fontSize: 11,
                          cursor: 'pointer',
                          padding: 0,
                          fontWeight: 600,
                        }}
                      >
                        ✏️ Edit
                      </button>
                    )}
                  </div>
                  {/* Body */}
                  <div style={{ padding: '16px 18px' }}>
                    {recoveryLoading[machineUuid] && (
                      <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                        {t('loading')}
                      </p>
                    )}
                    {!recoveryLoading[machineUuid]
                      && (!recovery || recovery.recovery_available === false) && (
                      <>
                        <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 12px' }}>
                          {t('recoveryAddPrice')}
                        </p>
                        {editingFinancialMachineId === machineUuid ? (
                          renderFinancialEditForm(machineUuid)
                        ) : (
                          <button
                            type="button"
                            onClick={() => openFinancialEdit(machineUuid, raw)}
                            style={{
                              background: 'rgba(201,168,76,0.12)',
                              border: '1px solid rgba(201,168,76,0.4)',
                              color: '#C9A84C',
                              borderRadius: 8,
                              padding: '8px 16px',
                              fontSize: 12,
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            {t('editFinancialDetails')}
                          </button>
                        )}
                      </>
                    )}
                    {!recoveryLoading[machineUuid]
                      && recovery?.recovery_available === true && (
                      <>
                        {editingFinancialMachineId === machineUuid ? (
                          renderFinancialEditForm(machineUuid)
                        ) : (
                          <>
                            {/* Top 3 KPI cards */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: gridCols(1, 3, 3),
                              gap: 10,
                              marginBottom: 14,
                            }}>
                              {[
                                {
                                  label: 'Machine Value',
                                  value: fmtInr(recovery.current_machine_value),
                                  color: '#C9A84C',
                                  border: 'rgba(201,168,76,0.2)',
                                },
                                {
                                  label: 'Total Earned',
                                  value: fmtInr(recovery.total_earned),
                                  color: '#4CAF50',
                                  border: 'rgba(76,175,80,0.2)',
                                },
                                {
                                  label: 'Remaining',
                                  value: fmtInr(recovery.remaining_amount),
                                  color: '#e94560',
                                  border: 'rgba(233,69,96,0.2)',
                                },
                              ].map((kpi) => (
                                <div key={kpi.label} style={{
                                  padding: '10px 12px',
                                  background: 'rgba(0,0,0,0.2)',
                                  border: `1px solid ${kpi.border}`,
                                  borderRadius: 8,
                                }}>
                                  <div style={{
                                    fontSize: 9,
                                    color: '#8896a8',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    marginBottom: 4,
                                  }}
                                  >
                                    {kpi.label}
                                  </div>
                                  <div style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: kpi.color,
                                  }}
                                  >
                                    {kpi.value}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Progress bar */}
                            <div style={{ marginBottom: 14 }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 6,
                              }}>
                                <span style={{ fontSize: 11, color: '#8896a8' }}>
                                  {t('recoveryProgress')}
                                </span>
                                <span style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: '#C9A84C',
                                }}
                                >
                                  {recoveryPct}%
                                </span>
                              </div>
                              <div style={{
                                height: 6,
                                background: 'rgba(255,255,255,0.08)',
                                borderRadius: 3,
                                overflow: 'hidden',
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: `${recoveryPct}%`,
                                  background: 'linear-gradient(90deg, #C9A84C, #8b6914)',
                                  borderRadius: 3,
                                  transition: 'width 0.3s ease',
                                }}
                                />
                              </div>
                            </div>
                            {/* Traffic light status */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 14,
                              padding: '8px 12px',
                              background: 'rgba(255,255,255,0.03)',
                              borderRadius: 6,
                            }}>
                              <span style={{ fontSize: 14 }}>
                                {recoveryTrafficEmoji(recovery.traffic_light)}
                              </span>
                              <span style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#e8e0d0',
                              }}
                              >
                                {t(recoveryStatusI18nKey(recovery.traffic_light))}
                              </span>
                            </div>
                            {/* Two column — Daily + Projection */}
                            {recovery.daily_net_income != null && (
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: gridCols(1, 2, 2),
                                gap: 10,
                                marginBottom: 14,
                              }}>
                                <div style={{
                                  padding: '12px 14px',
                                  background: 'rgba(0,0,0,0.2)',
                                  border: '1px solid rgba(201,168,76,0.15)',
                                  borderRadius: 8,
                                }}>
                                  <div style={{
                                    fontSize: 9,
                                    color: '#C9A84C',
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    marginBottom: 10,
                                  }}
                                  >
                                    {t('recoveryDailyNet')}
                                  </div>
                                  <div style={{
                                    fontSize: 16,
                                    fontWeight: 700,
                                    color: '#4CAF50',
                                    marginBottom: 4,
                                  }}
                                  >
                                    {fmtInr(recovery.daily_net_income)}/day
                                  </div>
                                  {recovery.roi_percent != null && (
                                    <div style={{
                                      fontSize: 11,
                                      color: recovery.roi_percent >= 30
                                        ? '#4CAF50'
                                        : recovery.roi_percent >= 15
                                          ? '#FF9800'
                                          : '#e94560',
                                      fontWeight: 600,
                                    }}
                                    >
                                      {t('recoveryRoi')}: {recovery.roi_percent}%
                                    </div>
                                  )}
                                </div>
                                <div style={{
                                  padding: '12px 14px',
                                  background: 'rgba(0,0,0,0.2)',
                                  border: '1px solid rgba(201,168,76,0.15)',
                                  borderRadius: 8,
                                }}>
                                  <div style={{
                                    fontSize: 9,
                                    color: '#C9A84C',
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    marginBottom: 10,
                                  }}
                                  >
                                    {t('recoveryProjectedDate')}
                                  </div>
                                  {recoveryPct < 100 && (
                                    <>
                                      {recovery.projected_recovery_date != null && (
                                        <div style={{
                                          fontSize: 16,
                                          fontWeight: 700,
                                          color: '#e8e0d0',
                                          marginBottom: 4,
                                        }}
                                        >
                                          {new Date(recovery.projected_recovery_date).toLocaleDateString('en-IN', {
                                            month: 'long',
                                            year: 'numeric',
                                          })}
                                        </div>
                                      )}
                                      {recovery.recovery_days != null && (
                                        <div style={{
                                          fontSize: 11,
                                          color: '#8896a8',
                                          fontWeight: 600,
                                        }}
                                        >
                                          {t('recoveryDays')}: {(() => {
                                            const v = recovery.recovery_days;
                                            const yrs = Math.floor(v / 365);
                                            const mos = Math.floor((v % 365) / 30);
                                            if (yrs > 0 && mos > 0) return `${yrs} yr ${mos} mo`;
                                            if (yrs > 0) return `${yrs} yr`;
                                            return `${mos} mo`;
                                          })()}
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {recoveryPct >= 100 && (
                                    <div style={{
                                      fontSize: 16,
                                      fontWeight: 700,
                                      color: '#4CAF50',
                                    }}
                                    >
                                      100%
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                  );
                })()}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ TAB: SETTINGS — Bank & Tax ═══ */}
        {activeTab === 'settings' && (
          <div style={s.tableCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <h3 style={{ ...s.tableTitle, margin: 0 }}>🏦 {t('ownerBankTaxDetails')}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>Registered Mobile: {sessionUser.phone}</p>
                <button type="button" style={s.downloadBtn} onClick={() => setShowPhoneChangeModal(true)}>Change Mobile Number</button>
              </div>
              {!ownerBankProfileEditing && !ownerBankProfileLoading && (
                <button type="button" style={s.downloadBtn} onClick={startOwnerBankProfileEdit}>
                  {t('ownerEditProfile')}
                </button>
              )}
            </div>
            <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 14px', lineHeight: 1.45 }}>{t('ownerBankTaxHint')}</p>
            {ownerBankProfileSuccess && (
              <p style={{ color: '#4CAF50', fontSize: '12px', margin: '0 0 12px' }}>{ownerBankProfileSuccess}</p>
            )}
            {ownerBankProfileLoading ? (
              <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
            ) : ownerBankProfileError && !ownerBankProfileEditing ? (
              <p style={{ color: '#e94560', fontSize: '12px', margin: 0 }}>{ownerBankProfileError}</p>
            ) : ownerBankProfileEditing ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {[
                  { key: 'account_holder_name', label: t('ownerAccountHolderName') },
                  { key: 'bank_name', label: t('ownerBankName') },
                  { key: 'account_number', label: t('accountNumber'), type: 'text' },
                  { key: 'ifsc', label: t('ifsc') },
                  { key: 'pan_number', label: t('ownerPanNumber') },
                  { key: 'gstin', label: `${t('gstin')} (${t('optionalLabel')})` },
                ].map((field) => (
                  <div key={field.key}>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{field.label}</p>
                    <input
                      style={s.input}
                      value={ownerBankProfileForm[field.key]}
                      onChange={(e) => setOwnerBankProfileForm((prev) => ({
                        ...prev,
                        [field.key]: field.key === 'ifsc' || field.key === 'pan_number' || field.key === 'gstin'
                          ? e.target.value.toUpperCase()
                          : e.target.value,
                      }))}
                    />
                  </div>
                ))}
                {ownerBankProfileError && (
                  <p style={{ color: '#e94560', fontSize: '12px', margin: 0 }}>{ownerBankProfileError}</p>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button type="button" style={{ ...s.confirmBtn, flex: 1 }} disabled={ownerBankProfileSaving} onClick={handleOwnerBankProfileSave}>
                    {ownerBankProfileSaving ? t('pleaseWait') : t('ownerSaveProfile')}
                  </button>
                  <button
                    type="button"
                    style={s.cancelBtn}
                    disabled={ownerBankProfileSaving}
                    onClick={() => { setOwnerBankProfileEditing(false); setOwnerBankProfileError(''); }}
                  >
                    {t('ownerCancelEdit')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                {[
                  { label: t('ownerAccountHolderName'), val: ownerBankProfile?.account_holder_name || ownerDisplayName || '—' },
                  { label: t('ownerBankName'), val: ownerBankProfile?.bank_name || '—' },
                  { label: t('accountNumber'), val: maskAccountNumber(ownerBankProfile?.account_number) },
                  { label: t('ifsc'), val: ownerBankProfile?.ifsc || '—' },
                  { label: t('ownerPanNumber'), val: maskPan(ownerBankProfile?.pan_number) },
                  { label: t('gstin'), val: ownerBankProfile?.gstin || '—' },
                  ...(ownerEntityTier === 3 ? [
                    {
                      label: 'Company Identification No. (CIN)',
                      val: ownerBankProfile?.cin || sessionUser?.cin || '—',
                    },
                    {
                      label: 'Director Identification No. (DIN)',
                      val: ownerBankProfile?.din || sessionUser?.din || '—',
                    },
                  ] : []),
                ].map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ color: '#8896a8', fontSize: '12px' }}>{d.label}</span>
                    <span style={{ color: '#e8e0d0', fontWeight: '600', fontSize: '12px' }}>{d.val}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ TAB: KYC ═══ */}
        {activeTab === 'kyc' && (
          <OwnerKycForm
            authToken={localStorage.getItem('developmentexpress_token') || ''}
            apiBaseUrl={API_BASE_URL}
            onStatusChange={(status) => {
              setOwnerKycStatus(status);
              // KYC approval may auto-upgrade tier when bank details exist
              getOwnerProfile().then((result) => {
                if (result.profile) {
                  setOwnerBankProfile(result.profile);
                  applyOwnerProfileTier(result.profile);
                }
              });
            }}
          />
        )}

        {/* ═══ TAB: ALERTS ═══ */}
        {activeTab === 'alerts' && (
          <div style={s.tableCard}>
            <PushNotificationToggle />
            <h3 style={s.tableTitle}>🔔 {t('allAlerts')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '8px', marginBottom: '14px' }}>
              {[
                { icon: '📋', label: t('alertPendingBookings'), count: alertCounts.bookingsPending },
                { icon: '💰', label: t('alertPendingPayouts'), count: alertCounts.settlementsPending },
                { icon: '⛽', label: t('alertLowFuel'), count: alertCounts.lowFuelMachines },
                { icon: '🔧', label: t('alertOpenIssues'), count: alertCounts.openIssues },
                { icon: '📋', label: t('alertComplianceExpiring'), count: alertCounts.complianceAlerts },
                { icon: '🛠️', label: t('alertServiceDue'), count: alertCounts.serviceAlerts },
              ].map((badge) => (
                <div key={badge.label} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '8px 10px', fontSize: '11px', color: '#e8e0d0' }}>
                  {badge.icon} {badge.count} {badge.label}
                </div>
              ))}
            </div>
            {alertsLoading && (
              <p style={{ color: '#8896a8', fontSize: 13 }}>{t('loading')}</p>
            )}
            {!alertsLoading && alertsError && (
              <div style={{ textAlign: 'center', margin: '12px 0' }}>
                <p style={{ color: '#e94560', fontSize: 13, margin: '0 0 10px' }}>{alertsError}</p>
                <button
                  type="button"
                  onClick={loadAlerts}
                  style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {t('retryLabel')}
                </button>
              </div>
            )}
            {!alertsLoading && !alertsError && allAlerts.length === 0 && (
              <p style={{ color: '#8896a8', fontSize: 13 }}>
                {t('noAlertsNow')}
              </p>
            )}
            {!alertsLoading && !alertsError && allAlerts.length > 0 && allAlerts.map((a) => (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => a.link && openOwnerAlertTarget(a)}
                onKeyDown={(e) => { if (e.key === 'Enter' && a.link) openOwnerAlertTarget(a); }}
                style={{ ...s.alertRow, borderLeft: `3px solid ${a.color}`, marginBottom: '8px', cursor: a.link ? 'pointer' : 'default' }}
              >
                <span style={{ fontSize: '20px' }}>{a.icon}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ color: '#e8e0d0', fontSize: '13px', margin: '0 0 3px' }}>{ownerAlertMsg(a, t)}</p>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>
                    {a.time ? new Date(a.time).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </p>
                </div>
              </div>
            ))}

            <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid rgba(201,168,76,0.2)' }}>
              <h3 style={{ ...s.tableTitle, marginBottom: '10px' }}>
                {String.fromCodePoint(0x26A0)} {t('blacklistedClients')}
              </h3>
              {blacklistedClientsLoading && (
                <p style={{ color: '#8896a8', fontSize: 13 }}>{t('loading')}</p>
              )}
              {!blacklistedClientsLoading && blacklistedClientsError && (
                <p style={{ color: '#e94560', fontSize: 13 }}>{blacklistedClientsError}</p>
              )}
              {!blacklistedClientsLoading && !blacklistedClientsError && blacklistedClients.length === 0 && (
                <p style={{ color: '#8896a8', fontSize: 13 }}>{t('blacklistedClientsEmpty')}</p>
              )}
              {!blacklistedClientsLoading && !blacklistedClientsError && blacklistedClients.map((c) => (
                <div
                  key={c.id}
                  style={{
                    ...s.alertRow,
                    borderLeft: '3px solid #e94560',
                    marginBottom: '8px',
                    background: 'rgba(233,69,96,0.08)',
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{String.fromCodePoint(0x1F6AB)}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#e8e0d0', fontSize: '13px', margin: '0 0 3px', fontWeight: 600 }}>
                      {c.name} · {c.phone}
                    </p>
                    <p style={{ color: '#ff9aa8', fontSize: '12px', margin: '0 0 3px' }}>
                      {c.blacklisted_reason || '—'}
                    </p>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>
                      {c.blacklisted_at
                        ? new Date(c.blacklisted_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                        : '—'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ TAB: NOTIFICATIONS ═══ */}
        {activeTab === 'notifications' && (
          <NotificationCenter
            authToken={localStorage.getItem('developmentexpress_token') || ''}
            apiEndpoint="/api/owner/notifications"
            title={t('notificationCenter')}
          />
        )}

        {/* ═══ TAB: TERMS ═══ */}
        {activeTab === 'terms' && (
          <div>
            <div style={{ ...s.tableCard, marginBottom: '15px' }}>
              <h3 style={s.tableTitle}>📜 Terms & Conditions</h3>
              <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 10px', lineHeight: 1.5 }}>
                Review the latest platform legal documents. These links open the canonical policy pages.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                {[
                  { label: 'Terms & Conditions', href: '/terms' },
                  { label: 'Privacy Policy', href: '/privacy' },
                  { label: 'Delete account', href: '/delete-account' },
                  { label: 'Refund Policy', href: '/refund-policy' },
                  { label: 'Cancellation Policy', href: '/cancellation-policy' },
                  { label: 'Service Delivery Policy', href: '/service-delivery-policy' },
                  { label: 'Grievance Redressal', href: '/grievance' },
                ].map((item) => (
                  <div
                    key={item.href}
                    onClick={() => { sessionStorage.setItem('returnTab', activeTab); navigate(item.href); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { sessionStorage.setItem('returnTab', activeTab); navigate(item.href); } }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '10px', padding: '12px' }}>
                      <p style={{ color: '#c9a84c', fontWeight: 700, fontSize: '13px', margin: 0 }}>{item.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══ TAB: SUPPORT ═══ */}
        {activeTab === 'support' && (
          <div>
            <div style={{ ...s.tableCard, marginBottom: '15px' }}>
              <h3 style={s.tableTitle}>🆘 {t('contactCompany')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <a href="tel:+918408000084" style={{ textDecoration: 'none' }}>
                  <div style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer' }}>
                    <p style={{ fontSize: '28px', margin: '0 0 8px' }}>📞</p>
                    <p style={{ color: '#4CAF50', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('callNow')}</p>
                    <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>+91-8408000084</p>
                  </div>
                </a>
                <a href="https://wa.me/918408000084" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                  <div style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer' }}>
                    <p style={{ fontSize: '28px', margin: '0 0 8px' }}>💬</p>
                    <p style={{ color: '#25D366', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('whatsapp')}</p>
                    <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>+91-8408000084</p>
                  </div>
                </a>
                <a href="mailto:om.chavan2026@zohomail.in" style={{ textDecoration: 'none' }}>
                  <div style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer' }}>
                    <p style={{ fontSize: '28px', margin: '0 0 8px' }}>📧</p>
                    <p style={{ color: '#c9a84c', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('emailLabel')}</p>
                    <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>om.chavan2026@zohomail.in</p>
                  </div>
                </a>
                <div style={{ background: 'rgba(21,101,192,0.1)', border: '1px solid rgba(21,101,192,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer' }}>
                  <p style={{ fontSize: '28px', margin: '0 0 8px' }}>📍</p>
                  <p style={{ color: '#1565C0', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('officeLabel')}</p>
                  <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>Karad, Satara - 415110</p>
                </div>
              </div>
            </div>
            <div style={s.tableCard}>
              <h3 style={s.tableTitle}>📋 {t('companyDetailsTitle')}</h3>
              {[
                { label: t('company'), val: 'Development Express' },
                { label: t('md'), val: 'Om Chavan (B.Tech Civil)' },
                { label: t('gstin'), val: (process.env.REACT_APP_DE_GSTIN && process.env.REACT_APP_DE_GSTIN !== '27ABCDE1234F1Z5') ? process.env.REACT_APP_DE_GSTIN : 'Pending incorporation' },
                { label: t('sinceLabel'), val: '2011 (15 Years)' },
                { label: t('commission'), val: t('commissionRate') },
                { label: t('paymentCycle'), val: t('threeWorkingDays') },
              ].map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ color: '#8896a8', fontSize: '12px' }}>{d.label}</span>
                  <span style={{ color: '#e8e0d0', fontWeight: '600', fontSize: '12px' }}>{d.val}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'machines-hub' && renderOwnerHubCards([
          { id: 'machines', icon: '🚜', i18nKey: 'myMachines', subtitleKey: 'ownerHubMachinesSubtitle' },
          { id: 'register', icon: '📝', i18nKey: 'registerMachine', subtitleKey: 'ownerHubRegisterSubtitle' },
          { id: 'edit-registration', icon: '📝', i18nKey: 'equipmentProfile', label: 'Equipment Profile', subtitleKey: 'ownerHubEditRegistrationSubtitle', hidden: machineData.length === 0 },
        ])}

        {activeTab === 'finance-hub' && renderOwnerHubCards([
          { id: 'wallet', icon: '💲', i18nKey: 'ownerPayoutWallet', subtitleKey: 'ownerHubWalletSubtitle' },
          { id: 'reports', icon: '💰', i18nKey: 'reports', subtitleKey: 'ownerHubReportsSubtitle' },
          { id: 'analytics', icon: '📊', i18nKey: 'fleetAnalytics', subtitleKey: 'ownerHubAnalyticsSubtitle', hidden: ownerEntityTier < 2 },
          { id: 'recovery', icon: '📉', i18nKey: 'recoveryPlan', subtitleKey: 'ownerHubRecoverySubtitle', hidden: machineData.length === 0 },
        ])}

        {activeTab === 'operations-hub' && renderOwnerHubCards([
          { id: 'operators', icon: '👥', i18nKey: 'operators', subtitleKey: 'ownerHubOperatorsSubtitle' },
          { id: 'tracking', icon: '📍', i18nKey: 'tracking', subtitleKey: 'ownerHubTrackingSubtitle' },
        ])}

        {activeTab === 'account-hub' && renderOwnerHubCards([
          { id: 'kyc', icon: '🪪', i18nKey: 'kycVerification', subtitleKey: 'ownerHubKycSubtitle' },
          { id: 'settings', icon: '⚙️', i18nKey: 'ownerSettings', subtitleKey: 'ownerHubSettingsSubtitle' },
          { id: 'alerts', icon: '⚠️', i18nKey: 'alerts', subtitleKey: 'ownerHubAlertsSubtitle', badge: allAlerts.length > 0 ? (allAlerts.length > 99 ? '99+' : allAlerts.length) : null },
          { id: 'notifications', icon: '📨', i18nKey: 'notificationCenter', subtitleKey: 'ownerHubNotificationsSubtitle' },
          { id: 'terms', icon: '📜', i18nKey: 'tacTitle', subtitleKey: 'ownerHubTermsSubtitle' },
          { id: 'support', icon: '🆘', i18nKey: 'support', subtitleKey: 'ownerHubSupportSubtitle' },
        ])}
      </div>
      </div>

      {rejectQuotationModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1200,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
        >
          <div style={{
            width: '100%',
            maxWidth: 430,
            background: '#030810',
            border: '1px solid rgba(201,168,76,0.35)',
            borderRadius: 12,
            padding: 18,
          }}
          >
            <h3 style={{ color: '#ff9aa8', fontSize: 15, fontWeight: 800, margin: '0 0 6px' }}>
              Reject quotation
            </h3>
            <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 12px', lineHeight: 1.45 }}>
              {rejectQuotationModal.quotationRef || 'This quotation'} will be declined and the client
              will be told the machine is not available.
            </p>
            <label style={{ color: '#8896a8', fontSize: 10, display: 'block', marginBottom: 4 }}>
              Reason (optional — kept for audit)
            </label>
            <textarea
              value={rejectQuotationReason}
              onChange={(e) => setRejectQuotationReason(e.target.value)}
              rows={3}
              placeholder="e.g. Machine already committed for these dates"
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                border: '1px solid rgba(201,168,76,0.3)',
                borderRadius: 8,
                color: '#e8e0d0',
                fontSize: 13,
                padding: '8px 12px',
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => {
                  setRejectQuotationModal(null);
                  setRejectQuotationReason('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'rgba(201,168,76,0.12)',
                  border: '1px solid rgba(201,168,76,0.5)',
                  color: '#C9A84C',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRejectQuotation}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  background: 'rgba(233,69,96,0.15)',
                  border: '1px solid rgba(233,69,96,0.5)',
                  color: '#ff9aa8',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Confirm reject
              </button>
            </div>
          </div>
        </div>
      )}

      <PhoneChangeModal
        isOpen={showPhoneChangeModal}
        onClose={() => setShowPhoneChangeModal(false)}
        userRole={sessionUser.role || 'owner'}
        authToken={localStorage.getItem('developmentexpress_token') || ''}
        onPhoneChanged={handleOwnerPhoneChanged}
      />

      {loadingMilestoneModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1200,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
        >
          <div style={{
            width: '100%',
            maxWidth: '430px',
            background: '#030810',
            border: '1px solid rgba(201,168,76,0.35)',
            borderRadius: '12px',
            padding: '18px',
          }}
          >
            <h3 style={{ color: '#c9a84c', margin: '0 0 8px', fontSize: '15px' }}>
              Submit Loading Milestone
            </h3>
            <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 14px' }}>
              Booking #{loadingMilestoneModal.booking_ref || loadingMilestoneModal.id}
              {' · '}
              Transport ₹{Number(loadingMilestoneModal.mobilization_transport_amount || 0).toLocaleString('en-IN')}
            </p>
            <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '12px' }}>
              Loading photo (required)
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setLoadingPhotoFile(e.target.files?.[0] || null)}
                style={{ display: 'block', marginTop: '6px', width: '100%', color: '#e8e0d0', fontSize: '12px' }}
              />
            </label>
            <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '12px' }}>
              Transport receipt (optional)
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setLoadingReceiptFile(e.target.files?.[0] || null)}
                style={{ display: 'block', marginTop: '6px', width: '100%', color: '#e8e0d0', fontSize: '12px' }}
              />
            </label>
            {loadingMilestoneError && (
              <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 10px' }}>{loadingMilestoneError}</p>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => {
                  setLoadingMilestoneModal(null);
                  setLoadingPhotoFile(null);
                  setLoadingReceiptFile(null);
                  setLoadingMilestoneError('');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#8896a8',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={Boolean(loadingMilestoneBusyId)}
                onClick={handleSubmitLoadingMilestone}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                  border: 'none',
                  color: '#030810',
                  borderRadius: '8px',
                  fontWeight: 800,
                  cursor: loadingMilestoneBusyId ? 'wait' : 'pointer',
                  opacity: loadingMilestoneBusyId ? 0.7 : 1,
                }}
              >
                {loadingMilestoneBusyId ? t('loading') : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {terminationModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1200,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
        >
          <div style={{
            width: '100%',
            maxWidth: '430px',
            background: '#030810',
            border: '1px solid rgba(136,150,168,0.35)',
            borderRadius: '12px',
            padding: '18px',
          }}
          >
            <h3 style={{ color: '#aab4c3', margin: '0 0 12px', fontSize: '15px' }}>
              Request contract termination
            </h3>
            <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '10px' }}>
              Termination type
              <select
                value={terminationType}
                onChange={(e) => setTerminationType(e.target.value)}
                style={{
                  width: '100%',
                  marginTop: '6px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: '#0a1628',
                  color: '#e8e0d0',
                }}
              >
                <option value="owner">Owner</option>
                <option value="mutual">Mutual</option>
                <option value="force_majeure">Force Majeure</option>
              </select>
            </label>
            {terminationType === 'force_majeure' && (
              <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '10px' }}>
                Force majeure reason
                <textarea
                  value={forceMajeureReason}
                  onChange={(e) => setForceMajeureReason(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    marginTop: '6px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: '#0a1628',
                    color: '#e8e0d0',
                    boxSizing: 'border-box',
                  }}
                />
              </label>
            )}
            {(() => {
              const fee = estimateOwnerTerminationFee(terminationModal, terminationType);
              return (
                <div style={{
                  marginBottom: '10px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(0,0,0,0.35)',
                  fontSize: '12px',
                  color: '#e8e0d0',
                }}
                >
                  {(terminationType === 'mutual' || terminationType === 'force_majeure') ? (
                    <p style={{ margin: 0 }}>Estimated fee: ₹0 (no early-termination fee)</p>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 4px' }}>
                        Est. remaining cycles: {fee.remaining} (fee capped at {fee.feeCycles} × 25%)
                      </p>
                      <p style={{ margin: 0, fontWeight: 700, color: '#aab4c3' }}>
                        Est. fee: ₹{fee.net.toLocaleString('en-IN')}
                        {' '}(₹{fee.gross.toLocaleString('en-IN')} + GST ₹{fee.gst.toLocaleString('en-IN')})
                      </p>
                      <p style={{ margin: '6px 0 0', fontSize: '10px', color: '#8896a8' }}>
                        Owner-initiated termination applies this fee estimate.
                      </p>
                    </>
                  )}
                </div>
              );
            })()}
            <p style={{ color: '#FFB74D', fontSize: '11px', margin: '0 0 12px' }}>
              15-day notice period applies
            </p>
            {terminationError && (
              <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 10px' }}>{terminationError}</p>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                disabled={terminationSubmitting}
                onClick={() => setTerminationModal(null)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent',
                  color: '#8896a8',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={terminationSubmitting}
                onClick={handleOwnerInitiateTermination}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(136,150,168,0.45)',
                  background: 'rgba(40,40,50,0.7)',
                  color: '#aab4c3',
                  fontWeight: 700,
                  cursor: 'pointer',
                  opacity: terminationSubmitting ? 0.7 : 1,
                }}
              >
                {terminationSubmitting ? 'Submitting…' : 'Confirm request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══ STYLES ═══
const s = {
  container: { display: 'flex', height: '100svh', maxHeight: '100svh', overflow: 'hidden', background: '#050d1a', fontFamily: 'Arial, sans-serif', color: '#fff', width: '100%' },
  containerMobile: { position: 'relative', display: 'block', minHeight: '100svh', width: '100%', maxWidth: '100vw', overflowX: 'hidden', background: '#050d1a', fontFamily: 'Arial, sans-serif', color: '#fff' },
  scrollShell: { position: 'relative', zIndex: 10, display: 'flex', flex: '1 1 0', flexDirection: 'column', minWidth: 0, minHeight: 0, width: '100%', overflow: 'hidden' },
  mainMobile: { display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
  sidebar: { position: 'sticky', top: 0, alignSelf: 'flex-start', width: '230px', height: '100dvh', maxHeight: '100dvh', background: 'linear-gradient(180deg, #0f2040 0%, #0a1628 100%)', borderRight: '1px solid rgba(201,168,76,0.2)', padding: '20px 15px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0, minHeight: 0, overflow: 'hidden' },
  sidebarNavScroll: { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', marginRight: '-4px', paddingRight: '4px', paddingBottom: 8 },
  sidebarLogo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  sidebarHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' },
  sidebarHeaderBrand: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 },
  sidebarToggleBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.1)', color: '#c9a84c', cursor: 'pointer', flexShrink: 0 },
  sidebarOpenFab: { position: 'fixed', left: 12, top: 'calc(18px + env(safe-area-inset-top, 0px))', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8, border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.1)', color: '#c9a84c', cursor: 'pointer' },
  logoCircle: { width: '38px', height: '38px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: '#0a1628', fontSize: '13px', flexShrink: 0 },
  logoTitle: { color: '#c9a84c', fontWeight: '700', fontSize: '12px', margin: 0 },
  logoSub: { color: '#8896a8', fontSize: '10px', margin: 0 },
  divider: { height: '1px', background: 'rgba(201,168,76,0.15)', margin: '8px 0' },
  navSectionLabel: {
    color: '#8896a8',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    margin: '2px 4px 6px',
    padding: '0 8px',
  },
  navSectionToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    margin: '0 0 2px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#8896a8',
    cursor: 'pointer',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  navSectionChevron: {
    display: 'inline-flex',
    width: 12,
    justifyContent: 'center',
    fontSize: 8,
    color: '#c9a84c',
    flexShrink: 0,
  },
  ownerCard: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'rgba(201,168,76,0.08)', borderRadius: '10px', margin: '4px 0' },
  ownerAvatar: { width: '36px', height: '36px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0a1628', fontWeight: '900', fontSize: '14px', flexShrink: 0 },
  ownerName: { color: '#c9a84c', fontWeight: '700', fontSize: '12px', margin: 0 },
  ownerSub: { color: '#8896a8', fontSize: '10px', margin: 0 },
  nav: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: '#8896a8', cursor: 'pointer', fontSize: '12px', width: '100%', textAlign: 'left' },
  navActive: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.6)', background: 'linear-gradient(to right, rgba(201,168,76,0.25), rgba(201,168,76,0.05))', boxShadow: '0 0 24px rgba(201,168,76,0.2)', color: '#c9a84c', cursor: 'pointer', fontSize: '12px', width: '100%', textAlign: 'left', fontWeight: '700' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.3)', background: 'rgba(233,69,96,0.08)', color: '#e94560', cursor: 'pointer', fontSize: '12px', width: '100%', marginTop: 'auto' },
  sidebarFooter: { color: 'rgba(201,168,76,0.4)', fontSize: '9px', textAlign: 'center', marginTop: '8px', letterSpacing: '1px' },
  main: { flex: '1 1 0', minWidth: 0, minHeight: 0, overflowY: 'scroll', overflowX: 'hidden', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '18px', borderBottom: '1px solid rgba(201,168,76,0.15)' },
  pageTitle: { color: '#c9a84c', fontWeight: '700', margin: '0 0 4px' },
  pageDate: { color: '#8896a8', fontSize: '11px', margin: 0 },
  ownerBadge: { display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', padding: '8px 14px', borderRadius: '20px', color: '#c9a84c', fontSize: '12px' },
  onlineDot: { width: '7px', height: '7px', background: '#4CAF50', borderRadius: '50%', display: 'inline-block' },
  cardRow: { display: 'grid', gap: '12px', marginBottom: '18px' },
  card: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '16px', textAlign: 'center' },
  cardIcon: { fontSize: '22px', margin: '0 0 6px' },
  cardVal: { color: '#c9a84c', fontWeight: '700', margin: '0 0 4px' },
  cardLbl: { color: '#8896a8', fontSize: '11px', margin: 0 },
  sectionTitle: { color: '#c9a84c', marginBottom: '14px', fontSize: '14px' },
  commissionCard: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '18px', marginBottom: '18px' },
  tableCard: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '18px', marginBottom: '18px' },
  tableTitle: { color: '#c9a84c', margin: '0 0 14px', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 10px', textAlign: 'left', color: 'rgba(201,168,76,0.7)', fontSize: '10px', letterSpacing: '1px', borderBottom: '1px solid rgba(201,168,76,0.15)', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '10px', fontSize: '12px', color: '#e8e0d0', whiteSpace: 'nowrap' },
  machineTag: { background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  statusBadge: { padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  alertRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' },
  downloadBtn: { background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px' },
  input: { width: '100%', padding: '10px 12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '8px', color: '#fff', fontSize: '13px', boxSizing: 'border-box' },
  label: { color: '#8896a8', fontSize: '11px', margin: '12px 0 5px' },
  cancelBtn: { flex: 1, padding: '11px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#8896a8', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  confirmBtn: { padding: '11px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
};

export default OwnerDashboard;







